import type {
  DavetYenidenGonderIstegi,
  HesapDavetDurumu,
  OgrenciDavetDurumu,
} from "@koclukpanel/contracts";
import { onayBilgisi } from "./onay-kilidi-durumu";
/**
 * API ISTEMCISI
 *
 * Panel ile sunucu arasindaki TEK gecis noktasi. Her `fetch` cagrisi
 * buradan geciyor cunku uc sey her istekte ayni sekilde yapilmali:
 *
 *   1. Cerez gonderimi (`credentials: "include"`)
 *   2. Hata govdesinin cozulmesi — sunucu `{ kod, mesaj }` doner
 *   3. 401'in oturum sonu olarak islenmesi
 *
 * Ayri ayri yazilsaydi bir ekran `credentials` vermeyi unutur ve o ekran
 * "neden hep 401 aliyorum" diye saatler yakardi.
 */

import type { Kategori, PlanTanimi } from "./plan-tanimi";
import type { DetayliAnalizVerisi } from "@koclukpanel/ui";
import type {
  AdayDavetAlani,
  DavetOnayMetni,
  MesaiAraligi,
  GorevRengi,
  DenemeHedefFarki,
  HedefNetGirdisi,
  HedefNetYaniti,
} from "@koclukpanel/contracts";
import { oturum } from "./oturum-durumu";

export class ApiHatasi extends Error {
  constructor(
    readonly durum: number,
    readonly kod: string,
    mesaj: string,
    readonly ayrinti?: Record<string, string>,
    /**
     * ================================================================
     * ARIZAYI TEŞHİS EDİLEBİLİR KILAN KİMLİK — ARIZA ANINDA DÜŞÜYORDU
     * ================================================================
     * Sunucunun `X-Istek-Id` başlığı: bir tıklamanın doğurduğu bütün
     * denetim ve log satırlarını birbirine bağlayan kimlik.
     *
     * Ölçüldü (05.09.2026, öğrenci PWA oturumu): sunucu bu başlığı
     * **her** yanıta koyuyor (`context.middleware.ts:35`),
     * `request-context.ts` kendi amacını *"bir tıklamanın doğurduğu
     * bütün olayları"* bağlamak diye yazıyor, `Hata500` çizimi ekranda
     * göstermeyi **açıkça istiyor** (*"Sorun sürerse bu kimliği
     * iletin"*) — ve **hiçbir istemci okumuyordu.** Öğrenci
     * uygulamasında da, panelde de.
     *
     * Sonucu: bir şey bozulduğunda kullanıcının söyleyebileceği tek şey
     * *"bir hata aldım"* oluyordu ve desteğin o isteği kayıtlarda
     * bulmasının **hiçbir yolu yoktu**. Yani teşhis için üretilmiş
     * bilgi, tam da teşhis gereken anda kayboluyordu.
     *
     * Bu, bu depoda tekrar eden sınıfın en pahalı biçimi: **bilgi
     * vardı, onu götürecek yol yoktu.**
     */
    readonly istekId: string | null = null,
  ) {
    super(mesaj);
    this.name = "ApiHatasi";
  }

  /**
   * Sunucu tarafı bir arıza mı (5xx)?
   *
   * İstek kimliği **yalnızca** bunlarda gösterilir. 4xx'te mesaj zaten
   * eyleme dönük ("şu alan eksik", "yetkiniz yok") ve oraya teknik bir
   * kimlik eklemek gürültü olur — gürültü de, kimliğin **gerçekten
   * gerektiği** yerde okunmamasına yol açar.
   */
  get sunucuHatasiMi(): boolean {
    return this.durum >= 500;
  }

  /** Zorunlu onay kapısı; sıradan yetki reddinden ayrı tutulur. */
  get onayBekliyorMu(): boolean {
    return this.durum === 403 && this.kod === "ONAY_BEKLIYOR";
  }

  /** Oturum bitti mi — kabuk buna bakip giris ekranina donuyor. */
  get oturumBittiMi(): boolean {
    return this.durum === 401;
  }

  /**
   * Istek SUNUCUYA HIC ULASMADI mi.
   *
   * ================================================================
   * "SUNUCU 500 DONDU" YANLIS BIR CUMLEYDI
   * ================================================================
   * 05.08.2026'da gercek bir kullanimda cikti: arka uc kapaliyken
   * panel "Sunucu 500 döndü." diyordu. Sunucu 500 DONMEDI — sunucuya
   * ULASILAMADI. Ikisi bambaska iki is emri veriyor:
   *
   *   500       "sistemde bir hata var, gelistiriciye bildir"
   *   ulasilmadi "arka uc ayakta degil ya da ag koptu, once onu bak"
   *
   * Yanlis cumle, bir uzmanin "sistem bozuldu" diye destek talebi
   * acmasina yol acar; asil sorun ise kimsenin sunucuyu baslatmamis
   * olmasidir.
   *
   * `durum === 0` bilerek: HTTP'de boyle bir kod yok, yani gercek bir
   * yanitla karistirilamaz.
   */
  get ulasilamadiMi(): boolean {
    return this.durum === 0;
  }

  /** Sudo modu gerekiyor mu (G-SES-04) — cagiran sifre soruyor. */
  /**
   * Bakım modu — F-SEC-06.
   *
   * Ölçüldü 05.09.2026: bakım açıkken sunucu muaf olmayan her uca
   * `503 BAKIM_MODU` döndürüyor ve panelin bunu ayırt edecek hiçbir
   * yolu YOKTU — kullanıcı her ekranda genel hata görüyordu. Oysa
   * sunucu, kimlik bile gerektirmeyen `/bakim/durum` ucunda
   * yöneticinin yazdığı başlık ve mesajı zaten veriyor.
   *
   * Yani bilgi vardı, okuyan yoktu. Bu satır okuyanı ekliyor.
   */
  get bakimModuMu(): boolean {
    return this.durum === 503 && this.kod === "BAKIM_MODU";
  }

  get sudoGerekliMi(): boolean {
    return this.durum === 403 && this.kod === "SUDO_GEREKLI";
  }
}

const KOK = "/api/v1";

/**
 * Sunucuya ulasilamadiginda gosterilen metin.
 *
 * KULLANICIYA YONELIK yazildi: bu mesaji goren kisi cogunlukla bir
 * uzman ya da veli ve "API ayakta degil" cumlesi ona hicbir sey
 * anlatmaz. Yapabilecegi seyi soyluyor.
 *
 * Gelistirme derlemesinde ek bir satir daha var: orada mesaji goren
 * kisi genellikle sunucuyu baslatmayi unutmus olan kisidir ve dogru
 * komutu bilmek isini bitiriyor. Uretim derlemesinde o satir
 * BULUNMUYOR — `import.meta.env.DEV` derleme aninda sabitleniyor.
 */
const ULASILAMIYOR_MESAJI: string =
  "Sunucuya şu anda ulaşılamıyor. Birkaç saniye sonra tekrar deneyin; " +
  "sorun sürerse yöneticinize bildirin." +
  (import.meta.env.DEV ? " (Geliştirme: `pnpm dev` çalışıyor mu?)" : "");

interface IstekSecenekleri {
  yontem?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  govde?: unknown;
  /** Sorgu dizesi parametreleri */
  sorgu?: Record<string, string | number | undefined>;
  signal?: AbortSignal;
}

export async function api<T>(yol: string, o: IstekSecenekleri = {}): Promise<T> {
  const istek = oturum.istekBaslat();
  const iptal = () => istek.denetleyici.abort();
  if (o.signal?.aborted) iptal();
  o.signal?.addEventListener("abort", iptal, { once: true });
  try {
    if (yol === "/auth/ben" && oturum.kapali)
      return { girisYapildi: false, userId: null, izinler: {}, roller: [] } as T;
    let veri: T = await istekYap<T>(yol, { ...o, signal: istek.denetleyici.signal });
    istek.dogrula();
    if (yol === "/auth/ben") {
      oturum.dogrula(veri as Ben);
      veri = onayBilgisi.benYanitiniIsle(veri as Ben, true) as T;
    }
    if (yol.startsWith("/auth/giris") && veri && typeof veri === "object" && "userId" in veri)
      oturum.degistir(false);
    if (yol === "/auth/gorunum" || yol === "/auth/gorunum/bitir") oturum.degistir(false);
    if (yol === "/auth/gorunum/durum") oturum.gorunumuDogrula(veri as GorunumDurumu);
    return veri;
  } catch (hata) {
    istek.dogrula();
    if (hata instanceof ApiHatasi && hata.oturumBittiMi && !yol.startsWith("/auth/giris"))
      oturum.degistir(true);
    if (hata instanceof ApiHatasi && hata.onayBekliyorMu && typeof window !== "undefined") {
      onayBilgisi.kilitle(oturum.kullaniciId);
      window.dispatchEvent(new Event("onay-kilidi"));
    }
    throw hata;
  } finally {
    istek.bitir();
    o.signal?.removeEventListener("abort", iptal);
  }
}

async function istekYap<T>(yol: string, o: IstekSecenekleri): Promise<T> {
  const sorgu = o.sorgu
    ? "?" +
      new URLSearchParams(
        Object.entries(o.sorgu)
          .filter(([, v]) => v !== undefined)
          .map(([k, v]) => [k, String(v)]),
      ).toString()
    : "";

  let yanit: Response;
  try {
    yanit = await fetch(KOK + yol + sorgu, {
      method: o.yontem ?? "GET",
      // Oturum cerezi httpOnly; JavaScript okuyamiyor ve okumasi da
      // gerekmiyor. `include` olmadan tarayici onu gondermez.
      credentials: "include",
      signal: o.signal,
      cache: "no-store",
      /*
        ÇOK PARÇALI GÖVDE (`FormData`) KENDİ BAŞLIĞINI ÜRETİR ve
        `Content-Type`ı ELLE YAZMAK BOZAR: tarayıcı sınır dizesini
        (boundary) kendisi ekliyor, biz `multipart/form-data` yazarsak
        o sınır kaybolur ve sunucu gövdeyi hiç çözemez.

        Dosya yükleme bu yüzden uzun süre AYRI bir `fetch` ile
        yapılıyordu; ayrı `fetch` ise `api()` içindeki hata çevirisini,
        401 davranışını ve istek kimliğini KAÇIRIYOR — bu depoda
        ölçülmüş bir hata sınıfı (`api-tek-kapi.test.ts`).
      */
      headers:
        o.govde !== undefined && !(o.govde instanceof FormData)
          ? { "Content-Type": "application/json" }
          : undefined,
      body:
        o.govde === undefined
          ? undefined
          : o.govde instanceof FormData
            ? o.govde
            : JSON.stringify(o.govde),
    });
  } catch {
    /**
     * `fetch` YALNIZCA ag katmani coktugunde firlatir: sunucu
     * dinlemiyor, DNS cozulmuyor, baglanti koptu. Bir HTTP hatasi
     * (4xx/5xx) burayi HIC gormez.
     *
     * Onceden bu hata `ApiHatasi` OLMADAN yukari cikiyordu ve
     * ekranlarin hepsi `e instanceof ApiHatasi ? e.message : "..."`
     * kaliyla yaziliyor — yani kullanici "Bilinmeyen hata" goruyordu.
     * En iyi teshis edilebilir ariza, en anlamsiz mesaji uretiyordu.
     */
    throw new ApiHatasi(0, "SUNUCUYA_ULASILAMIYOR", ULASILAMIYOR_MESAJI);
  }

  if (yanit.status === 204) return undefined as T;

  const metin = await yanit.text();
  let veri: unknown = null;
  try {
    veri = metin ? JSON.parse(metin) : null;
  } catch {
    veri = null;
  }

  if (!yanit.ok) {
    /**
     * ================================================================
     * 5xx + JSON OLMAYAN GOVDE = ISTEK BIZE ULASMADI
     * ================================================================
     * Sunucumuz her hatayi JSON olarak veriyor — kendi
     * `{kod, mesaj}` bicimimizde ya da en kotu ihtimalle NestJS'in
     * `{statusCode, message}` bicimiyle. Yani 5xx'in govdesi JSON
     * DEGILSE, yanit bizim uygulamamizdan gelmemis demektir: araya
     * giren bir vekil (gelistirmede Vite, uretimde ters vekil) arka
     * uca ulasamamis ve kendi HTML hata sayfasini dondurmustur.
     *
     * 05.08.2026'da tam olarak bu yasandi ve panel "Sunucu 500
     * döndü." dedi — teshisi yanlis yone cevirdi.
     *
     * 4xx BU DALA GIRMIYOR: onlar her zaman bizim uygulamamizdan
     * gelir ve govdeleri zaten anlamli.
     */
    if (veri === null && yanit.status >= 500) {
      throw new ApiHatasi(0, "SUNUCUYA_ULASILAMIYOR", ULASILAMIYOR_MESAJI);
    }

    const g = (veri ?? {}) as { kod?: string; mesaj?: string; ayrinti?: Record<string, string> };
    throw new ApiHatasi(
      yanit.status,
      g.kod ?? "BILINMEYEN",
      // Sunucunun mesaji kullaniciya GOSTERILIYOR: mesajlar zaten
      // Turkce ve kullaniciya yonelik yazildi. Burada kendi metnimizi
      // uydurmak, sunucunun anlatmak istedigini kaybetmek olurdu.
      g.mesaj ?? `Sunucu ${yanit.status} döndü.`,
      g.ayrinti,
      // Basligin YOKLUGU da bir bilgi: `null` kaliyor, bos dizgi degil.
      yanit.headers.get("X-Istek-Id"),
    );
  }

  return veri as T;
}

// =====================================================================
// Oturum
// =====================================================================

export interface Ben {
  onayKilidi?: { kilitli: boolean; kimVerir: "KENDISI" | "VELI"; eksikler: string[] } | null;
  gorunumde?: boolean;
  onayKilidiDevreDisi?: boolean;
  girisYapildi: boolean;
  userId: string | null;
  roller: string[];
  izinler: Record<string, string>;
  /**
   * ================================================================
   * SUNUCU SÖYLÜYOR — İSTEMCİ ROL ADI OKUMUYOR
   * ================================================================
   * `Student` kaydı var mı / en az bir `StudentParent` bağı var mı.
   * Sunucu bunları **kayıtlardan** üretiyor, roller tablosundan değil
   * (`auth.controller.ts`).
   *
   * Panelde bunlar geldiği ana kadar `personelMi`,
   * `!roller.includes("PARENT") && !roller.includes("STUDENT")` ile
   * türetiliyordu. O kuruluş rol ADINA bakıyor (CLAUDE.md 1. madde)
   * **ve** yanlış kümeye bakıyor: `roller` içinde özel bir rol de
   * bulunabilir ve "öğrenci değil, veli değil" cümlesi onu
   * ayırt edemez.
   *
   * ⚠️ İkisi AYRI AYRI `true` olabilir: mezun bir öğrenci küçük
   * kardeşinin velisi olabilir. Tek bir "tür" alanı bu durumu
   * kaybederdi — sunucu bu yüzden iki bayrak gönderiyor.
   *
   * ⚠️ Personel bayrağı YOK ve bilerek: o, ikisinin de `false`
   * olmasından türüyor. Üçüncü bir alan gönderilseydi üçü birden
   * tutarsız hâle gelebilirdi.
   */
  ogrenciMi: boolean;
  veliMi: boolean;
  /**
   * ================================================================
   * ÜÇÜNCÜ BAYRAK — VE TÜREMİYOR
   * ================================================================
   * Bu alan önce **yoktu** ve `/auth/ben`in yorumu *"personel,
   * ikisinin de `false` olmasından TÜREYEN bir sonuç"* diyordu.
   * 05.09.2026'da ölçüldü: **türemiyor.**
   *
   *   koç + veli   →  personelMi = true,  ikisi-de-false = false
   *
   * Çocuğu aynı kurumda okuyan bir koç tam bu durumda. Türetilmiş
   * koşul onu **müşteri** sayıp panelden atıyordu.
   *
   * Özel rol (`baseRole === null`) de aynı yerden vuruyordu: o rol
   * `systemRoles` kümesine hiç girmiyor (`resolve.ts:86`), yani
   * `ogrenciMi`/`veliMi` üzerinden hesaplanan hiçbir şey onu
   * göremiyor. `personelMi` ham rol atamalarından çözülüyor.
   *
   * Üçü birlikte tutarlı ve **hiçbiri ötekinden hesaplanmıyor** —
   * bu bilinçli.
   */
  personelMi: boolean;
  /**
   * ================================================================
   * DÖRDÜNCÜ BAYRAK — `Coach` KAYDI VAR MI
   * ================================================================
   * Öbür üçü gibi bu da rol adından değil KAYITTAN geliyor. Sunucu
   * `analiz.isListesi()` içinde uzmanlığı tam olarak böyle soruyor;
   * panel aynı soruyu soramadığı için iş listesini `analiz.ogrenci.
   * goruntule` iznine bağlamıştı.
   *
   * O izin Operasyon Admini'nde de var ama uç ona `UZMAN_DEGIL`
   * dönüyor: 12.09.2026'da ölçüldü, panelin açılış ekranı her
   * girişte bir 403 üretiyordu. Kullanıcı görmüyordu, log görüyordu.
   *
   * İzin "bu veriyi görebilir mi" sorusunun cevabı; uzmanlık ayrı bir
   * soru. İkisini tek alana bindirmek bu hatanın kendisiydi.
   */
  uzmanMi: boolean;
}

export function benGetir(): Promise<Ben> {
  return api<Ben>("/auth/ben");
}

// =====================================================================
// DAVET VE ŞİFRE SIFIRLAMA — F-PPL-01b
// =====================================================================

/**
 * Davet tamamlandıktan sonra hesabın gerçekten açılıp açılmadığı.
 *
 * Sunucunun kendi tipi (`jeton.service.ts`). "Açıldı mı" sorusunu
 * `boolean` ile sormak yerine durumu olduğu gibi taşıyoruz: ileride
 * üçüncü bir bekleme sebebi eklenirse `boolean` onu ezerdi.
 */
export type DavetDurumu = "AKTIF" | "MUVAFAKAT_BEKLIYOR";

/**
 * Davet bağlantısıyla şifre belirler ve hesabı aktifleştirir.
 *
 * ================================================================
 * `durum` ALANI HER ZAMAN OKUNMALI — "AKTIF" VARSAYILMAZ
 * ================================================================
 * F-PPL-05b: 18 yaş altı + veli muvafakatı yoksa şifre YAZILIR ama
 * hesap `INVITED` kalır. Giriş tarafı (`auth.service.ts`) `status`
 * `ACTIVE` değilse — şifre doğru olsa bile — reddediyor ve G-AUT-06
 * gereği **"E-posta veya şifre hatalı."** diyor.
 *
 * Yani `durum`u yok sayıp "artık giriş yapabilirsiniz" demek, o
 * kullanıcıyı DOĞRU şifresiyle "şifren yanlış" duvarına gönderirdi
 * ve sebebini hiçbir ekranda göremezdi.
 *
 * Alan sunucuda ZATEN vardı; `auth.controller.ts` dönüş tipi
 * `{ userId: string }` yazdığı için yolda düşüyordu (04.09.2026).
 */
export function davetiTamamla(
  jeton: string,
  sifre: string,
  onaylar: Array<{ tur: string; surum: string }> = [],
): Promise<{ userId: string; durum: DavetDurumu }> {
  return api("/auth/daveti-tamamla", { yontem: "POST", govde: { jeton, sifre, onaylar } });
}

/**
 * F-LGL-03a (24.10.2026) — aktivasyonda onaylanacak zorunlu metinler ve sürümleri. Jetonu HARCAMAZ.
 * Karar sunucuda: 18 yaş altı öğrencide liste boş döner.
 */
export function davetOnayMetinleri(jeton: string): Promise<{ metinler: DavetOnayMetni[] }> {
  return api("/auth/davet/onay-metinleri", { yontem: "POST", govde: { jeton } });
}

/** Sıfırlama bağlantısıyla şifreyi değiştirir; TÜM oturumlar kapanır. */
export function sifreSifirla(jeton: string, sifre: string): Promise<{ kapatilanOturum: number }> {
  return api("/auth/sifre-sifirla", { yontem: "POST", govde: { jeton, sifre } });
}

/**
 * Şifre sıfırlama bağlantısı ister.
 *
 * Sunucu adres KAYITLI OLMASA DA aynı cevabı veriyor ("bu adres
 * kayıtlıysa…"): farklı cevap verseydi, bu uç bir e-postanın sistemde
 * olup olmadığını sorgulamak için kullanılabilirdi. Ekran da o yüzden
 * "gönderildi" demiyor, sunucunun cümlesini aynen gösteriyor.
 */
export function sifirlamaIste(email: string): Promise<{ mesaj: string }> {
  return api("/auth/sifre-sifirlama-istegi", { yontem: "POST", govde: { email } });
}

export type GirisSonucu =
  | { ikinciAsama: false; userId: string }
  | { ikinciAsama: true; asamaJetonu: string; kalanSaniye: number };

export async function girisYap(
  /** E-posta, telefon ya da T.C. kimlik numarası — sunucu çözüyor (G-AUT-00). */
  kimlik: string,
  sifre: string,
  recaptchaJetonu?: string | null,
): Promise<GirisSonucu> {
  const y = await api<
    { userId: string } | { ikinciAsama: true; asamaJetonu: string; kalanSaniye: number }
  >("/auth/giris", {
    yontem: "POST",
    // Jeton YOKSA alan hic gonderilmiyor. `null` gondermek, sunucu
    // semasindaki `optional()` ile uyusmaz ve dogrulama hatasi verirdi.
    govde: recaptchaJetonu ? { kimlik, sifre, recaptchaJetonu } : { kimlik, sifre },
  });

  if ("ikinciAsama" in y) return y;
  return { ikinciAsama: false, userId: y.userId };
}

export interface RecaptchaDurumu {
  siteAnahtari: string | null;
  aktif: boolean;
  esik: number;
}

export function recaptchaDurumu(): Promise<RecaptchaDurumu> {
  return api<RecaptchaDurumu>("/auth/recaptcha/durum");
}

export interface IkinciAsamaSonucu {
  userId: string;
  /** G-AUT-02a — yedek kodla girildiyse kalan kod sayisi; TOTP ile `null`. */
  kalanYedekKod: number | null;
}

export function ikinciAsama(
  asamaJetonu: string,
  kod: string,
  /**
   * G-AUT-01c — "Bu cihazi 30 gun hatirla".
   *
   * VARSAYILAN FALSE: guvenlik gevsetmesi kullanicinin ACIK tercihi
   * olmali. Sunucu semasi da `.default(false)` — iki taraf ayni
   * varsayilanda duruyor ki biri degistiginde oteki sessizce
   * uymasin.
   */
  cihaziHatirla = false,
): Promise<IkinciAsamaSonucu> {
  return api<IkinciAsamaSonucu>("/auth/giris/ikinci-asama", {
    yontem: "POST",
    govde: { asamaJetonu, kod, cihaziHatirla },
  });
}

/** G-AUT-01c — kullanicinin kendi guvenilir cihazlari. */
export interface GuvenilirCihaz {
  id: string;
  label: string | null;
  ipAddress: string | null;
  createdAt: string;
  lastUsedAt: string | null;
  expiresAt: string;
}

export function guvenilirCihazlar(): Promise<GuvenilirCihaz[]> {
  return api<GuvenilirCihaz[]>("/auth/guvenilir-cihazlar");
}

export function guvenilirCihazKaldir(cihazId: string): Promise<{ kaldirildi: boolean }> {
  return api<{ kaldirildi: boolean }>(`/auth/guvenilir-cihazlar/${cihazId}/kaldir`, {
    yontem: "POST",
  });
}

export function guvenilirCihazlariKaldir(): Promise<{ kaldirilan: number }> {
  return api<{ kaldirilan: number }>("/auth/guvenilir-cihazlar/hepsini-kaldir", {
    yontem: "POST",
  });
}

export async function cikisYap(): Promise<void> {
  // F-AUTH-07 — çıkış başarısız olsa da eski ekran yeniden açılmamalı.
  oturum.degistir(true);
  await api<void>("/auth/cikis", { yontem: "POST" });
}

// =====================================================================
// Is listesi — F-ANL-09
// =====================================================================

export interface IsKalemi {
  tur:
    | "VERI_GIRMEYEN"
    | "PROGRAM_HAZIRLANMAMIS"
    | "GORUSME_RAPORU_YAZILMAMIS"
    | "VELI_RAPORU_BEKLIYOR"
    | "NET_DUSEN";
  oncelik: "KRITIK" | "YUKSEK" | "ORTA" | "BILGI";
  ogrenciId: string;
  ogrenciAdi: string;
  baslik: string;
  siddet: number;
  hedef: string;
}

export function isListesiGetir(): Promise<IsKalemi[]> {
  return api<IsKalemi[]>("/analiz/is-listem");
}

// =====================================================================
// Program — F-SCH
// =====================================================================

export interface ProgramGorevi {
  /** Yalnız hafta okumasında dönen hesaplanmış durum ve öğrenci ölçümleri. */
  renk?: GorevRengi;
  correctCount?: number | null;
  wrongCount?: number | null;
  emptyCount?: number | null;
  durationSeconds?: number | null;
  testFrom?: number | null;
  testTo?: number | null;
  id: string;
  itemDate: string;
  sortOrder: number;
  kind: string;
  lessonId: string;
  topicId: string | null;
  studentResourceBookId: string | null;
  pageFrom: number | null;
  pageTo: number | null;
  targetQuestionCount: number | null;
  targetTestCount: number | null;
  startTime: string | null;
  youtubeUrl: string | null;
  videoTitle: string | null;
  topicPdfUrl: string | null;
  testPdfUrl: string | null;
  coachNote: string | null;
  isExcused: boolean;
  status: string;
  /**
   * Okunur adlar — yalnızca HAFTA okumasında gelir (F-SCH-03b). Görev
   * ekleme/güncelleme cevabında yok; ekran haftayı zaten yeniden okuyor.
   * Ders adı sunucuda önekli kuruluyor ("TYT Matematik").
   */
  dersAdi?: string;
  konuAdi?: string | null;
  kaynakAdi?: string | null;
  /** F-STV-04a — öğrenci günü geçtikten sonra girdi (yalnız hafta okumasında). */
  lateEnteredAt?: string | null;
  /** Koç sonucu düzeltti (yalnız hafta okumasında). */
  editedByCoachAt?: string | null;
  missReason?: string | null;
  missNote?: string | null;
}

/**
 * KOÇUN SONUÇ DÜZELTMESİ — A10 kararı ("hafta kapanınca yalnız koç düzeltir").
 * Öğrencinin kullandığı AYNI uçlar; sunucu koç yolunu `editedByCoachAt` ile
 * damgalar, tarih sınırı koça uygulanmaz, kapsam (yalnız kendi öğrencisi) sunucuda.
 */
export function gorevSonucunuDuzelt(
  gorevId: string,
  veri: {
    correctCount: number | null;
    wrongCount: number | null;
    emptyCount: number | null;
    durationSeconds: number | null;
    tamamlandi: boolean;
  },
): Promise<ProgramGorevi> {
  return api(`/calisma/gorevler/${gorevId}/giris`, { yontem: "POST", govde: veri });
}

export function gorevYapamadiDuzelt(
  gorevId: string,
  neden: string,
  not: string | null,
): Promise<ProgramGorevi> {
  return api(`/calisma/gorevler/${gorevId}/yapamadim`, { yontem: "POST", govde: { neden, not } });
}

export interface HaftalikProgram {
  id: string;
  studentId: string;
  weekStart: string;
  weekEnd: string;
  status: "DRAFT" | "PENDING_APPROVAL" | "PUBLISHED" | "ARCHIVED";
  publishedAt: string | null;
  preparedWithStudent: boolean;
  preparedInSessionId: string | null;
  revision: number;
  items: ProgramGorevi[];
  tamamlama: {
    toplam: number;
    tamamlanan: number;
    mazeretli: number;
    yuzde: number | null;
  };
  izinliGecisler: string[];
  revizyonGerekir: boolean;
}

export function programGetir(ogrenciId: string, tarih: string): Promise<HaftalikProgram | null> {
  return api<HaftalikProgram | null>(`/program/ogrenci/${ogrenciId}/hafta`, { sorgu: { tarih } });
}

export function programOlustur(
  ogrenciId: string,
  tarih: string,
  gorusmeId?: string,
): Promise<{ id: string; weekStart: string }> {
  return api<{ id: string; weekStart: string }>("/program", {
    yontem: "POST",
    govde: { ogrenciId, tarih, gorusmeId: gorusmeId ?? null },
  });
}

/**
 * Görevin bağlantı ve kaynak alanları — F-SCH-03 / F-SCH-03b.
 * Sunucu bağlantıyı yalnızca http/https olarak kabul ediyor; kaynak
 * yalnızca o öğrencinin listesinden olabilir.
 */
export interface GorevIcerigi {
  studentResourceBookId?: string | null;
  targetTestCount?: number | null;
  youtubeUrl?: string | null;
  videoTitle?: string | null;
  topicPdfUrl?: string | null;
  testPdfUrl?: string | null;
}

export interface GorevGirdisi extends GorevIcerigi {
  testFrom?: number | null;
  testTo?: number | null;
  itemDate: string;
  kind: string;
  lessonId: string;
  topicId?: string | null;
  pageFrom?: number | null;
  pageTo?: number | null;
  targetQuestionCount?: number | null;
  startTime?: string | null;
  coachNote?: string | null;
}

/**
 * GÖREV FORMUNUN DERS LİSTESİ — F-SCH-03b.
 *
 * Liste öğrencinin alanından sunucuda kuruluyor (AYT alanında TYT de
 * listede, adlar "TYT Matematik" biçiminde). Panel alan seçtirmiyor ve
 * kuralı kendisi tekrar yazmıyor.
 */
export interface DersHavuzu {
  /** `null` = öğrencinin alanı seçilmemiş. */
  ogrenciAlani: { id: string; ad: string; kullanilabilir: boolean } | null;
  gruplar: Array<{
    alan: { id: string; ad: string; sinav: string | null };
    dersler: AgacDugumu[];
  }>;
}

export function dersHavuzuGetir(ogrenciId: string): Promise<DersHavuzu> {
  return api<DersHavuzu>(`/program/ogrenci/${encodeURIComponent(ogrenciId)}/ders-havuzu`);
}

export function gorevEkle(programId: string, gorev: GorevGirdisi): Promise<ProgramGorevi> {
  return api<ProgramGorevi>(`/program/${programId}/gorevler`, { yontem: "POST", govde: gorev });
}

export function gorevSil(gorevId: string): Promise<{ silindi: true }> {
  return api<{ silindi: true }>(`/program/gorevler/${gorevId}`, { yontem: "DELETE" });
}

export function gorevTasi(
  gorevId: string,
  itemDate: string,
  sortOrder: number,
): Promise<ProgramGorevi> {
  return api<ProgramGorevi>(`/program/gorevler/${gorevId}/tasi`, {
    yontem: "POST",
    govde: { itemDate, sortOrder },
  });
}

export function programDurumDegistir(
  programId: string,
  durum: string,
): Promise<{ id: string; status: string }> {
  return api<{ id: string; status: string }>(`/program/${programId}/durum`, {
    yontem: "POST",
    govde: { durum },
  });
}

export function oncekiHaftadanKopyala(
  programId: string,
  secenekler: Record<string, unknown>,
): Promise<{ kopyalanan: number; atlanan: number }> {
  return api<{ kopyalanan: number; atlanan: number }>(
    `/program/${programId}/onceki-haftadan-kopyala`,
    { yontem: "POST", govde: secenekler },
  );
}

// =====================================================================
// Akademik agac — gorev formu icin
// =====================================================================

/**
 * Ünite ile konu dersin altında AYNI DÜZEYDE duruyor (ünite zorunlu
 * değil). `tur` olmadan ikisi ayırt edilemiyordu — "çocuğu varsa
 * ünitedir" gibi bir tahmin yanlış olurdu, alt konusu olan bir konu da
 * çocuk taşıyor.
 */
export type DugumTuru = "ders" | "unite" | "konu";

export interface AgacDugumu {
  id: string;
  ad: string;
  tur: DugumTuru;
  cocuklar?: AgacDugumu[];
}

/**
 * Ağaçtaki her KONU — alt konular dahil, ünite ve ders düşer.
 *
 * ================================================================
 * NEDEN BURADA, ÇAĞIRAN EKRANDA DEĞİL
 * ================================================================
 * Bu fonksiyon depoda ÜÇ kez yazılmış olmalıydı ve iki kez yazılmıştı
 * (`KonuIlerlemesi`, `Mufredat`). Üçüncüsü — `Program.tsx`teki görev
 * formu — hiç yazmamıştı ve dersin BÜTÜN çocuklarını konu sanıyordu:
 *
 *     const konular = agac.data?.find((d) => d.id === dersId)?.cocuklar
 *
 * Sonucu 07.09.2026'da canlı denemede çıktı: "Konu (zorunlu)" açılır
 * listesi ünite adlarını gösteriyordu (Analiz, Temel Kavramlar,
 * Fonksiyonlar) ve seçilince sunucu 400 dönüyordu. Yani müfredatında
 * ünite olan bir kurumda uzman haftalık programa **hiçbir görev
 * ekleyemiyordu**.
 *
 * `AgacDugumu.tur` alanı tam bu ayrım için var ve yorumu bunu açıkça
 * söylüyor: *"`tur` olmadan ikisi ayırt edilemiyordu."* Alan vardı,
 * onu okumayan bir yol vardı.
 *
 * Tipin yanında duruyor ki yeni bir çağıran onu aramak zorunda
 * kalmasın; `agac-konulari.test.ts` üçüncü bir kopyanın yazılmasını
 * engelliyor.
 */
export function agactakiKonular(dugumler: readonly AgacDugumu[]): AgacDugumu[] {
  const cikti: AgacDugumu[] = [];
  for (const d of dugumler) {
    if (d.tur === "konu") cikti.push(d);
    if (d.cocuklar) cikti.push(...agactakiKonular(d.cocuklar));
  }
  return cikti;
}

export function alanlariGetir(): Promise<Array<{ id: string; name: string; code: string }>> {
  return api<Array<{ id: string; name: string; code: string }>>("/akademik/alanlar");
}

export function agacGetir(alanId: string): Promise<AgacDugumu[]> {
  return api<AgacDugumu[]>(`/akademik/alanlar/${alanId}/agac`);
}

// ---- Müfredat yönetimi — F-ACA ----

export function alanEkle(veri: {
  code: string;
  name: string;
  examKind?: string | null;
}): Promise<{ id: string }> {
  return api("/akademik/alanlar", { yontem: "POST", govde: veri });
}

/** Elenen HER satır sebebiyle döner — sessiz kayıp yok (F-ACA-03). */
export interface TopluSonuc {
  /**
   * Geri alma için parti kimliği — F-ACA-03b.
   * Ön izlemede `null`: ortada geri alınacak bir şey yok.
   */
  partiId?: string | null;
  eklenen: number;
  /**
   * Elenen satırlar — sayı DEĞİL, tek tek.
   *
   * `neden` makine için, `mesaj` ekran için. Sunucu ikisini de
   * gönderiyor; tip yalnızca `mesaj`ı bildirdiği sürece bir ekran
   * sebebe göre davranmak isterse Türkçe cümleyi metin olarak
   * eşlemek zorunda kalır — ve cümle değiştiği gün o eşleme sessizce
   * bozulur. (04.09.2026: sunucuda `neden` vardı, tip bildirmiyordu.)
   */
  elenen: Array<{ satirNo: number; ham: string; neden: string; mesaj: string }>;
  onIzleme: boolean;
}

/**
 * Toplu ders girişi. `onIzleme` ile HİÇBİR ŞEY yazılmaz.
 *
 * Önizleme ayrı bir uç değil aynı ucun bayrağı: iki ayrı uç olsaydı
 * önizlemenin gösterdiği ile uygulamanın yaptığı bir gün ayrışırdı ve
 * kullanıcı gördüğünden başkasını kaydederdi.
 */
export function dersTopluEkle(
  alanId: string,
  metin: string,
  onIzleme: boolean,
): Promise<TopluSonuc> {
  return api(`/akademik/alanlar/${alanId}/dersler/toplu`, {
    yontem: "POST",
    govde: { metin, onIzleme },
  });
}

export function konuTopluEkle(
  dersId: string,
  metin: string,
  onIzleme: boolean,
): Promise<TopluSonuc> {
  return api(`/akademik/dersler/${dersId}/konular/toplu`, {
    yontem: "POST",
    govde: { metin, onIzleme },
  });
}

export function uniteEkle(dersId: string, ad: string): Promise<{ id: string }> {
  return api(`/akademik/dersler/${dersId}/uniteler`, { yontem: "POST", govde: { ad } });
}

/**
 * Konu arşivleme — F-ACA. **Kalıcı silme yok.**
 *
 * Bir konu silinseydi ona bağlı geçmiş programlar ve deneme sonuçları
 * koparadı. Gerekçe zorunlu (en az 3 karakter): denetim kaydına
 * `reason` olarak giriyor ve altı ay sonra "bu konu neden kayboldu"
 * sorusunu cevaplayan tek şey o.
 */
export function konuArsivle(konuId: string, gerekce: string): Promise<void> {
  return api(`/akademik/konular/${konuId}/arsivle`, { yontem: "POST", govde: { gerekce } });
}

/**
 * Konuyu bir komşusuyla yer değiştirir — teslim çizimi:
 * *"Sıralama sürükle-bırakla değil yukarı/aşağı düğmesiyle."*
 *
 * Sürükle-bırak dokunmatikte ve klavyede zahmetli; 200 konuluk bir
 * ağaçta neredeyse kullanılamaz. Uçtaki öğe için `degisti: false`
 * döner — hata değil.
 */
export function konuSirasiDegistir(
  konuId: string,
  yon: "yukari" | "asagi",
): Promise<{ degisti: boolean }> {
  return api(`/akademik/konular/${konuId}/sira`, { yontem: "PATCH", govde: { yon } });
}

/** Bir konunun gerektirdiği konular — F-ACA-06. */
export interface OnKosulSatiri {
  konuId: string;
  ad: string;
  ders: string;
}

export function onKosullariGetir(konuId: string): Promise<OnKosulSatiri[]> {
  return api(`/akademik/konular/${konuId}/on-kosullar`);
}

/**
 * Ön koşul bağı ekler. **Döngü sunucuda reddedilir** (400).
 *
 * A→B varken B→A yazılırsa iki konu da sonsuza kadar "ön koşulu
 * tamamlanmamış" görünürdü; uyarı her programda çıkar, sebebi
 * anlaşılmaz ve bir süre sonra bütün uyarılar görmezden gelinir.
 */
export function onKosulEkle(konuId: string, onKosulId: string): Promise<unknown> {
  return api(`/akademik/konular/${konuId}/on-kosullar`, {
    yontem: "POST",
    govde: { onKosulId },
  });
}

export function onKosulKaldir(konuId: string, onKosulId: string): Promise<unknown> {
  return api(`/akademik/konular/${konuId}/on-kosullar/${onKosulId}`, { yontem: "DELETE" });
}

// =====================================================================
// KAYNAK KİTAP — F-RES
// =====================================================================

/**
 * Havuzdaki kitap. `kullananOgrenci`, arşivlemeden önce "bu kitabı kaç
 * öğrenci kullanıyor" sorusunu cevaplıyor.
 */
export interface KitapSatiri {
  id: string;
  ad: string;
  yayinevi: string | null;
  sinav: string | null;
  seviye: string | null;
  baskiYili: number | null;
  toplamSayfa: number | null;
  toplamSoru: number | null;
  dersId: string | null;
  ders: string | null;
  arsivlendiMi: boolean;
  kullananOgrenci: number;
}

export function kaynakHavuzu(
  filtre: { ders?: string; sinav?: string; ara?: string; arsiv?: boolean } = {},
): Promise<KitapSatiri[]> {
  return api("/kaynak/kitaplar", {
    sorgu: {
      ders: filtre.ders,
      sinav: filtre.sinav,
      ara: filtre.ara,
      arsiv: filtre.arsiv ? "1" : undefined,
    },
  });
}

export interface KitapGirdisi {
  ad: string;
  dersId?: string | null;
  yayinevi?: string | null;
  sinav?: string | null;
  seviye?: string | null;
  baskiYili?: number | null;
  toplamSayfa?: number | null;
  toplamSoru?: number | null;
}

export function kitapEkle(veri: KitapGirdisi): Promise<{ id: string }> {
  return api("/kaynak/kitaplar", { yontem: "POST", govde: veri });
}

export function kitapGuncelle(id: string, veri: Partial<KitapGirdisi>): Promise<unknown> {
  return api(`/kaynak/kitaplar/${id}`, { yontem: "PATCH", govde: veri });
}

/**
 * Kitabı havuzdan çıkarır — **silmez**. Öğrencilerin listesindeki
 * kayıtlar olduğu gibi kalır; yarım kalan ilerleme kaybolmaz.
 * Arşivlenmiş kitap yalnızca YENİ eklemelerde seçilemez.
 */
export function kitapArsivle(id: string, gerekce: string): Promise<unknown> {
  return api(`/kaynak/kitaplar/${id}/arsivle`, { yontem: "POST", govde: { gerekce } });
}

export interface TopluKaynakSonuc {
  onIzleme: boolean;
  eklenen?: number;
  eklenecek?: Array<{ ad: string; yayinevi: string | null }>;
  /**
   * Elenen satırlar — sayı DEĞİL, tek tek.
   *
   * `neden` makine için, `mesaj` ekran için. Sunucu ikisini de
   * gönderiyor; tip yalnızca `mesaj`ı bildirdiği sürece bir ekran
   * sebebe göre davranmak isterse Türkçe cümleyi metin olarak
   * eşlemek zorunda kalır — ve cümle değiştiği gün o eşleme sessizce
   * bozulur. (04.09.2026: sunucuda `neden` vardı, tip bildirmiyordu.)
   */
  elenen: Array<{ satirNo: number; ham: string; neden: string; mesaj: string }>;
  toplamSatir: number;
  partiId?: string | null;
}

export function kitapTopluEkle(veri: {
  dersId?: string | null;
  sinav?: string | null;
  metin: string;
  onIzleme?: boolean;
}): Promise<TopluKaynakSonuc> {
  return api("/kaynak/kitaplar/toplu", { yontem: "POST", govde: veri });
}

/**
 * ÖĞRENCİNİN KAYNAK LİSTESİ — F-RES-02
 *
 * `ilerlemeYuzdesi` ve `basariOrani` **`null` olabilir** ve bu SIFIR
 * DEĞİLDİR: toplam sayfa bilinmiyorsa yüzde ölçülemez, yeterli soru
 * çözülmemişse başarı oranı hesaplanamaz. Ekran ikisini "—" gösterir;
 * "%0" yazmak öğrenciyi hiç denenmediği bir şeyde başarısız
 * göstermek olurdu.
 */
export interface OgrenciKaynakSatiri {
  id: string;
  ad: string;
  yayinevi: string | null;
  havuzdanMi: boolean;
  /** Havuzdan çıkarıldı ama öğrencide duruyor — ilerleme kaybolmasın. */
  havuzdanCikarildiMi: boolean;
  dersId: string;
  ders: string;
  kacinciSayfa: number | null;
  kacinciTest: number | null;
  toplamSayfa: number | null;
  ilerlemeYuzdesi: number | null;
  uzmanPuani: number | null;
  basariOrani: number | null;
  cozulenSoru: number;
  zorlukHukmu: string;
  eklenme: string;
  sonGuncelleme: string;
}

export function ogrenciKaynaklari(ogrenciId: string): Promise<OgrenciKaynakSatiri[]> {
  return api(`/ogrenciler/${ogrenciId}/kaynaklar`);
}

export function ogrenciKaynakEkle(
  ogrenciId: string,
  veri: { dersId: string; kitapId?: string | null; ozelAd?: string | null },
): Promise<{ id: string }> {
  return api(`/ogrenciler/${ogrenciId}/kaynaklar`, { yontem: "POST", govde: veri });
}

export function ogrenciKaynakTopluEkle(
  ogrenciId: string,
  veri: { dersId: string; metin: string; onIzleme?: boolean },
): Promise<TopluKaynakSonuc> {
  return api(`/ogrenciler/${ogrenciId}/kaynaklar/toplu`, { yontem: "POST", govde: veri });
}

export function ogrenciKaynakIlerleme(
  ogrenciId: string,
  kayitId: string,
  veri: { kacinciSayfa?: number | null; kacinciTest?: number | null; uzmanPuani?: number | null },
): Promise<unknown> {
  return api(`/ogrenciler/${ogrenciId}/kaynaklar/${kayitId}`, { yontem: "PATCH", govde: veri });
}

/** Listeden çıkarır — arşivleme (CLAUDE.md §7), geçmiş görevler kayda bakıyor. */
export function ogrenciKaynakCikar(ogrenciId: string, kayitId: string): Promise<unknown> {
  return api(`/ogrenciler/${ogrenciId}/kaynaklar/${kayitId}`, { yontem: "DELETE" });
}

/**
 * DEMO VERİSİ — F-TEC-15
 *
 * Uçlar `DEMO_VERI=true` olmadan **hiç tanımlanmıyor**: bayrak
 * kapalıyken bu çağrılar 404 döner ve ekran bölümü hiç çizilmez.
 * Üretimde bayrak zaten açılamaz — açılırsa süreç ayağa kalkmaz.
 */
export interface DemoDurumu {
  uretilmisKullanici: number;
  veritabani: string | null;
  /** Üretim/silme mümkün mü; dolu ise sebebi. */
  engel: string | null;
}

export interface DemoUretimSonucu {
  yazilan: number;
  operasyon: Record<string, number>;
  toplam: number;
}

export interface DemoSilmeSonucu {
  silinen: Record<string, number>;
  /** Silinemeyenler — sessiz kalmıyor, sebebiyle dönüyor. */
  silinemeyen: Array<{ eposta: string; sebep: string }>;
}

export function demoDurumu(): Promise<DemoDurumu> {
  return api("/teknik/demo-veri");
}

export function demoVeriUret(adet: number, tohum?: number): Promise<DemoUretimSonucu> {
  return api("/teknik/demo-veri/uret", { yontem: "POST", govde: { adet, tohum } });
}

export function demoVeriSil(): Promise<DemoSilmeSonucu> {
  return api("/teknik/demo-veri/sil", { yontem: "POST" });
}

/**
 * IP KISITI — G-SES-07
 *
 * Liste **boşsa kısıt yoktur** (özellik isteğe bağlı). Kontrol her
 * istekte yapılıyor: yalnızca girişte bakmak, çalınan bir çerezin
 * izinli ağın dışından kullanılmasını engellemez.
 */
export interface IpKisitiDurumu {
  araliklar: string[];
  /** Şu anki adresiniz — ne yazacağını bilmeyen kullanıcı kendini kilitler. */
  suAnkiIp: string | null;
}

export function ipKisitiGetir(): Promise<IpKisitiDurumu> {
  return api("/auth/ip-kisiti");
}

/**
 * Kendi IP kısıtını yazar. **Kendi adresinizi dışarıda bırakan liste
 * reddedilir** — yazıldığı anda oturumunuzu geçersiz kılardı.
 */
export function ipKisitiYaz(veri: {
  araliklar: string[];
  sifre: string;
}): Promise<{ araliklar: string[] }> {
  return api("/auth/ip-kisiti", { yontem: "PATCH", govde: veri });
}

/**
 * TOPLU GİRİŞ PARTİLERİ VE GERİ ALMA — F-ACA-03b
 *
 * Liste YALNIZCA geri alabileceğiniz türleri döner. İzni olmayana
 * "şunları geri alabilirsin" gösterip her denemede 403 vermek çıkmaz
 * sokak olurdu — süzgeç sunucuda.
 */
export type PartiTuru = "Lesson" | "Topic" | "ResourceBook" | "StudentResourceBook";

export interface PartiSatiri {
  id: string;
  modul: string;
  tur: PartiTuru;
  kayitSayisi: number;
  baglamId: string | null;
  tarih: string;
  yapan: string | null;
  geriAlindiMi: boolean;
  geriAlmaTarihi: string | null;
  geriAlan: string | null;
  geriAlmaGerekcesi: string | null;
}

export interface GeriAlmaSonucu {
  geriAlindi: true;
  /** Gerçekten arşivlenen kayıt sayısı (zaten arşivli olanlar hariç). */
  arsivlenen: number;
  /** Bu arada KULLANILMIŞ kayıt sayısı — engellemiyor, söylüyor. */
  kullanilan: number;
  toplam: number;
}

export function topluPartileriGetir(
  filtre: {
    tur?: PartiTuru;
    baglam?: string;
  } = {},
): Promise<PartiSatiri[]> {
  return api("/toplu-girisler", { sorgu: { tur: filtre.tur, baglam: filtre.baglam } });
}

/**
 * Partiyi geri alır — **arşivler, silmez** (CLAUDE.md §7).
 *
 * Parti BİR KEZ geri alınır. Kullanılmış kayıt geri almayı
 * ENGELLEMEZ ama yanıtta `kullanilan` ile sayılır: engelleseydi tek
 * bir kullanım bütün partiyi kilitlerdi, sessizce arşivleseydi
 * kullanıcı ne kırdığını bilmezdi.
 */
export function topluPartiGeriAl(partiId: string, gerekce: string): Promise<GeriAlmaSonucu> {
  return api(`/toplu-girisler/${partiId}/geri-al`, { yontem: "POST", govde: { gerekce } });
}

/**
 * EĞİTİM YILI — F-PPL-11
 *
 * `bugunIcinde` sunucudan geliyor ve elle işaretlenen `gecerli`
 * bayrağıyla karşılaştırılıyor. İkisi ayrışınca ekran bunu YAZIYOR,
 * sessizce düzeltmiyor: bayrağı tarihten türetmek, yeni yıl başlamadan
 * "geçerli" işaretleme ihtiyacını yok ederdi.
 */
export interface YilSatiri {
  id: string;
  ad: string;
  baslangic: string;
  bitis: string;
  gecerli: boolean;
  bugunIcinde: boolean;
  surumSayisi: number;
}

export function yillariGetir(): Promise<YilSatiri[]> {
  return api("/akademik/yillar");
}

export function yilEkle(veri: {
  ad: string;
  /** `YYYY-AA-GG` — saat gönderilmiyor, sunucu da kabul etmiyor. */
  baslangic: string;
  bitis: string;
  gecerli?: boolean;
}): Promise<{ id: string }> {
  return api("/akademik/yillar", { yontem: "POST", govde: veri });
}

export interface SurumSatiri {
  id: string;
  name: string;
  status: string;
  publishedAt: string | null;
  konuSayisi: number;
}

export function surumleriGetir(): Promise<SurumSatiri[]> {
  return api("/akademik/surumler");
}

export function surumEkle(veri: {
  academicYearId: string;
  name: string;
  fieldId?: string | null;
}): Promise<{ id: string }> {
  return api("/akademik/surumler", { yontem: "POST", govde: veri });
}

export function surumeKonuAta(surumId: string, konuIdler: string[]): Promise<{ eklenen: number }> {
  return api(`/akademik/surumler/${surumId}/konular`, {
    yontem: "POST",
    govde: { konuIdler },
  });
}

export function surumYayinla(surumId: string): Promise<unknown> {
  return api(`/akademik/surumler/${surumId}/yayinla`, { yontem: "POST" });
}

// =====================================================================
// DENEME TANIMLAMA — F-EXM-01/02/03
// =====================================================================

export interface DenemeBolumu {
  id: string;
  ad: string;
  soruSayisi: number;
  anahtarSayisi: number;
  /** Anahtar TAM mı — eksik anahtar sonucu yanlış değerlendirir */
  anahtarTam: boolean;
}

export interface DenemeSatiri {
  id: string;
  ad: string;
  tur: string;
  kaynak: string;
  yayinevi: string | null;
  denemeTarihi: string;
  baslangic: string;
  bitis: string;
  /** F-EXM-01b — kitapçık bağlantısı (öğrenci listesinde "Kitapçık PDF"). */
  pdfUrl?: string | null;
  /** Düzenleme düğmesi için ipucu (OWN/ALL); karar yine sunucuda. */
  duzenleyebilir?: boolean;
  katilimSayisi: number;
  bolumler: DenemeBolumu[];
}

export function denemeleriGetir(): Promise<DenemeSatiri[]> {
  return api("/denemeler");
}

export type DenemeDilimiSorgusu = "yaklasan" | "devam" | "gecmis";

/**
 * SAYFALI LİSTE — `GET /denemeler` en çok 100 deneme döndürdüğü için eski
 * denemeler düşüyordu. Ad araması ve dilim SUNUCUDA uygulanır; `toplam`
 * süzgeçten sonraki gerçek sayıdır.
 */
export function denemeleriSayfali(sorgu: {
  sayfa: number;
  boyut: number;
  arama?: string;
  dilim?: DenemeDilimiSorgusu;
}): Promise<{ toplam: number; sayfa: number; boyut: number; denemeler: DenemeSatiri[] }> {
  return api("/denemeler/sayfali", {
    sorgu: {
      sayfa: sorgu.sayfa,
      boyut: sorgu.boyut,
      arama: sorgu.arama?.trim() || undefined,
      dilim: sorgu.dilim,
    },
  });
}

/** F-EXM-04a — katılım satırı. İletişim bilgisi YOK (05.10 kararı: yalnız profilde). */
export interface DenemeAtamaSatiri {
  ogrenciId: string;
  ad: string;
  soyad: string;
  ogrenciNo: number | null;
  arsivli: boolean;
  katilim: "ASSIGNED" | "IN_PROGRESS" | "COMPLETED" | "MISSED";
  kova: "KATILDI" | "KACIRDI" | "BEKLIYOR";
  net: number | null;
  atandi: string;
  gonderdi: string | null;
  iptalEdilebilir: boolean;
}

export interface DenemeKatilimi {
  ozet: { atanan: number; katildi: number; kacirdi: number; bekliyor: number };
  ogrenciler: DenemeAtamaSatiri[];
}

export function denemeAtamalari(id: string, arama?: string): Promise<DenemeKatilimi> {
  return api(`/denemeler/${id}/atamalar`, arama ? { sorgu: { arama } } : undefined);
}

/**
 * Denemeyi öğrencilere atar — F-EXM-04a. Tümü-ya-hiç: kapsam dışı bir
 * kimlik varsa sunucu 403 KAPSAM_DISI döner ve HİÇBİRİ atanmaz.
 */
export function denemeAta(
  id: string,
  secim: { ogrenciIdler: string[] } | { kapsamimdakiHepsi: true },
): Promise<{ istenen: number; yeni: number; geriAcilan: number; zatenAtanmis: number }> {
  return api(`/denemeler/${id}/atamalar`, { yontem: "POST", govde: secim });
}

/** Atamayı geri alır (arşivler) — yalnız sonucu olmayan atama. */
export function denemeAtamaIptal(id: string, ogrenciId: string): Promise<{ iptal: true }> {
  return api(`/denemeler/${id}/atamalar/${ogrenciId}/iptal`, { yontem: "POST" });
}

/**
 * Denemeyi düzenler — F-EXM-01b. Yalnız gönderilen alan değişir;
 * `pdfUrl: null` bağlantıyı kaldırır. Anlar saat dilimi ofsetiyle
 * (`toISOString()`) gönderilmeli; sunucu ofsetsizi reddeder.
 */
export function denemeGuncelle(
  id: string,
  govde: { pdfUrl?: string | null; baslangic?: string; bitis?: string },
): Promise<{ id: string; baslangic: string; bitis: string; pdfUrl: string | null }> {
  return api(`/denemeler/${id}`, { yontem: "PATCH", govde });
}

export function denemeOlustur(govde: {
  ad: string;
  tur: string;
  kaynak: string;
  yayinevi: string | null;
  denemeTarihi: string;
  baslangic: string;
  bitis: string;
  /**
   * Özel bölüm yapısı — F-EXM-02 "Düzenlenebilir".
   *
   * ================================================================
   * SUNUCU BUNU 04.09.2026'DAN ÖNCE DE KABUL EDİYORDU
   * ================================================================
   * `deneme.controller.ts:31` alanı bildiriyor, `deneme.service.ts:88`
   * verildiyse ONU yazıyor, verilmediyse şablonu. Ama bu tip alanı
   * taşımadığı için hiçbir ekran gönderemiyordu — alan telden
   * geçebilir durumdaydı, istemci erişemiyordu.
   *
   * Bedeli somuttu: sunucunun kendi gerekçesindeki örnek — yayınevinin
   * **38 soruluk** Türkçe bölümü — girilemiyordu. Şablon 40 diyorsa
   * anahtar formu 40 harf bekliyor, `anahtarTam` hiç `true` olmuyor ve
   * SONUÇ GİRİŞİ KALICI OLARAK KAPALI kalıyor.
   *
   * BOŞ BIRAKILABİLİR ve boşken GÖNDERİLMİYOR: o zaman sunucu şablonu
   * uyguluyor. Şablonu buraya kopyalamak, aynı tabloyu iki evde
   * tutmak olurdu.
   */
  bolumler?: Array<{ name: string; questionCount: number; lessonId?: string | null }>;
  /** F-EXM-01b — kitapçık bağlantısı; boşsa gönderilmez. */
  pdfUrl?: string | null;
}): Promise<{ id: string; name: string }> {
  return api("/denemeler", { yontem: "POST", govde });
}

/**
 * Cevap anahtarını toplu yapıştırmayla kaydeder.
 *
 * Sunucu çözülemeyen karakter varsa isteği TAMAMEN reddediyor — yarım
 * yazılmış bir anahtar, kaydırmalı cevaplarla bütün denemeyi yanlış
 * değerlendirir ve kimse fark etmez.
 */
export function anahtarKaydet(bolumId: string, topluMetin: string): Promise<{ yazilan: number }> {
  return api(`/denemeler/bolumler/${bolumId}/anahtar`, {
    yontem: "POST",
    govde: { topluMetin },
  });
}

// =====================================================================
// PROGRAM DEĞİŞİKLİK TALEBİ — F-SCH-11
// =====================================================================

export interface DegisiklikTalebi {
  id: string;
  studentId: string;
  status: string;
  reason: string;
  reasonText: string;
  quotaMonth: string;
  grantIndex: number;
  isExtraGrant: boolean;
  coachResponse: string | null;
  decidedAt: string | null;
  createdAt: string;
}

export function degisiklikTalepleri(
  durum?: string,
  /** Tek öğrenci — süzgeç kapsamın YANINA ekleniyor, yerine değil. */
  ogrenciId?: string,
): Promise<ListeKunyesi & { talepler: DegisiklikTalebi[] }> {
  return api("/program/degisiklik", { sorgu: { durum, ogrenci: ogrenciId } });
}

/**
 * Talebi karara bağlar.
 *
 * Karar BİR KEZ verilir: karara bağlanmış talep tekrar karara
 * bağlanamıyor — ikinci karar birincinin izini silmeden üstüne yazardı
 * ve öğrenci hangi cevabın geçerli olduğunu bilemezdi.
 */
export function degisiklikKarar(
  id: string,
  hedef: string,
  coachResponse: string | null,
): Promise<unknown> {
  return api(`/program/degisiklik/${id}/karar`, {
    yontem: "POST",
    govde: { hedef, coachResponse },
  });
}

/**
 * Ek değişiklik hakkı tanır — matris §5.6.
 *
 * Uzman öğrencinin adına TALEP AÇMAZ; yalnızca boş bir hak yeri açar.
 * Gerekçe öğrencinin sözüdür.
 */
export function degisiklikEkHak(ogrenciId: string, gerekce: string): Promise<unknown> {
  return api("/program/degisiklik/ek-hak", {
    yontem: "POST",
    govde: { ogrenciId, gerekce },
  });
}

// =====================================================================
// KOÇLUK GÖRÜŞMELERİ — F-SES
// =====================================================================

export interface KoclukGorusmesi {
  id: string;
  studentId: string | null;
  kind: string;
  status: string;
  scheduledAt: string;
  durationMin: number | null;
  meetUrl: string | null;
  agenda: string | null;
  /** Okuyucunun görme hakkı yoksa `null` döner — F-SES-04 */
  summary: string | null;
  summaryVisibility: string;
  decisions: string | null;
  studentGoals: string | null;
  nextSessionAt: string | null;
  /** İÇERİK DEĞİL, yalnızca varlık bilgisi — F-SES-05 */
  korumaliNotVar: boolean;
  /**
   * Koçu izinde olduğu için YENİDEN PLANLANMALI — F-PPL-11b.
   *
   * Durum `RESCHEDULED` YAPILMIYOR: o "yeniden planlandı" demek,
   * "planlanmalı" değil. İşaret izin penceresinden türetiliyor ve
   * izin bitince kendiliğinden kayboluyor.
   */
  yenidenPlanlanmali: boolean;
  /** SUNUCUDAN: ekran geçiş tablosu tutmuyor */
  izinliGecisler: string[];
  /** SUNUCUDAN: görünürlük yalnızca DARALTILABİLİR */
  izinliGorunurlukler: string[];
}

export function ogrenciGorusmeleri(ogrenciId: string): Promise<KoclukGorusmesi[]> {
  return api(`/gorusmeler/ogrenci/${ogrenciId}`);
}

export function koclukGorusmesiAc(govde: {
  ogrenciId: string;
  kind: string;
  scheduledAt: string;
  durationMin?: number | null;
  /** F-SES-02b — yalnız https; boş alan `null`. */
  meetUrl?: string | null;
  agenda?: string | null;
}): Promise<KoclukGorusmesi> {
  return api("/gorusmeler", { yontem: "POST", govde });
}

export function gorusmeGuncelle(
  id: string,
  govde: Record<string, unknown>,
): Promise<KoclukGorusmesi> {
  return api(`/gorusmeler/${id}`, { yontem: "PATCH", govde });
}

export function gorusmeDurumDegistir(id: string, durum: string): Promise<KoclukGorusmesi> {
  return api(`/gorusmeler/${id}/durum`, { yontem: "POST", govde: { durum } });
}

/** Görünürlük yalnızca DARALTILIR — genişletme sunucuda reddedilir (F-SES-04). */
export function gorusmeGorunurluk(id: string, gorunurluk: string): Promise<KoclukGorusmesi> {
  return api(`/gorusmeler/${id}/gorunurluk`, { yontem: "POST", govde: { gorunurluk } });
}

/**
 * KORUMALI PDR NOTU — F-SES-05
 *
 * Ayrı anahtarla şifreleniyor; öğrencinin açık rızası yoksa sunucu
 * reddediyor. SÜPER ADMİN bu izne sahip değil — yalnızca uzman yazar.
 */
export function korumaliNotYaz(id: string, not: string): Promise<unknown> {
  return api(`/gorusmeler/${id}/korumali-not`, { yontem: "POST", govde: { not } });
}

/**
 * Korumalı notu okur — GEREKÇE ZORUNLU.
 *
 * Her okuma ayrı erişim tablosuna yazılıyor ve sudo modu gerekiyor
 * (izin kodu `auth/sudo.ts` listesinde). Süper Admin dahil kimse iz
 * bırakmadan okuyamıyor.
 */
export function korumaliNotOku(
  id: string,
  gerekce: string,
): Promise<{ not: string; yazanAd?: string; yazilmaZamani?: string }> {
  return api(`/gorusmeler/${id}/korumali-not/oku`, { yontem: "POST", govde: { gerekce } });
}

/** İçerik GERÇEKTEN siliniyor — arşivleme uygulanmıyor (F-SES-05). */
export function korumaliNotSil(id: string): Promise<unknown> {
  return api(`/gorusmeler/${id}/korumali-not/sil`, { yontem: "POST" });
}

/**
 * Korumalı PDR notu gerekçesinin alt sınırı — SÖZLEŞMEDEN.
 *
 * ================================================================
 * SAYI SÖZLEŞMEDE, AD DA ÇAKIŞMIYOR
 * ================================================================
 * Burada `GEREKCE_EN_AZ = 15` yazılıydı ve sözleşmedeki aynı adı
 * (değeri 3) GÖLGELİYORDU. Bağımsız denetim yarım saatini buna
 * kaybetti: testi sözleşmeden aldı, ekran 15 kullanıyordu, ikisi de
 * doğru görünüyordu ve hata mesajı sebebi söylemedi.
 *
 * İlk düzeltmem takma adla yeniden dışa vermekti
 * (`KORUMALI_NOT_GEREKCE_EN_AZ as GEREKCE_EN_AZ`). SAYIYI tekilleştirdi
 * ama TUZAĞI korudu: `./api`den gelen `GEREKCE_EN_AZ` 15, sözleşmeden
 * gelen 3 — yani yarım saati yakan şey aynen duruyordu. Ad da ayrıldı.
 */
export { KORUMALI_NOT_GEREKCE_EN_AZ } from "@koclukpanel/contracts";

// =====================================================================
// PROGRAM HAZIRLAMA ARAÇLARI — F-SCH-04/05/09
// =====================================================================

/**
 * Günü tatil / mazeretli işaretler — F-SCH-09.
 *
 * O gün tamamlama oranının PAYDASINDAN düşer. Bu ayrım önemli:
 * mazeretsiz bir tatil, öğrenciyi "yapmadı" diye gösterir ve haftalık
 * oran düşer — oysa yapılacak bir şey yoktu.
 */
export function mazeretIsaretle(
  programId: string,
  tarih: string,
  mazeretli: boolean,
): Promise<unknown> {
  return api(`/program/${programId}/mazeret`, {
    yontem: "POST",
    govde: { tarih, mazeretli },
  });
}

/**
 * Hızlı giriş değerleri (F-SCH-04b). Alan YOKSA sunucu dokunmaz; `null`
 * ise temizler — ekran yalnızca dolu alanları gönderiyor (`hizli-giris.ts`).
 */
export interface TopluDegerler {
  targetQuestionCount?: number | null;
  targetTestCount?: number | null;
  studentResourceBookId?: string | null;
  youtubeUrl?: string | null;
  videoTitle?: string | null;
  topicPdfUrl?: string | null;
  testPdfUrl?: string | null;
  coachNote?: string | null;
  kind?: string;
}

/** Hızlı giriş kapsamı — tür açıkça yazılır; `tarih` `YYYY-AA-GG` (F-SCH-04b). */
export type TopluKapsamGovdesi =
  { tur: "secili"; gorevIdler: string[] } | { tur: "gun"; tarih: string } | { tur: "hafta" };

/** Hızlı giriş — seçili satırlara / güne / tüm haftaya uygular (F-SCH-04, F-SCH-04b). */
export function programToplu(
  programId: string,
  kapsam: TopluKapsamGovdesi,
  degerler: TopluDegerler,
): Promise<{ etkilenen: number }> {
  return api(`/program/${programId}/toplu`, {
    yontem: "POST",
    govde: { kapsam, degerler },
  });
}

export interface ProgramSablonu {
  id: string;
  name: string;
  description: string | null;
  /** Paylaşıma açık şablonlar bütün koçlarda listeleniyor */
  isShared: boolean;
  createdAt: string;
}

export function programSablonlari(): Promise<ProgramSablonu[]> {
  return api("/program/sablonlar");
}

/**
 * Programı şablon olarak kaydeder — F-SCH-05.
 *
 * Alan adı `paylasimli` (`paylas` DEĞİL): sunucudaki Zod şeması böyle
 * ve yanlış adla gönderilen alan sessizce düşer — şablon "paylaşıldı"
 * sanılıp yalnızca sahibinde kalırdı.
 */
export function programSablonKaydet(
  programId: string,
  ad: string,
  paylasimli: boolean,
): Promise<{ id: string }> {
  return api(`/program/${programId}/sablon`, {
    yontem: "POST",
    govde: { ad, paylasimli },
  });
}

export function programSablonUygula(programId: string, sablonId: string): Promise<unknown> {
  return api(`/program/${programId}/sablon/${sablonId}/uygula`, { yontem: "POST" });
}

// =====================================================================
// Deneme motoru — F-EXM
// =====================================================================

/** Deneme sonucunun nasıl girildiği — F-EXM. */
export type GirisYontemi = "CEVAP_CEVAP" | "DERS_NET";

export interface DenemeTrendNoktasi {
  /** Telafi önerisi ucu bunu bekliyor — arayüz deneme kimliğinden türetemez */
  atamaId: string;
  examId: string;
  ad: string;
  tur: string;
  tarih: string;
  net: number;
  puan: number | null;
  rank: number | null;
  /** Üstteki yüzde: 0 en iyi. Tek katılımcıda `null` */
  percentile: number | null;
  /**
   * Sonuç nasıl girildi.
   *
   * `DERS_NET` ise soru/konu bazlı analiz HİÇ üretilmedi — telafi
   * paneli bu durumda "yanlış çıkmadı" DEMEMELİ, "ölçülmedi"
   * demeli. İkisi apayrı şey ve ilkini varsaymak uzmana öğrencinin
   * o konuları bildiğini söylüyordu.
   */
  girisYontemi: GirisYontemi;
  /** F-EXM-09 — o satırın sınav türünün hedefi yoksa `null`/yok. Toplam ve fark SUNUCUDAN gelir. */
  hedefNet?: DenemeHedefFarki | null;
}

export interface DenemeTrendi {
  denemeler: DenemeTrendNoktasi[];
  /** Tek deneme varsa `null` — "0 değişim" DEĞİL */
  sonDegisim: number | null;
  ortalama: number | null;
  enYuksek: number | null;
  enDusuk: number | null;
}

export function denemeTrendi(ogrenciId: string, son = 10): Promise<DenemeTrendi> {
  return api<DenemeTrendi>(`/denemeler/ogrenci/${ogrenciId}/trend`, { sorgu: { son } });
}

/**
 * F-EXM-09 — ders bazlı hedef net. Okuma: `deneme.hedef.goruntule`; yazma: `deneme.hedef.duzenle`
 * (yalnız koç ASSIGNED ve süper yönetici). `govde` bir sınav türünün TAM kümesidir; boş dizi o türün
 * hedefini temizler.
 */
export function hedefNetGetir(ogrenciId: string): Promise<HedefNetYaniti> {
  return api<HedefNetYaniti>(`/denemeler/ogrenci/${encodeURIComponent(ogrenciId)}/hedef-net`);
}

export function hedefNetKaydet(ogrenciId: string, govde: HedefNetGirdisi): Promise<HedefNetYaniti> {
  return api<HedefNetYaniti>(`/denemeler/ogrenci/${encodeURIComponent(ogrenciId)}/hedef-net`, {
    yontem: "PUT",
    govde,
  });
}

export interface TelafiKonusu {
  topicId: string;
  konuAdi: string;
  lessonId: string | null;
  dersAdi: string | null;
  yanlis: number;
  /** Ölçülemiyorsa `null` — "%0 başarı" DEĞİL */
  basariOrani: number | null;
}

export interface TelafiOnerisi {
  ogrenciId: string;
  konular: TelafiKonusu[];
}

export function telafiOnerisi(atamaId: string): Promise<TelafiOnerisi> {
  return api<TelafiOnerisi>(`/denemeler/sonuclar/${atamaId}/telafi`);
}

export interface DenemeSonucu {
  atamaId: string;
  deneme: { id: string; name: string; kind: string; endsAt: string };
  toplam: { dogru: number; yanlis: number; bos: number; net: number };
  puan: number | null;
  rank: number | null;
  percentile: number | null;
  sure: number | null;
  sureSupheli: boolean;
  /** `DERS_NET` ise `konular` boş olacak — "yanlış yok" değil, "ölçülmedi". */
  girisYontemi: GirisYontemi;
}

export function denemeSonucu(atamaId: string): Promise<DenemeSonucu> {
  return api<DenemeSonucu>(`/denemeler/sonuclar/${atamaId}`);
}

// =====================================================================
// Veli raporu — uzman tarafı (F-REP)
// =====================================================================

/** `ReportStatus` ile BİREBİR — şemadan sapma sessiz bir hataya döner. */
export type RaporDurumu =
  "DRAFT" | "AWAITING_REVIEW" | "REVISION_REQUESTED" | "APPROVED" | "PUBLISHED" | "READ_BY_PARENT";

export interface KapsamUyarisi {
  dusukMu: boolean;
  onayGerekli: boolean;
  mesaj: string;
}

export interface Rapor {
  id: string;
  studentId: string;
  periodStart: string;
  periodEnd: string;
  /** Sayısal bölüm — SİSTEM üretti, değiştirilemez (F-REP-01) */
  draftContent: string | null;
  generalComment: string | null;
  positives: string | null;
  improvements: string | null;
  nextGoals: string | null;
  parentAdvice: string | null;
  dataCoverage: number | null;
  status: RaporDurumu;
  sentAt: string | null;
  readByParentAt: string | null;
  parentComment: string | null;
  /**
   * Son revizyon gerekçesi — F-REP-09.
   *
   * TEK PARÇA geliyor, üç ayrı alan değil: üç alan olsaydı "metin yok
   * ama tarih var" gibi imkânsız bir bileşimi çizmek gerekirdi.
   *
   * `yazan` null olabilir (`ON DELETE SET NULL`): yazan arşivlenirse
   * METİN KALIR. Ekran yer tutucu bir ad uydurmuyor.
   *
   * Boşluktan ibaret gerekçe sunucuda `null` sayılıyor — boş bir kutu
   * çizilseydi uzman "yazılmış ama okuyamıyorum" sanırdı.
   */
  sonRevizyonGerekcesi: RevizyonGerekcesiSozlesme | null;
  /**
   * Sunucunun izin verdiği geçişler. Arayüz KENDİ durum makinesini
   * kurmuyor: kural iki yerde yaşarsa er ya da geç ayrışır ve arayüz
   * sunucunun reddedeceği bir düğmeyi etkin gösterir.
   */
  izinliGecisler: RaporDurumu[];
  veliyeAcik: boolean;
  kapsamUyarisi: KapsamUyarisi;
  /**
   * Raporun sürümü (updatedAt'in ISO metni; her yazımda değişir).
   *
   * Onay ve düzenleme isteği, kullanıcının EKRANDA GÖRDÜĞÜ metnin
   * sürümünü `beklenenSurum` olarak taşır. Sunucu kaydı yeniden okuyup
   * güncel sürümle yazsaydı, araya giren bir değişikliği (başkasının
   * kaydettiği metni) kullanıcı görmeden onaylamış olurdu.
   */
  surum: string;
}

export interface RaporYorumlari {
  generalComment?: string | null;
  positives?: string | null;
  improvements?: string | null;
  nextGoals?: string | null;
  parentAdvice?: string | null;
}

export interface OnayBekleyen {
  id: string;
  studentId: string;
  ogrenciAdi: string;
  hazirlayan: string;
  /** Kendi hazırladığın rapor — onaylayamazsın (F-REP-03) */
  kendiRaporum: boolean;
  periodStart: string;
  periodEnd: string;
  dataCoverage: number | null;
  bekliyorAt: string;
}

export function onayBekleyenler(): Promise<OnayBekleyen[]> {
  return api<OnayBekleyen[]>("/raporlar/onay-bekleyenler");
}

// =====================================================================
// GENEL ONAY MOTORU — F-APR
//
// Veli raporu onayıyla KARIŞTIRILMASIN: o ayrı bir yoldan gidiyor
// (`/raporlar/onay-bekleyenler`, yukarıda). Bu, herhangi bir kayda
// onay talebi açan JENERİK mekanizma — aynı motoru haftalık program,
// müfredat sürümü ve doküman da kullanacak.
// =====================================================================

export type OnayDurumu =
  "DRAFT" | "PENDING" | "IN_REVIEW" | "REVISION_REQUESTED" | "APPROVED" | "REJECTED" | "CANCELLED";

export interface OnayTalebi {
  id: string;
  entityType: string;
  entityId: string;
  category: string | null;
  requesterId: string;
  /** Talebi açanın adı — SUNUCUDAN gelir, arayüz kimlik çözmüyor */
  talepSahibi: string | null;
  studentId: string | null;
  ogrenciAdi: string | null;
  summary: string | null;
  status: OnayDurumu;
  priority: number;
  deciderId: string | null;
  decidedAt: string | null;
  reason: string | null;
  dueAt: string | null;
  resolvedInMinutes: number | null;
  createdAt: string;
  /**
   * Bu durumdan gidilebilecek durumlar — SUNUCUDAN gelir.
   * Ekran kendi geçiş tablosunu tutsaydı, sunucudaki kural
   * değiştiğinde arayüz basılıp hata alan düğmeler gösterirdi.
   */
  izinliGecisler: OnayDurumu[];
}

export function genelOnayBekleyenler(): Promise<OnayTalebi[]> {
  return api<OnayTalebi[]>("/onaylar/bekleyenler");
}

export function genelOnayKarar(
  id: string,
  durum: OnayDurumu,
  gerekce: string | null,
): Promise<OnayTalebi> {
  return api(`/onaylar/${encodeURIComponent(id)}/karar`, {
    yontem: "POST",
    govde: { durum, gerekce },
  });
}

/** Rapor listesi süzgeci (sürüm 9a) — tarih dönem ÇAKIŞMASIYLA, metin sunucuda aranır. */
export interface RaporAramasi {
  /** "YYYY-AA-GG" */
  bas?: string;
  bit?: string;
  ara?: string;
}

export function raporlariGetir(ogrenciId: string, suzgec: RaporAramasi = {}): Promise<Rapor[]> {
  const sorgu = Object.fromEntries(
    Object.entries(suzgec).filter(([, v]) => typeof v === "string" && v.trim().length > 0),
  );
  return api<Rapor[]>(`/raporlar/ogrenci/${ogrenciId}`, {
    ...(Object.keys(sorgu).length > 0 ? { sorgu } : {}),
  });
}

/**
 * F-REP-04 — raporun PDF'i için süreli imzalı bağlantı. Yalnız veliye gönderilmiş
 * raporda var (409 PDF_YOK / PDF_HAZIRLANIYOR). Bağlantı 15 dk geçerli; her
 * indirmede yeniden istenir — saklanmaz.
 */
export function raporPdfBaglantisi(
  raporId: string,
): Promise<{ url: string; gecerlilikSaniye: number }> {
  return api(`/raporlar/${encodeURIComponent(raporId)}/pdf`);
}

/** Raporun dönem görüşmesi — veliye açık özet yoksa `ozet: null` (yalnız tarih/tür). */
export interface RaporGorusmesi {
  id: string;
  tarih: string;
  tur: "COACHING" | "MAKEUP" | "PARENT_MEETING";
  sureDk: number | null;
  ozet: string | null;
}

export interface RaporDenemesi {
  ad: string;
  tur: string;
  tarih: string;
  dogru: number;
  yanlis: number;
  bos: number;
  net: number;
}

export interface RaporEkBolumleri {
  gorusmeSayisi: number;
  gorusmeler: RaporGorusmesi[];
  denemeler: RaporDenemesi[];
}

/** Dönemin görüşme ve deneme bölümleri — veliye gidecek hâliyle (korumalı not YOK). */
export function raporEkBolumleri(raporId: string): Promise<RaporEkBolumleri> {
  return api<RaporEkBolumleri>(`/raporlar/${encodeURIComponent(raporId)}/ek-bolumler`);
}

// =====================================================================
// Notlarım — koçun kişisel notları (sürüm 9a). YALNIZ YAZAN görür.
// =====================================================================

export interface KisiselNot {
  id: string;
  baslik: string;
  icerik: string;
  etiketler: string[];
  olusturma: string;
  guncelleme: string;
  /** Düzenleme/arşivde `beklenenSurum` olarak geri gönderilir (409 NOT_DEGISTI). */
  surum: string;
}

export interface NotAramasi {
  ara?: string;
  bas?: string;
  bit?: string;
}

export function notlarim(suzgec: NotAramasi = {}): Promise<KisiselNot[]> {
  const sorgu = Object.fromEntries(
    Object.entries(suzgec).filter(([, v]) => typeof v === "string" && v.trim().length > 0),
  );
  return api<KisiselNot[]>("/notlarim", {
    ...(Object.keys(sorgu).length > 0 ? { sorgu } : {}),
  });
}

export function notOlustur(veri: {
  baslik: string;
  icerik: string;
  etiketler: string[];
}): Promise<KisiselNot> {
  return api<KisiselNot>("/notlarim", { yontem: "POST", govde: veri });
}

export function notGuncelle(
  id: string,
  veri: { baslik: string; icerik: string; etiketler: string[] },
  beklenenSurum: string,
): Promise<KisiselNot> {
  return api<KisiselNot>(`/notlarim/${encodeURIComponent(id)}`, {
    yontem: "PATCH",
    govde: { ...veri, beklenenSurum },
  });
}

export function notArsivle(
  id: string,
  beklenenSurum: string,
): Promise<{ id: string; arsivlendi: true }> {
  return api(`/notlarim/${encodeURIComponent(id)}/arsivle`, {
    yontem: "POST",
    govde: { beklenenSurum },
  });
}

// =====================================================================
// Öğrenci tanıma notu (sürüm 9a) — personel görür; öğrenci ve veli GÖRMEZ.
// =====================================================================

export function tanimaNotu(ogrenciId: string): Promise<{ metin: string | null }> {
  return api(`/ogrenciler/${encodeURIComponent(ogrenciId)}/tanima-notu`);
}

export function tanimaNotuYaz(
  ogrenciId: string,
  metin: string | null,
  beklenenMetin: string | null,
): Promise<{ metin: string | null }> {
  return api(`/ogrenciler/${encodeURIComponent(ogrenciId)}/tanima-notu`, {
    yontem: "PUT",
    govde: { metin, beklenenMetin },
  });
}

export function raporGetir(raporId: string): Promise<Rapor> {
  return api<Rapor>(`/raporlar/${raporId}`);
}

/** F-REP-07a — eksik haftalık veli raporu satırı (öğrenci-hafta). */
export interface EksikHaftalikRapor {
  ogrenciId: string;
  ogrenciAdi: string;
  kocAdi: string | null;
  /** Pazartesi, `YYYY-MM-DDT00:00:00.000Z` (yerel gün) */
  haftaBasi: string;
  /** Pazar */
  haftaSonu: string;
  /** "29 Eyl – 5 Eki" */
  hafta: string;
  gecikmeHafta: number;
  kritik: boolean;
}

export function eksikHaftalikRaporlar(): Promise<EksikHaftalikRapor[]> {
  return api("/raporlar/eksik-haftalik");
}

export function raporOlustur(
  ogrenciId: string,
  donemBaslangic: string,
  donemBitis: string,
): Promise<Rapor> {
  return api<Rapor>("/raporlar", {
    yontem: "POST",
    govde: { ogrenciId, donemBaslangic, donemBitis },
  });
}

/**
 * F-REP-03 — PATCH yanıtı: rapor + onay düştü mü.
 *
 * Kontrol bekleyen ya da onaylanmış raporun içeriği değişince sunucu raporu
 * TASLAK'a döndürüyor; onaylanan metin sessizce değişmesin diye. Ekran bunu
 * kendisi tahmin etmiyor, sunucunun bayrağını okuyor.
 */
export type RaporGuncelleSonucu = Rapor & { onayDusuruldu: boolean };

export function raporGuncelle(
  raporId: string,
  yama: RaporYorumlari,
  beklenenSurum: string,
): Promise<RaporGuncelleSonucu> {
  return api<RaporGuncelleSonucu>(`/raporlar/${raporId}`, {
    yontem: "PATCH",
    govde: { ...yama, beklenenSurum },
  });
}

export function raporDurumDegistir(
  raporId: string,
  durum: RaporDurumu,
  secenekler: {
    gerekce?: string | null;
    kapsamOnaylandi?: boolean;
    /** Ekranda gösterilen metnin sürümü — zorunlu, unutulamasın. */
    beklenenSurum: string;
  },
): Promise<Rapor> {
  return api<Rapor>(`/raporlar/${raporId}/durum`, {
    yontem: "POST",
    govde: {
      durum,
      gerekce: secenekler.gerekce ?? null,
      kapsamOnaylandi: secenekler.kapsamOnaylandi ?? false,
      beklenenSurum: secenekler.beklenenSurum,
    },
  });
}

// =====================================================================
// Bildirim — F-NTF
// =====================================================================

export interface Bildirim {
  id: string;
  eventCode: string;
  title: string;
  body: string | null;
  linkUrl: string | null;
  isRead: boolean;
  createdAt: string;
}

/**
 * Panel içi bildirimler — F-NTF-09.
 *
 * `yalnizOkunmamis` SUNUCUDA süzülüyor: aynı süzgeç zilin okunmamış
 * sayacını da besliyor. İki yerde ayrı hesaplansaydı zil ile liste
 * ayrışır ve "okudum ama sayaç düşmüyor" olurdu.
 */
/**
 * Okunmamış sayısı — zil için.
 *
 * Liste ucu `take: 100` ile sınırlı; saymak için onu çekmek hem
 * YANLIŞ sayı verirdi hem de sayı için 100 satır taşırdı.
 */
export function bildirimSayaci(): Promise<{ okunmamis: number }> {
  return api("/bildirimler/sayac");
}

export interface BildirimSayfasi {
  bildirimler: Bildirim[];
  /** `null` = daha eski bildirim YOK. Boş dizgi DEĞİL. */
  sonrakiImlec: string | null;
}

/**
 * İMLEÇLİ SAYFALAMA — 04.09.2026.
 *
 * Uç önce `take: 100` ile sınırlıydı ve gerisi hiç görülemiyordu:
 * 101. bildirimden eskisine ulaşmanın yolu yoktu.
 *
 * OFSET DEĞİL İMLEÇ: `skip` ile sayfalamak, aradaki listeye yeni
 * bildirim düştüğünde sayfa sınırlarını kaydırır ve kullanıcı aynı
 * kaydı iki kez görür ya da hiç görmez. Bildirim akışı tam da
 * sürekli başa yeni kayıt düşen bir liste.
 */
export function bildirimleriGetir(
  yalnizOkunmamis = false,
  imlec?: string,
): Promise<BildirimSayfasi> {
  return api<BildirimSayfasi>("/bildirimler", {
    sorgu: { okunmamis: yalnizOkunmamis ? "1" : undefined, imlec },
  });
}

export function bildirimOkundu(id: string): Promise<{ okundu: true }> {
  return api<{ okundu: true }>(`/bildirimler/${id}/okundu`, { yontem: "POST" });
}

// =====================================================================
// CRM — aday, huni, tanışma görüşmesi (F-CRM)
// =====================================================================

export type AdayDurumu =
  | "NEW"
  | "TO_CALL"
  | "UNREACHED"
  | "CONTACTED"
  | "INFORMED"
  | "MEETING_SCHEDULED"
  | "MEETING_HELD"
  | "OFFER_MADE"
  | "FOLLOW_UP"
  | "ENROLLED"
  | "LOST";

export type AdayKaynagi =
  | "GOOGLE_ADS"
  | "META_ADS"
  | "ORGANIC"
  | "INSTAGRAM"
  | "WHATSAPP"
  | "REFERRAL"
  | "PHONE"
  | "MANUAL"
  | "OTHER";

export type KayipNedeni =
  | "PRICE"
  | "POSTPONED"
  | "UNREACHABLE"
  | "CHOSE_COMPETITOR"
  | "STUDENT_REFUSED"
  | "PARENT_REFUSED"
  | "NOT_SUITABLE"
  | "WRONG_LEAD"
  | "OTHER";

export type EtkinlikTuru = "CALL" | "WHATSAPP" | "EMAIL" | "MEETING" | "NOTE" | "STATUS_CHANGE";

export interface AdayOzeti {
  id: string;
  studentName: string;
  status: AdayDurumu;
  source: AdayKaynagi;
  ownerId: string | null;
  nextFollowUpAt: string | null;
  createdAt: string;
}

/** `davetKodu` (kayıtlı davet kodu) sözleşmeden: `AdayDavetAlani`, packages/contracts/src/oyun-b.ts. */
export interface AdayAyrinti extends AdayDavetAlani {
  id: string;
  studentName: string;
  studentPhone: string | null;
  studentEmail: string | null;
  parentName: string | null;
  parentPhone: string | null;
  parentEmail: string | null;
  status: AdayDurumu;
  source: AdayKaynagi;
  studentId: string | null;
  city: string | null;
  targetExam: string | null;
  lostReason: KayipNedeni | null;
  lostNote: string | null;
  /** Para DİZGİ gelir — JSON'da ondalık kaymasın */
  offeredAmount: string | null;
  nextFollowUpAt: string | null;
  firstContactAt: string | null;
  informedAt: string | null;
  enrolledAt: string | null;
  createdAt: string;
  activities: Array<{
    id: string;
    kind: EtkinlikTuru;
    summary: string;
    outcome: string | null;
    createdAt: string;
  }>;
  introSessions: Array<{
    id: string;
    scheduledAt: string;
    status: string;
    introOutcome: string | null;
    introReportSubmittedAt: string | null;
    introReportApprovedAt: string | null;
  }>;
  basamak: string;
  /**
   * SUNUCUDAN gelir. Ekran düğmeleri bundan üretilir — burada sabit bir
   * liste tutmak, geçiş kuralı değiştiğinde ekranın sessizce eskimesi
   * demek olurdu.
   */
  izinliGecisler: AdayDurumu[];
}

export interface HuniSatiri {
  basamak: string;
  ulasan: number;
  /** `null` = ölçülemedi (önceki basamakta aday yok). SIFIR DEĞİL. */
  gecisOrani: number | null;
}

export type Maliyet =
  | { olculdu: false; neden: "HARCAMA_GIRILMEDI" | "KAYIT_YOK"; harcama: number | null }
  | { olculdu: true; harcama: number; kayit: number; kayitBasina: number };

export interface KaynakSatiri {
  kaynak: AdayKaynagi;
  aday: number;
  kayit: number;
  /** `null` = o kaynakta hiç aday yok; oran ölçülemez */
  donusum: number | null;
  maliyet: Maliyet;
}

export interface HuniRaporu {
  aralik: { baslangic: string; bitis: string };
  toplamAday: number;
  huni: HuniSatiri[];
  genelDonusum: number | null;
  kaynaklar: KaynakSatiri[];
  kayipNedenleri: Array<{ neden: KayipNedeni | null; sayi: number }>;
}

export function adaylariGetir(
  filtre: {
    durum?: AdayDurumu;
    kaynak?: AdayKaynagi;
  } = {},
): Promise<ListeKunyesi & { adaylar: AdayOzeti[] }> {
  return api("/crm/adaylar", { sorgu: { durum: filtre.durum, kaynak: filtre.kaynak } });
}

export function adayGetir(id: string): Promise<AdayAyrinti> {
  return api<AdayAyrinti>(`/crm/adaylar/${id}`);
}

export interface YeniAday {
  studentName: string;
  studentPhone?: string | null;
  parentName?: string | null;
  parentPhone?: string | null;
  source: AdayKaynagi;
  city?: string | null;
  targetExam?: string | null;
}

export interface AdayOlusturmaSonucu {
  aday: { id: string; studentName: string; status: AdayDurumu; source: AdayKaynagi };
  /** Telefonu eşleşen yakın tarihli kayıtlar — UYARI, engel değil */
  benzerler: Array<{ id: string; studentName: string; status: AdayDurumu }>;
}

export function adayOlustur(veri: YeniAday): Promise<AdayOlusturmaSonucu> {
  return api<AdayOlusturmaSonucu>("/crm/adaylar", { yontem: "POST", govde: veri });
}

export function adayDurumDegistir(
  id: string,
  hedef: AdayDurumu,
  ek: { lostReason?: KayipNedeni | null; not?: string | null } = {},
): Promise<{ id: string; status: AdayDurumu }> {
  return api(`/crm/adaylar/${id}/durum`, {
    yontem: "POST",
    govde: { hedef, lostReason: ek.lostReason ?? null, not: ek.not ?? null },
  });
}

export function adayEtkinligiEkle(
  id: string,
  veri: { kind: EtkinlikTuru; summary: string; outcome?: string | null },
): Promise<{ id: string; kind: EtkinlikTuru; createdAt: string }> {
  return api(`/crm/adaylar/${id}/etkinlik`, { yontem: "POST", govde: veri });
}

export function huniRaporu(
  aralik: {
    baslangic?: string;
    bitis?: string;
  } = {},
): Promise<HuniRaporu> {
  return api<HuniRaporu>("/crm/huni", { sorgu: aralik });
}

// =====================================================================
// Takvim — F-CAL
// =====================================================================

/**
 * ================================================================
 * MESGULIYET MODUNDA BASLIK ALANI CEVAPTA HIC YOK
 * ================================================================
 * Sunucu görünüm modunu ayrık birleşim olarak dönüyor. Bu tip birebir
 * öyle yazıldı: `title` alanına ulaşmak için `mod === "AYRINTI"`
 * daraltması ZORUNLU, atlamak derleme hatası veriyor.
 *
 * `title?: string` gibi isteğe bağlı bir alan yazsaydık ekran
 * `e.title ?? "Meşgul"` gibi bir şey yazabilir ve satış danışmanının
 * göremeyeceği bir alanı okumaya çalıştığını fark etmezdik.
 */
export type TakvimEtkinligi =
  | {
      mod: "MESGULIYET";
      id: string;
      startsAt: string;
      endsAt: string;
      isAllDay: boolean;
    }
  | {
      mod: "AYRINTI";
      id: string;
      startsAt: string;
      endsAt: string;
      isAllDay: boolean;
      type: string;
      title: string;
      description: string | null;
      location: string | null;
      meetUrl: string | null;
    };

export interface TakvimCevabi {
  mod: "AYRINTI" | "MESGULIYET";
  etkinlikler: TakvimEtkinligi[];
}

/**
 * Tarih aralığı.
 *
 * `interface` yerine `type` — `interface` dizin imzası taşımadığı için
 * sorgu parametresi olarak geçirilemiyordu (TS2322).
 */
export type Aralik = { baslangic: string; bitis: string };

export function kendiTakvimim(a: Aralik): Promise<{ etkinlikler: TakvimEtkinligi[] }> {
  return api("/takvim/benim", { sorgu: a });
}

export function kurumTakvimi(a: Aralik): Promise<{ etkinlikler: TakvimEtkinligi[] }> {
  return api("/takvim/kurum", { sorgu: a });
}

export function baskasininTakvimi(userId: string, a: Aralik): Promise<TakvimCevabi> {
  return api<TakvimCevabi>(`/takvim/${userId}`, { sorgu: a });
}

export function cokluTakvim(
  kullanicilar: string[],
  a: Aralik,
): Promise<Array<TakvimCevabi & { userId: string }>> {
  return api("/takvim/coklu", { yontem: "POST", govde: { kullanicilar, ...a } });
}

export interface MusaitlikCevabi {
  boslar: Array<{ baslangic: string; bitis: string }>;
  oneriler: Array<{ baslangic: string; bitis: string }>;
}

export function musaitlikBul(veri: {
  kullanicilar: string[];
  baslangic: string;
  bitis: string;
  sureDakika: number;
  adimDakika?: number;
}): Promise<MusaitlikCevabi> {
  return api<MusaitlikCevabi>("/takvim/musaitlik", { yontem: "POST", govde: veri });
}

/** F-CAL-06c — takvim kapsamından süzülmüş adlar; kimlik elle yazdırılmaz. */
export function takvimKatilimcilari(
  arama = "",
): Promise<{ kisiler: Array<{ userId: string; ad: string; ogrenciNo: number | null }> }> {
  return api("/takvim/katilimcilar", { sorgu: { arama } });
}

export type KagitRizaTuru = "PRIVACY_NOTICE" | "DATA_TRANSFER_ABROAD" | "PARENT_GUARANTEE";
export interface KagitOnayHedefi {
  ogrenciUserId: string;
  veliler: Array<{ userId: string; ad: string }>;
  metinler: Array<{ tur: KagitRizaTuru; surum: string }>;
}
export function kagitOnayHedefi(ogrenciId: string): Promise<KagitOnayHedefi> {
  return api(`/kvkk/rizalar/kagit/hedef/${encodeURIComponent(ogrenciId)}`);
}
export interface KagitOnayFormu {
  ogrenciUserId: string;
  veliUserId: string | null;
  kayitlar: Array<{ tur: KagitRizaTuru; surum: string }>;
  gerekce: string;
  belge: File;
}
/** F-LGL-03c — belge mevcut POST ile aynı istekte gider; api() oturum ve hata korumasını taşır. */
export function kagitOnayKaydet(veri: KagitOnayFormu): Promise<{ denetimYazildi: boolean }> {
  const govde = new FormData();
  govde.append("ogrenciUserId", veri.ogrenciUserId);
  if (veri.veliUserId) govde.append("veliUserId", veri.veliUserId);
  govde.append("kayitlar", JSON.stringify(veri.kayitlar));
  govde.append("gerekce", veri.gerekce);
  govde.append("belge", veri.belge);
  return api("/kvkk/rizalar/kagit", { yontem: "POST", govde });
}

export interface KapasiteSatiri {
  coachId: string;
  userId: string;
  aktifOgrenci: number;
  toplamDk: number;
  doluDk: number;
  bosDk: number;
  /** `null` = pencere sıfır uzunlukta; ölçülemez. "%0" DEĞİL. */
  dolulukOrani: number | null;
}

export function kapasiteTablosu(a: Aralik): Promise<{ uzmanlar: KapasiteSatiri[] }> {
  return api("/takvim/kapasite", { sorgu: a });
}

export function kurumEtkinligiEkle(veri: {
  baslik: string;
  aciklama?: string | null;
  baslangic: string;
  bitis: string;
  tumGun?: boolean;
}): Promise<{ id: string; title: string }> {
  return api("/takvim/kurum", { yontem: "POST", govde: veri });
}

// =====================================================================
// Operasyon — duyuru, görev, izin (matris §5.8)
// =====================================================================

export interface Duyuru {
  id: string;
  title: string;
  body: string;
  linkUrl: string | null;
  publishAt: string;
  isPinned: boolean;
  okunduMu: boolean;
  /** F-NTF-08b — duyuruyu bu kullanıcı yazdı (okunma listesi yalnız ona açık olabilir). */
  benYazdim?: boolean;
}

/** F-NTF-08b — koç duyurusunun hedefi: yalnız öğrenci · yalnız veli · ikisi. */
export type DuyuruKime = "OGRENCI" | "VELI" | "IKISI";

export type HedefKitle =
  "ALL_STUDENTS" | "ALL_PARENTS" | "ALL_STAFF" | "BY_GRADE" | "BY_FIELD" | "BY_COACH" | "CUSTOM";

export function duyurulariGetir(): Promise<ListeKunyesi & { duyurular: Duyuru[] }> {
  return api("/operasyon/duyurular");
}

export function duyuruOkundu(id: string): Promise<{ okundu: true }> {
  return api(`/operasyon/duyurular/${id}/okundu`, { yontem: "POST" });
}

export function duyuruOlustur(veri: {
  baslik: string;
  govde: string;
  hedefKitle: HedefKitle;
  hedefFiltre?: Record<string, string[]>;
  sabitlensin?: boolean;
  kime?: DuyuruKime;
}): Promise<{ id: string; title: string }> {
  return api("/operasyon/duyurular", { yontem: "POST", govde: veri });
}

export type GorevDurumu = "PENDING_ACCEPT" | "IN_PROGRESS" | "COMPLETED" | "FAILED";

export interface Gorev {
  id: string;
  title: string;
  description: string | null;
  status: GorevDurumu;
  dueDate: string | null;
  priority: number;
  createdAt: string;
  /** SUNUCUDAN gelir — ekran sabit geçiş tablosu tutmuyor */
  izinliGecisler: GorevDurumu[];
}

export function gorevlerimiGetir(
  durum?: GorevDurumu,
): Promise<ListeKunyesi & { gorevler: Gorev[] }> {
  return api("/operasyon/gorevlerim", { sorgu: { durum } });
}

export function gorevDurumuDegistir(
  id: string,
  hedef: GorevDurumu,
  sebep?: string | null,
): Promise<{ id: string; status: GorevDurumu }> {
  return api(`/operasyon/gorevlerim/${id}/durum`, {
    yontem: "POST",
    govde: { hedef, sebep: sebep ?? null },
  });
}

export type IzinDurumu = "PENDING" | "APPROVED" | "REJECTED" | "CANCELLED";

export interface Izin {
  id: string;
  coachId: string;
  startDate: string;
  endDate: string;
  reason: string;
  status: IzinDurumu;
  coveringCoachId: string | null;
  decidedAt: string | null;
  createdAt: string;
}

export function izinleriGetir(durum?: IzinDurumu): Promise<ListeKunyesi & { izinler: Izin[] }> {
  return api("/operasyon/izinler", { sorgu: { durum } });
}

export function izinTalebiAc(veri: {
  baslangic: string;
  bitis: string;
  gerekce: string;
  devirNotu?: string | null;
}): Promise<{ id: string; status: IzinDurumu }> {
  return api("/operasyon/izinler", { yontem: "POST", govde: veri });
}

export function izinKararaBagla(
  id: string,
  hedef: "APPROVED" | "REJECTED",
  not?: string | null,
): Promise<{ id: string; status: IzinDurumu }> {
  return api(`/operasyon/izinler/${id}/karar`, {
    yontem: "POST",
    govde: { hedef, not: not ?? null },
  });
}

// =====================================================================
// Öğrenci/veli talebi ve destek talebi (matris §5.8 / §5.15)
// =====================================================================

export type TalepDurumu = "PENDING" | "IN_REVIEW" | "APPROVED" | "REJECTED" | "CANCELLED";

export interface TalepKategorisi {
  id: string;
  code: string;
  name: string;
  description: string | null;
  openableBy: "STUDENT" | "PARENT" | "BOTH";
}

export interface OgrenciTalebi {
  id: string;
  studentId: string;
  /** Talebin ait olduğu öğrencinin adı; sunucu kapsamdan geçen satırda doldurur */
  ogrenciAdi?: string | null;
  subject: string;
  body: string;
  status: TalepDurumu;
  decidedAt: string | null;
  decisionNote: string | null;
  createdAt: string;
  category: { code: string; name: string; decisionPermission: string };
  /**
   * SUNUCUDAN gelir — ekran kategori koduna bakıp KENDİ karar vermiyor.
   * Hangi iznin gerektiğini kategori söylüyor; ekranda tekrar
   * hesaplansaydı yetki mantığı iki yerde yaşardı (CLAUDE.md kural 1).
   */
  kararaBaglayabilirMiyim: boolean;
}

export function talepKategorileri(): Promise<TalepKategorisi[]> {
  return api<TalepKategorisi[]>("/operasyon/talep-kategorileri");
}

export function talepleriGetir(
  durum?: TalepDurumu,
  /**
   * Tek öğrencinin talepleri — 360° ekranının Talepler sekmesi.
   *
   * Süzgeç bir KOLAYLIK, bir yetki değil: sunucu kapsam filtresini
   * ayrıca uyguluyor ve ikisi `AND` ile birleşiyor
   * (`ogrenci-kapsam.ts` -> `ogrenciSuzgecliFiltre`). Adres çubuğuna
   * başka öğrencinin kimliği yazılınca boş dönüyor.
   */
  ogrenciId?: string,
): Promise<ListeKunyesi & { talepler: OgrenciTalebi[] }> {
  return api("/operasyon/talepler", { sorgu: { durum, ogrenci: ogrenciId } });
}

/**
 * Personelin öğrenci adına talep açması. Kapsam ve izin SUNUCUDA
 * (`talepHedefi`): atanmış olmadığı öğrenci için 403.
 */
export function talepAc(veri: {
  ogrenciId: string;
  kategoriId: string;
  konu: string;
  govde: string;
}): Promise<{ id: string; status: TalepDurumu }> {
  return api("/operasyon/talepler", { yontem: "POST", govde: veri });
}

export function talebiKararaBagla(
  id: string,
  hedef: "APPROVED" | "REJECTED",
  not?: string | null,
): Promise<{ id: string; status: TalepDurumu }> {
  return api(`/operasyon/talepler/${id}/karar`, {
    yontem: "POST",
    govde: { hedef, not: not ?? null },
  });
}

export type DestekKonusu = "TECHNICAL" | "HOW_TO" | "BUG" | "SUGGESTION" | "OTHER";
export type DestekDurumu = "OPEN" | "IN_REVIEW" | "ANSWERED" | "CLOSED";

export interface DestekOzeti {
  id: string;
  ticketNo: number;
  topic: DestekKonusu;
  subject: string;
  status: DestekDurumu;
  screenKey: string | null;
  createdAt: string;
  respondedAt: string | null;
  /** Yalnızca yönetim görünümünde gelir */
  userId?: string;
}

export interface DestekMesaji {
  id: string;
  senderId: string;
  body: string;
  /** SUNUCUDA izinden türetilir — istemciden gelmez */
  isStaff: boolean;
  createdAt: string;
}

export interface DestekAyrinti extends DestekOzeti {
  /*
    SUNUCU BU ALANI `message` DIYE GONDERIYOR, `body` DIYE DEGIL.

    Onceki hali `body?: string` idi ve OPSIYONEL oldugu icin derleyici
    hic bagirmadi: talebin ASIL GOVDESI hicbir zaman gelmiyordu.
    Su an bu tipi cizen bir ekran YOK — yani kusur gizli kaldi; bir
    sonraki kisi `ayrinti.body` yazsaydi sessizce `undefined`
    cizerdi. (`body` adi `DestekMesaji` uzerinde DOGRU; ayni ad iki
    tipte iki farkli sey.)
  */
  message: string;
  mesajlar: DestekMesaji[];
}

export function destekListesi(durum?: DestekDurumu): Promise<
  ListeKunyesi & {
    /** SUNUCUDAN gelir: bu kullanıcı hepsini mi yoksa kendininkini mi görüyor */
    yonetimGorunumu: boolean;
    talepler: DestekOzeti[];
  }
> {
  return api("/yardim/destek", { sorgu: { durum } });
}

export function destekGetir(id: string): Promise<DestekAyrinti> {
  return api<DestekAyrinti>(`/yardim/destek/${id}`);
}

export function destekMesajEkle(id: string, govde: string): Promise<{ id: string }> {
  return api(`/yardim/destek/${id}/mesaj`, { yontem: "POST", govde: { govde } });
}

export function destekKapat(id: string): Promise<{ id: string; status: DestekDurumu }> {
  return api(`/yardim/destek/${id}/kapat`, { yontem: "POST" });
}

export function destekAc(veri: {
  konu: DestekKonusu;
  baslik: string;
  mesaj: string;
  ekranAnahtari?: string | null;
}): Promise<{ id: string; ticketNo: number }> {
  return api("/yardim/destek", { yontem: "POST", govde: veri });
}

export function destekEkranIstatistigi(): Promise<Array<{ ekran: string | null; sayi: number }>> {
  return api("/yardim/destek/ekran-istatistigi");
}

// =====================================================================
// Tanışma görüşmesi — F-CRM-04, matris §5.2
// =====================================================================

export type GorusmeSonucu = "ENROLLED" | "NOT_ENROLLED" | "CONSIDERING" | "UNREACHED";

export interface OnayBekleyenGorusme {
  id: string;
  scheduledAt: string;
  introOutcome: GorusmeSonucu | null;
  introReport: string | null;
  introReportSubmittedAt: string | null;
  /** Plansız (serbest) raporda `null` — başlık `ogrenciAdi`ndan (sürüm 9a) */
  lead: { id: string; studentName: string } | null;
  ogrenciAdi: string;
  serbest: boolean;
  yazan: string;
  /**
   * Kendi yazdığın rapor — onaylayamazsın (iki çift göz, matris §5.2).
   * SUNUCUDAN gelir; ekran kendi oturum kimliğiyle karşılaştırma yapmıyor.
   */
  kendiRaporum: boolean;
}

export interface Gorusmem {
  id: string;
  scheduledAt: string;
  durationMin: number | null;
  agenda: string | null;
  status: string;
  introOutcome: GorusmeSonucu | null;
  introReport: string | null;
  introReportSubmittedAt: string | null;
  introReportApprovedAt: string | null;
  meetUrl: string | null;
  /** Plansız (serbest) raporda `null` */
  lead: { id: string; studentName: string; studentPhone: string | null } | null;
  ogrenciAdi: string;
  ogrenciIletisim: string | null;
  /**
   * Veli ve seviye: adayı ZATEN görebilen (crm.aday.goruntule) için dolu; diğerlerinde `null` — sunucu
   * karar veriyor, ekran "—" yazar (sürüm 9a, yeni görünürlük açılmadı).
   */
  veliAdi: string | null;
  veliIletisim: string | null;
  seviye: string | null;
  serbest: boolean;
  planlayan: string | null;
  gorulduAt: string | null;
  /** SUNUCUDAN: görüşmeyi yapan uzman ben miyim (rapor/katılmadı düğmeleri buna bağlı) */
  kendiGorusmem: boolean;
  /** SUNUCUDAN gelir — onaylanmış rapor değiştirilemez */
  raporYazilabilir: boolean;
  katilmadiIsaretlenebilir: boolean;
  meetDuzenlenebilir: boolean;
}

/**
 * Görüşme planlanacak uzmanların seçim listesi.
 *
 * `personelListesi` DEĞİL: o uç personel yönetimi izni istiyor ve
 * satış danışmanında yok. Ekran onu çağırdığı sürece "Personel
 * listesi alınamadı; görüşme planlanamıyor" yazıyordu.
 */
export function gorusmeUzmanlari(): Promise<Array<{ id: string; ad: string }>> {
  return api("/crm/gorusme-uzmanlari");
}

export function gorusmelerim(): Promise<ListeKunyesi & { gorusmeler: Gorusmem[] }> {
  return api("/crm/gorusmelerim");
}

export function onayBekleyenGorusmeler(): Promise<OnayBekleyenGorusme[]> {
  return api<OnayBekleyenGorusme[]>("/crm/gorusmeler/onay-bekleyen");
}

export function gorusmePlanla(veri: {
  leadId: string;
  coachId: string;
  scheduledAt: string;
  durationMin?: number | null;
  agenda?: string | null;
  meetUrl?: string | null;
}): Promise<{ id: string; scheduledAt: string }> {
  return api("/crm/gorusmeler", { yontem: "POST", govde: veri });
}

/** Sürüm 9a — UNİKOÇ "Katılmadı". Yalnız görüşmeyi yapan uzman (sunucu SELF kontrolü). */
export function gorusmeKatilmadi(id: string): Promise<{ id: string; status: "STUDENT_NO_SHOW" }> {
  return api(`/crm/gorusmeler/${id}/katilmadi`, { yontem: "POST" });
}

/** Sürüm 9a — tanışma görüşmesinin Meet bağlantısı (`null` = kaldır). */
export function tanismaMeetGuncelle(
  id: string,
  meetUrl: string | null,
): Promise<{ id: string; meetUrl: string | null }> {
  return api(`/crm/gorusmeler/${id}/meet`, { yontem: "POST", govde: { meetUrl } });
}

/** Sürüm 9a — liste açılınca kendi görülmemiş görüşmelerim "görüldü" olur. */
export function tanismalariGorduIsaretle(idler: string[]): Promise<{ isaretlenen: number }> {
  return api("/crm/gorusmeler/goruldu", { yontem: "POST", govde: { idler } });
}

/** Sürüm 9a — plansız tanışma raporu (UNİKOÇ "Ücretsiz Tanışma Seans Raporu"; tüm alanlar isteğe bağlı). */
export function serbestTanismaRaporu(veri: {
  ogrenciAdi?: string | null;
  veliAdi?: string | null;
  ogrenciIletisim?: string | null;
  veliIletisim?: string | null;
  alan?: string | null;
  introOutcome?: GorusmeSonucu | null;
  rapor?: string | null;
}): Promise<{ id: string }> {
  return api("/crm/gorusmeler/serbest-rapor", { yontem: "POST", govde: veri });
}

export function gorusmeRaporuYaz(
  id: string,
  veri: { introOutcome: GorusmeSonucu; introReport: string },
): Promise<{ id: string; introOutcome: GorusmeSonucu }> {
  return api(`/crm/gorusmeler/${id}/rapor`, { yontem: "POST", govde: veri });
}

export function gorusmeRaporuOnayla(id: string): Promise<{ id: string; onaylandi: true }> {
  return api(`/crm/gorusmeler/${id}/onay`, { yontem: "POST" });
}

// =====================================================================
// Finans — paket, abonelik, tahsilat (F-BIL)
// =====================================================================

export type Periyot = "MONTHLY" | "QUARTERLY" | "SEMIANNUAL" | "ANNUAL" | "CUSTOM";
export type OdemeYontemi = "ONLINE" | "BANK_TRANSFER" | "CASH" | "OTHER";

export interface Paket {
  id: string;
  name: string;
  period: Periyot;
  /** Para DİZGİ — `number` kayan nokta sorununu taşırdı */
  fiyat: string;
  currency: string;
  sessionQuota: number | null;
  isActive: boolean;
}

/**
 * ================================================================
 * PARA DUVARI TİPTE — `mod`a bakmadan tutar okunamıyor
 * ================================================================
 * Matris §5.7: uzman ödeme durumunu görmemeli. Sunucu iki ayrı şekil
 * dönüyor ve `OZET` görünümünde tutar alanları CEVAPTA HİÇ YOK.
 *
 * `toplamTutar?: string` gibi isteğe bağlı bir alan yazsaydık ekran
 * `abonelik.toplamTutar ?? "—"` yazabilir ve göremeyeceği bir alanı
 * okumaya çalıştığını kimse fark etmezdi.
 */
export interface AbonelikOzeti {
  mod: "OZET";
  id: string;
  paketAdi: string;
  aktifMi: boolean;
  baslangic: string;
  bitis: string;
  /** `null` = paket görüşme hakkı TANIMLAMIYOR (sıfır değil) */
  kalanGorusme: number | null;
  toplamGorusme: number | null;
  durum: string;
  /** Sürüm 9a — paket hakları (parasız). Eski sunucu göndermezse bölüm çizilmez. */
  haklar?: PaketHaklari;
}

/** Sürüm 9a — `Package.features`ten yalnız bilinen anahtarlar (sunucu `paket-haklari.ts`). */
export interface PaketHaklari {
  ozelDers: boolean;
  /** `null` = tanımlanmamış (sıfır değil) */
  ozelDersSaati: number | null;
  soruCozumGrubu: boolean;
  kitapGonderimi: boolean;
  periyot: Periyot;
}

export interface AbonelikTaksiti {
  id: string;
  sira: number;
  tutar: string;
  vade: string;
  odendiMi: boolean;
  odemeTarihi: string | null;
  /** ANLIK hesaplanır — gece işi koşmadı diye gecikmiş taksit "bekliyor" görünmemeli */
  gecikmisMi: boolean;
  durum: string;
  yontem: OdemeYontemi | null;
}

export interface AbonelikTam extends Omit<AbonelikOzeti, "mod"> {
  mod: "TAM";
  toplamTutar: string;
  indirim: string;
  odenen: string;
  kalan: string;
  taksitler: AbonelikTaksiti[];
}

export type AbonelikGorunumu = AbonelikOzeti | AbonelikTam;

export function paketleriGetir(): Promise<Paket[]> {
  return api<Paket[]>("/finans/paketler");
}

export function paketTanimla(veri: {
  ad: string;
  periyot: Periyot;
  fiyat: string;
  gorusmeHakki?: number | null;
}): Promise<{ id: string; name: string }> {
  return api("/finans/paketler", { yontem: "POST", govde: veri });
}

/** Aboneliği olmayan öğrencide `null` döner. */
export function abonelikGetir(ogrenciId: string): Promise<AbonelikGorunumu | null> {
  return api<AbonelikGorunumu | null>(`/finans/abonelik/${ogrenciId}`);
}

export function abonelikAc(veri: {
  ogrenciId: string;
  paketId: string;
  baslangic: string;
  bitis: string;
  indirim?: string | null;
  taksitSayisi: number;
  ilkVade: string;
  araAy?: number;
}): Promise<{ id: string; taksitSayisi: number }> {
  return api("/finans/abonelikler", { yontem: "POST", govde: veri });
}

export function tahsilatIsle(
  taksitId: string,
  veri: { yontem: OdemeYontemi; odemeTarihi?: string | null; not?: string | null },
): Promise<{ id: string; status: string; paidAt: string }> {
  return api(`/finans/taksitler/${taksitId}/tahsilat`, { yontem: "POST", govde: veri });
}

export interface KuyrukSatiri {
  id: string;
  sira: number;
  tutar: string;
  vade: string;
  gecikmisMi: boolean;
  ogrenciId: string;
  /** Sütun başlığı "Öğrenci / paket" ama önce yalnızca paket geliyordu. */
  ogrenciAdi: string;
  ogrenciNo: number;
  abonelikId: string;
  paketAdi: string;
}

export function tahsilatKuyrugu(
  gun?: number,
): Promise<ListeKunyesi & { taksitler: KuyrukSatiri[] }> {
  return api("/finans/tahsilat-kuyrugu", { sorgu: { gun } });
}

// =====================================================================
// KVKK — rıza, sürümlü metin, veri sahibi başvurusu (F-LGL-02)
// =====================================================================

export type RizaTuru =
  | "DATA_TRANSFER_ABROAD"
  | "WITHDRAWAL_RIGHT"
  | "PRIVACY_NOTICE"
  | "CONTACT_SMS"
  | "CONTACT_WHATSAPP"
  | "CONTACT_EMAIL"
  | "MARKETING"
  | "PROTECTED_NOTES"
  | "DISTANCE_SALES"
  | "PRE_INFO_FORM"
  | "PARENT_GUARANTEE";

export type BasvuruTuru =
  "ACCESS" | "RECTIFICATION" | "ERASURE" | "PORTABILITY" | "OBJECTION" | "RESTRICTION";

export type BasvuruDurumu = "RECEIVED" | "IN_PROGRESS" | "COMPLETED" | "REJECTED";

export interface RizaSatiri {
  type: RizaTuru;
  documentVersion: string;
  granted: boolean;
  grantedAt: string;
  revokedAt: string | null;
  veliVerdi: boolean;
  /** Yürürlükteki sürüme ait mi — eski sürüme verilmiş onay geçersiz */
  guncelMi: boolean;
}

export interface RizaDurumu {
  userId: string;
  resitMi: boolean;
  /**
   * SUNUCUDAN gelir — ekran yaş hesabı YAPMIYOR.
   * 18 yaşını dolduran öğrencinin rızasını yalnızca kendisi yönetir.
   */
  rizayiKimVerir: "KENDISI" | "VELI";
  yururluktekiSurumler: Record<string, string>;
  rizalar: RizaSatiri[];
  /**
   * Hangi rıza türleri ZORUNLU — eksik olsun olmasın.
   *
   * `engelleyenEksikler` yalnızca eksikleri sayıyor; verilmiş bir
   * rızanın hangi gruba ait olduğu buradan bilinir. Ekran bu listeyi
   * KENDİ TUTMAMALI: sunucudaki `ZORUNLU_RIZALAR` 04.09.2026'da
   * değişti (yurt dışı aktarım isteğe bağlıdan zorunluya geçti) ve bir
   * kopya o gün sessizce yanlış olurdu.
   */
  zorunluTurler: RizaTuru[];
  /** Kullanımı engelleyen zorunlu onay eksikleri */
  engelleyenEksikler: RizaTuru[];
  eksikIstegeBagli: RizaTuru[];
  aktiflesebilirMi: boolean;
  /** Zorunlu onayların kullanım etkisi; tüm hesap türleri için sunucudan gelir (F-LGL-03b). */
  rizaHesabiEtkiler?: boolean;
}

export function rizalarim(): Promise<RizaDurumu> {
  return api<RizaDurumu>("/kvkk/rizalarim");
}

export function rizaDurumu(userId: string): Promise<RizaDurumu> {
  return api<RizaDurumu>(`/kvkk/rizalar/${userId}`);
}

export interface HukukiMetin {
  id: string;
  type: RizaTuru;
  version: string;
  title: string;
  body: string;
  publishedAt: string | null;
}

export function metinGetir(tur: RizaTuru): Promise<HukukiMetin> {
  return api<HukukiMetin>(`/kvkk/metinler/${tur}`);
}

export interface MetinSurumu {
  id: string;
  type: RizaTuru;
  version: string;
  title: string;
  publishedAt: string | null;
  supersededAt: string | null;
  createdAt: string;
}

export function metinSurumleri(tur?: RizaTuru): Promise<MetinSurumu[]> {
  return api<MetinSurumu[]>("/kvkk/metinler", { sorgu: { tur } });
}

/** Bir öğrencinin neden hatırlatılamadığı — F-LGL-05. */
export type HatirlatmaAtlamaSebebi = "VELI_YOK" | "COK_YAKIN";

export interface MuvafakatHatirlatmaSonucu {
  /** Kuru provada `gonderilecek`, gerçek gönderimde `gonderildi` dolu */
  gonderilecek?: number;
  gonderildi?: number;
  /**
   * Atlananlar SESSİZCE düşürülmüyor — sebebiyle birlikte geliyor.
   *
   * `VELI_YOK` özellikle önemli: o öğrenci hiçbir zaman
   * hatırlatılamaz ve tekrar denemek işe yaramaz. Yapılacak BAŞKA bir
   * iş var (önce veliyi bağlamak) ve o iş ancak sebep görününce
   * yapılır. Ekranda gizleme.
   */
  atlanan: Array<{ ogrenciId: string; ogrenciAdi: string; sebep: HatirlatmaAtlamaSebebi }>;
  kuruProva: boolean;
}

/**
 * Eksik muvafakat için veliye hatırlatma — F-LGL-05.
 *
 * `kuruProva: true` HİÇBİR ŞEY GÖNDERMEZ; kimin hatırlatılacağını
 * döner. Toplu gönderim kapsam onayından geçmek zorunda ve ekranda
 * yazan sayı bir tahmin değil, onaydan sonra gönderimi yapacak olan
 * AYNI kodun ürettiği karar olmalı.
 *
 * Akış: önce `kuruProva: true` → sayıyı kullanıcıya yazılı göster →
 * onay al → aynı çağrıyı `kuruProva: false` ile yap.
 *
 * Sıklık koruması sunucuda ve tek öğrenci gönderiminde de geçerli:
 * tek tek göndermek, toplu sınırı atlatmanın en kolay yolu olurdu.
 * Süre `MUVAFAKAT_HATIRLATMA_GUN` (contracts) — ekranda elle yazma.
 */
export function muvafakatHatirlat(veri: {
  ogrenciId?: string;
  kuruProva: boolean;
}): Promise<MuvafakatHatirlatmaSonucu> {
  return api("/kvkk/muvafakat-hatirlat", { yontem: "POST", govde: veri });
}

/** Bir satırın iki sürüm arasındaki hâli — F-LGL-04. */
export interface MetinFarkSatiri {
  tur: "AYNI" | "EKLENDI" | "SILINDI";
  metin: string;
  /** Eski sürümdeki satır numarası; eklenen satırda `null` */
  eskiSatir: number | null;
  /** Yeni sürümdeki satır numarası; silinen satırda `null` */
  yeniSatir: number | null;
}

export interface MetinKarsilastirma {
  eski: { surum: string; baslik: string; yayinlandi: string | null };
  yeni: { surum: string; baslik: string; yayinlandi: string | null };
  /**
   * Başlık gövde farkının İÇİNDE görünmez (ayrı bir alan). Değişmiş
   * bir başlığı kaçırmak, metnin kapsamının değiştiğini kaçırmaktır.
   */
  baslikDegisti: boolean;
  satirlar: MetinFarkSatiri[];
  ozet: {
    eklenen: number;
    silinen: number;
    /** Ekran bunu AÇIKÇA yazmalı — boş liste "yüklenmedi" ile karışır */
    aynilar: boolean;
  };
}

/**
 * İki yasal metin sürümünü karşılaştırır — F-LGL-04.
 *
 * Fark SUNUCUDA hesaplanıyor: yeni metin yayınlandığında eski rızaların
 * geçerliliğini yitirip yitirmediği hukuki bir karar ve o kararın
 * dayanağı istemciye göre değişemez.
 *
 * Görünüm SALT OKUNUR — bu ekrandan metin düzenlenmez; düzenleme yayın
 * akışının kendi adımı (`metinYayinla`).
 */
export function metinKarsilastir(
  tur: RizaTuru,
  eski: string,
  yeni: string,
): Promise<MetinKarsilastirma> {
  return api<MetinKarsilastirma>(`/kvkk/metinler/${tur}/karsilastir`, {
    sorgu: { eski, yeni },
  });
}

export function metinYayinla(veri: {
  tur: RizaTuru;
  surum: string;
  baslik: string;
  govde: string;
}): Promise<{ id: string; version: string }> {
  return api("/kvkk/metinler", { yontem: "POST", govde: veri });
}

/**
 * Sunucu bu yolu TANIMIYOR mu. Nest'in yol bulunamadı 404'ü gövdede `kod` taşımaz (`BILINMEYEN`e düşer); bizim
 * 404'lerimiz her zaman `kod` taşır. Yeni istemci eski API'ye bağlandığında (istemci önce dağıtılınca ya da geri
 * dönüşte) kişisel uçlar yok — eski yola düşülür (Codex tur 32 [P2]). `kod`lu bir 404 ASLA eski yola düşürmez.
 */
export function eskiApiYoluYokMu(hata: unknown): boolean {
  return hata instanceof ApiHatasi && hata.durum === 404 && hata.kod === "BILINMEYEN";
}

export async function rizaVer(tur: RizaTuru, surum: string): Promise<{ id: string }> {
  try {
    return await api<{ id: string }>("/kvkk/rizalarim", { yontem: "POST", govde: { tur, surum } });
  } catch (hata) {
    if (!eskiApiYoluYokMu(hata)) throw hata;
    return api<{ id: string }>("/kvkk/rizalar", { yontem: "POST", govde: { tur, surum } });
  }
}

export async function rizaGeriCek(
  tur: RizaTuru,
  hedefUserId?: string | null,
): Promise<{ id: string; revokedAt: string }> {
  type Sonuc = { id: string; revokedAt: string };
  if (hedefUserId) {
    return api<Sonuc>("/kvkk/rizalar/geri-cek", { yontem: "POST", govde: { tur, hedefUserId } });
  }
  try {
    return await api<Sonuc>("/kvkk/rizalarim/geri-cek", { yontem: "POST", govde: { tur } });
  } catch (hata) {
    if (!eskiApiYoluYokMu(hata)) throw hata;
    return api<Sonuc>("/kvkk/rizalar/geri-cek", { yontem: "POST", govde: { tur } });
  }
}

export interface Basvuru {
  id: string;
  requesterId: string;
  subjectId: string;
  kind: BasvuruTuru;
  status: BasvuruDurumu;
  detail: string;
  dueAt: string;
  resolution: string | null;
  resolvedAt: string | null;
  createdAt: string;
  /** ANLIK hesaplanır — zamanlanmış işin durumu güncellemesi beklenmez */
  gecikmisMi: boolean;
  kalanGun: number;
}

export function basvurular(durum?: BasvuruDurumu): Promise<{
  toplam: number;
  yonetimGorunumu: boolean;
  basvurular: Basvuru[];
}> {
  return api("/kvkk/basvurular", { sorgu: { durum } });
}

export function basvuruAc(veri: {
  tur: BasvuruTuru;
  ayrinti: string;
  hedefUserId?: string | null;
}): Promise<{ id: string; dueAt: string }> {
  return api("/kvkk/basvurular", { yontem: "POST", govde: veri });
}

export function basvuruSonuclandir(
  id: string,
  veri: { durum: "COMPLETED" | "REJECTED"; sonuc: string },
): Promise<{ id: string; status: BasvuruDurumu }> {
  return api(`/kvkk/basvurular/${id}/sonuclandir`, { yontem: "POST", govde: veri });
}

// =====================================================================
// Yenileme riski — F-ANL-10
// =====================================================================

export type RiskEtkeni =
  "veriGirisi" | "gorusmeKatilimi" | "veliIlgisi" | "odemeGecikmesi" | "paketBitisi";

export type RiskSeviyesi = "DUSUK" | "ORTA" | "YUKSEK";

/**
 * ================================================================
 * ÖLÇÜLDÜ ile ÖLÇÜLEMEDİ AYRIK BİRLEŞİM
 * ================================================================
 * `skor?: number` gibi isteğe bağlı bir alan olsaydı ekran
 * `skor ?? 0` yazabilir ve dün kayıt olmuş öğrenci "risk 0" görünürdü
 * — yani tam da bu ayrımın engellemek için var olduğu şey.
 */
export type RiskSonucu =
  | {
      olculdu: true;
      skor: number;
      seviye: RiskSeviyesi;
      katkilar: Array<{ etken: RiskEtkeni; risk: number; agirlik: number }>;
      olculemeyen: RiskEtkeni[];
    }
  | {
      olculdu: false;
      neden: "YETERSIZ_VERI";
      olculenAgirlik: number;
      olculemeyen: RiskEtkeni[];
    };

export interface RiskSatiri {
  ogrenciId: string;
  studentNo: number;
  /** Ekran "Öğrenci no 176" yazıyordu; doksan satırı numarayla okuyan yok. */
  ad: string;
  sonuc: RiskSonucu;
  /** "Neden riskli" — skor tek başına eyleme dönüşmüyor */
  baskinEtkenAciklamasi: string | null;
}

export function yenilemeRiski(): Promise<
  ListeKunyesi & {
    /**
     * SUNUCUDAN: ödeme etkeni bu kullanıcı için hesaba katıldı mı.
     * Uzmanda `false` — matris §5.11 kritik kuralı.
     */
    odemeEtkeniDahil: boolean;
    satirlar: RiskSatiri[];
  }
> {
  return api("/analiz/yenileme-riski");
}

// =====================================================================
// Denetim log ekranı — F-LOG, matris §5.17
// =====================================================================

export type OlayKategorisi =
  | "IDENTITY"
  | "SESSION"
  | "DATA_CHANGE"
  | "DATA_READ"
  | "AUTHORIZATION"
  | "WORKFLOW"
  | "COMMUNICATION"
  | "FINANCE"
  | "EXPORT"
  | "SYSTEM"
  | "SECURITY";

export type OlaySiddeti = "INFO" | "NOTICE" | "WARNING" | "CRITICAL";

export interface GorulebilirKategori {
  kategori: OlayKategorisi;
  kapsam: string;
  /** Bu kategoride alan değerleri maskeli mi (matris §5.17 kural 1) */
  maskeli: boolean;
}

export interface AlanDegisikligi {
  field: string;
  oldValue?: unknown;
  newValue?: unknown;
}

export interface LogSatiri {
  id: string;
  occurredAt: string;
  category: OlayKategorisi;
  type: string;
  actorId: string | null;
  actorRole: string | null;
  /** Ekran ham kimlik yazıyordu; ad artık sunucudan geliyor. */
  actorAdi: string | null;
  ogrenciAdi: string | null;
  isSystem: boolean;
  entityType: string | null;
  entityId: string | null;
  studentId: string | null;
  outcome: string;
  severity: OlaySiddeti;
  module: string | null;
  ipAddress: string | null;
  reason: string | null;
  /** Yalnızca DATA_CHANGE olaylarında dolu */
  changes: AlanDegisikligi[] | null;
  /** Değerler maskelendi mi — ekran bunu SÖYLEMEK zorunda */
  maskeliMi: boolean;
}

/**
 * Ekranın hangi filtreleri göstereceği BURADAN gelir.
 *
 * Kendi listesini tutsaydı iki doğruluk kaynağı olurdu: ekran bir
 * kategoriyi gösterir, sunucu reddeder ve kullanıcı "filtre
 * çalışmıyor" diye düşünürdü.
 */
export function logGorunumu(): Promise<{
  kategoriler: GorulebilirKategori[];
  maskelemeVar: boolean;
}> {
  return api("/denetim/log/gorunum");
}

export function logSorgula(filtre: {
  kategori?: string;
  tur?: string;
  aktor?: string;
  ogrenci?: string;
  modul?: string;
  siddet?: string;
  baslangic?: string;
  bitis?: string;
  imlec?: string;
}): Promise<{ satirlar: LogSatiri[]; sonrakiImlec: string | null; maskelemeVar: boolean }> {
  return api("/denetim/log", { sorgu: filtre });
}

// =====================================================================
// Rol ve izin yönetimi — F-ROL
// =====================================================================

export type Kapsam = "ALL" | "TEAM" | "ASSIGNED" | "SELF" | "LINKED" | "LEAD_ONLY" | "NONE";

export interface KatalogIzni {
  code: string;
  module: string;
  name: string;
  description: string | null;
  riskLevel: number;
  isRestricted: boolean;
  allowedScopes: Kapsam[];
  /** Aktörün kendi kapsamı — bundan genişi verilemez */
  kendiKapsamim: Kapsam | null;
  /**
   * SUNUCUDAN: bu izin özel role eklenebilir mi.
   * Ekran kendi hesaplasaydı yetki motorunun kuralları burada TEKRAR
   * yazılmış olurdu.
   */
  verebilirMiyim: boolean;
}

export interface RolOzeti {
  id: string;
  code: string;
  name: string;
  description: string | null;
  isSystem: boolean;
  isActive: boolean;
  defaultScope: Kapsam;
  /** Bu rolü taşıyan kişi hangi uygulamayı görür — F-ROL-04b */
  surface: RolYuzeyi;
  kullaniciSayisi: number;
  /** Süresi dolmamış atama sayısı — pasifleştirme kapısının saydığı */
  etkinKullaniciSayisi: number;
  /** SUNUCUDAN: pasifleştirilebilir mi (F-ROL-05b, Codex tur 36 B7) */
  pasifKarari: { olur: true } | { olur: false; neden: "TEMEL_ROL" | "ATAMA_VAR" };
  izinSayisi: number;
  /** SUNUCUDAN: kendi taşıdığın rolü düzenleyemezsin (F-ROL-16) */
  duzenleyebilirMiyim: boolean;
}

export interface RolAyrinti {
  id: string;
  code: string;
  name: string;
  description: string | null;
  isSystem: boolean;
  isActive: boolean;
  defaultScope: Kapsam;
  permissions: Array<{ code: string; scope: Kapsam }>;
}

export interface KullaniciIzniSatiri {
  id: string;
  code: string;
  name: string;
  effect: "GRANT" | "DENY";
  scope: Kapsam | null;
  reason: string;
  expiresAt: string | null;
  grantedAt: string;
}

export function izinKatalogu(): Promise<KatalogIzni[]> {
  return api<KatalogIzni[]>("/authz/izin-katalogu");
}

export function rolleriGetir(): Promise<RolOzeti[]> {
  return api<RolOzeti[]>("/authz/roller");
}

// =====================================================================
// PERSONEL — F-PPL
// =====================================================================

export interface PersonelSatiri extends HesapDavetDurumu {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  /**
   * T.C. kimlik numarasının MASKELİ hâli (`•••••••••46`) ya da `null`.
   * Ham değer sunucudan HİÇ gelmiyor — özel nitelikli kişisel veri.
   */
  nationalIdMaskeli: string | null;
  status: string;
  roller: string[];
  archivedAt: string | null;
}

export function personelListesi(
  arsiv = false,
): Promise<ListeKunyesi & { personel: PersonelSatiri[] }> {
  return api("/personel", { sorgu: { arsiv: arsiv ? "1" : undefined } });
}

/**
 * Tek personel kaydı.
 *
 * Kapsam dışındaki personel "bulunamadı" dönüyor — varlığının
 * doğrulanması da bir bilgi sızıntısıdır.
 */
export function personelTekil(id: string): Promise<PersonelSatiri> {
  return api(`/personel/${encodeURIComponent(id)}`);
}

/**
 * Personel hesabı açar ve davet gönderir — F-PPL-01b.
 *
 * ŞİFRE BELİRLENMEZ: davet bağlantısı gider, şifreyi kullanıcı kendisi
 * koyar. Yönetici hiçbir aşamada personelin şifresini görmez.
 *
 * SUDO İSTER — 06.08.2026'da eklendi. Uç `roleIds` alarak rol ATIYOR ve
 * rol atama sudo gerektiriyor (G-SES-04). Daha önce istemiyordu ve bu,
 * çalınmış bir oturumun şifre bilmeden tam yetkili yeni bir hesap
 * açmasına izin veriyordu.
 */
export function personelOlustur(govde: {
  email: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  roleIds: string[];
  /** G-AUT-00 — T.C. ile giriş. İsteğe bağlı. */
  nationalId: string | null;
  /** C39 — ortak numarada bu kişi mi telefonuyla giriyor. */
  phoneIsPrimary: boolean;
}): Promise<{ id: string; davetGonderildi: boolean }> {
  return api("/personel", { yontem: "POST", govde });
}

/**
 * T.C. kimlik numarasını değiştirir — AYRI UÇ, AYRI İZİN.
 *
 * `kisi.tc_kimlik.degistir` istiyor ve o izin uzmanda YOK. Profil
 * güncelleme ucundan gönderilirse `400` döner: alan orada bilerek
 * tanınmıyor (G-AUT-00, Emre'nin kararı 05.09.2026).
 *
 * `null` göndermek numarayı siler.
 */
export function ogrenciTcKimlikDegistir(
  id: string,
  nationalId: string | null,
): Promise<{ guncellendi: string[] }> {
  return api(`/ogrenciler/${encodeURIComponent(id)}/tc-kimlik`, {
    yontem: "PATCH",
    govde: { nationalId },
  });
}

/** Personel için aynısı — bkz. `ogrenciTcKimlikDegistir`. */
export function personelTcKimlikDegistir(
  id: string,
  nationalId: string | null,
): Promise<{ guncellendi: string[] }> {
  return api(`/personel/${encodeURIComponent(id)}/tc-kimlik`, {
    yontem: "PATCH",
    govde: { nationalId },
  });
}

export function personelRolleriDegistir(
  id: string,
  roleIds: string[],
): Promise<{ roller: string[] }> {
  return api(`/personel/${id}/roller`, { yontem: "POST", govde: { roleIds } });
}

export function personelArsivle(id: string, gerekce: string): Promise<void> {
  return api(`/personel/${id}/arsivle`, { yontem: "POST", govde: { gerekce } });
}

export function personel2faSifirla(id: string, gerekce: string): Promise<void> {
  return api(`/personel/${id}/2fa-sifirla`, { yontem: "POST", govde: { gerekce } });
}

export function rolGetir(id: string): Promise<RolAyrinti> {
  return api<RolAyrinti>(`/authz/roller/${id}`);
}

/** Rolun yuzeyi — `RoleSurface`. Sunucuda ZORUNLU (F-ROL-04b). */
export type RolYuzeyi = "PANEL" | "STUDENT_APP";

export function rolOlustur(veri: {
  kod: string;
  ad: string;
  aciklama?: string | null;
  varsayilanKapsam: Kapsam;
  /**
   * Bu rolu tasiyan kisi hangi uygulamayi gorur.
   *
   * ISTEGE BAGLI DEGIL ve varsayilani YOK: sunucu eksik gonderilen
   * istegi 400 ile reddediyor. Burada `?` yazsaydik, ekran alani
   * gondermemeyi unuttugunda hata calisma aninda cikardi.
   */
  yuzey: RolYuzeyi;
  izinler: Array<{ code: string; scope: Kapsam }>;
}): Promise<{ id: string; code: string; name: string }> {
  return api("/authz/roller", { yontem: "POST", govde: veri });
}

/**
 * Ozel rolun yuzeyini degistirir — F-ROL-04b.
 *
 * Sistem rolunde 409 doner: `STUDENT`i panele tasimak, ogrenciye
 * personel kabugunu acmak demek olurdu.
 */
/**
 * Rolü pasifleştirir / etkinleştirir — F-ROL-05b. Silmez. Temel roller
 * ve atanmış kullanıcısı olan rol için sunucu 409 döndürür.
 */
export function rolDurumuGuncelle(
  id: string,
  aktif: boolean,
): Promise<{ id: string; isActive: boolean }> {
  return api(`/authz/roller/${id}/durum`, { yontem: "POST", govde: { aktif } });
}

export function rolYuzeyiGuncelle(
  id: string,
  yuzey: RolYuzeyi,
): Promise<{ id: string; surface: RolYuzeyi }> {
  return api(`/authz/roller/${id}/yuzey`, { yontem: "POST", govde: { yuzey } });
}

export function rolIzinleriGuncelle(
  id: string,
  izinler: Array<{ code: string; scope: Kapsam }>,
): Promise<{ id: string; izinSayisi: number; degisenSayisi: number }> {
  return api(`/authz/roller/${id}/izinler`, { yontem: "POST", govde: { izinler } });
}

export function rolAta(veri: {
  userId: string;
  rolId: string;
  kapsam?: Kapsam | null;
}): Promise<{ id: string; rolKodu: string }> {
  return api("/authz/rol-ata", { yontem: "POST", govde: veri });
}

export function kullaniciIzinleri(userId: string): Promise<KullaniciIzniSatiri[]> {
  return api<KullaniciIzniSatiri[]>(`/authz/kullanici/${userId}/izinler`);
}

export function kullaniciIzniVer(veri: {
  userId: string;
  kod: string;
  etki: "GRANT" | "DENY";
  kapsam?: Kapsam | null;
  gerekce: string;
  bitis?: string | null;
}): Promise<{ id: string; effect: string }> {
  return api("/authz/kullanici-izni", { yontem: "POST", govde: veri });
}

// =====================================================================
// YEDEKLEME KONSOLU — F-YED
// =====================================================================

export type YedekSagligi = "IYI" | "DIKKAT" | "KRITIK" | "OLCULEMEDI";

export type HedefUyarisiKodu = "TEK_HEDEF" | "SITE_DISI_YOK" | "DEGISMEZ_YOK" | "YAZILMAYAN_HEDEF";

export interface YedekKosusu {
  id: string;
  isAdi: string;
  hedefAdi: string;
  durum: string;
  kusak: string;
  basladi: string;
  bitti: string | null;
  sureSn: number | null;
  boyutBayt: number | null;
  boyutDegisimYuzde: number | null;
  uzakKopyaVar: boolean;
  elleMi: boolean;
  suresiDoldu: boolean;
  hata: string | null;
  /** `null` = HİÇ doğrulanmadı. `false` = denendi ve BAŞARISIZ. */
  dogrulandiMi: boolean | null;
  dogrulamaSeviyesi: string | null;
}

export interface YedekHedefi {
  id: string;
  ad: string;
  tur: string;
  aktif: boolean;
  siteDisi: boolean;
  degismez: boolean;
  kilitGun: number | null;
  yalnizYazma: boolean;
  sonYazma: string | null;
  sonSinamaAt: string | null;
  sonSinamaBasarili: boolean | null;
  kullanilanBayt: number | null;
  kapasiteBayt: number | null;
}

export interface YedekAnahtari {
  id: string;
  etiket: string;
  algoritma: string;
  /** Anahtarın KENDİSİ değil — yalnızca parmak izi (F-YED-18) */
  parmakIzi: string;
  zarftaMi: boolean;
  aktif: boolean;
  sonDondurme: string | null;
}

export interface YedekKonsolu {
  saglik: {
    saglik: YedekSagligi;
    sebep: string;
    sonYedekYasiSaat: number | null;
    dogrulanmamisSayisi: number;
  };
  sonTatbikat: { tarih: string; basarili: boolean; geriDonusSn: number | null } | null;
  tatbikatGecikmisMi: boolean;
  olculenRtoSn: number | null;
  hedefUyarilari: { kod: HedefUyarisiKodu; aciklama: string }[];
  zarfsizAnahtarVar: boolean;
  kosular: YedekKosusu[];
  hedefler: YedekHedefi[];
  anahtarlar: YedekAnahtari[];
  esikler: { beklenenAralikSaat: number; dogrulamaAraligiGun: number };
  /** Artakalan veritabanları — sistem SİLMİYOR, yalnızca gösteriyor */
  artikVeritabanlari: {
    ad: string;
    boyut: string;
    tur: "GERI_YUKLEME_ONCESI" | "TATBIKAT" | "DIGER";
  }[];
}

export function yedekKonsolu(): Promise<YedekKonsolu> {
  return api("/yedek/konsol");
}

export function elleYedekAl(): Promise<{
  basarili: boolean;
  boyutBayt: number;
  hata: string | null;
}> {
  return api("/yedek/elle-yedek", { yontem: "POST" });
}

export function yedekTatbikati(): Promise<{
  seviye: string;
  basarili: boolean;
  sureSn: number;
  hata: string | null;
}> {
  return api("/yedek/tatbikat", { yontem: "POST" });
}

// =====================================================================
// SUDO MODU — G-SES-04
// =====================================================================

export function sudoAc(sifre: string): Promise<{ kalanSaniye: number }> {
  return api("/auth/sudo", { yontem: "POST", govde: { sifre } });
}

export function sudoDurum(): Promise<{
  acik: boolean;
  kalanSaniye: number;
  /**
   * Pencere açılırsa kaç dakika süreceği. KAPALIYKEN de geliyor —
   * ekran "5 dakika" gibi bir sayıyı kendi tutsaydı, sunucudaki
   * `SUDO_MINUTES` değiştiği gün sessizce yalan söylerdi.
   */
  pencereDakika: number;
}> {
  return api("/auth/sudo/durum");
}

/**
 * Sudo penceresini süresinden ÖNCE kapatır.
 *
 * Süre dolmasını beklemek tek seçenek olsaydı, ortak bir bilgisayarda
 * hassas bir işi bitiren kişi masadan kalkarken arkasında açık bir
 * yetki penceresi bırakırdı.
 */
export function sudoKapat(): Promise<void> {
  return api("/auth/sudo/kapat", { yontem: "POST" });
}

// =====================================================================
// KENDİ HESABI — oturumlar (G-SES-03/06) ve 2FA (G-AUT-01/02)
// =====================================================================

export interface OturumOzeti {
  id: string;
  ipAddress: string | null;
  userAgent: string | null;
  platform: string | null;
  deviceLabel: string | null;
  createdAt: string;
  lastSeenAt: string;
  expiresAt: string;
  /** Bu ekranı açan oturumun kendisi mi — SUNUCUDAN gelir */
  suAnki: boolean;
}

export function oturumlarim(): Promise<OturumOzeti[]> {
  return api("/auth/oturumlar");
}

/**
 * Tek oturumu sonlandırır.
 *
 * Kullanıcı kimliği gövdede DEĞİL, sunucu oturumdan okuyor: başkasının
 * oturum kimliği yazılsa da `where` eşleşmiyor ve hiçbir şey kapanmıyor.
 */
export function oturumSonlandir(sessionId: string): Promise<{ kapatildi: boolean }> {
  return api(`/auth/oturumlar/${encodeURIComponent(sessionId)}/sonlandir`, { yontem: "POST" });
}

/**
 * Kendi şifresini değiştirir — G-AUT-08a. 400 MEVCUT_SIFRE_HATALI / DOGRULAMA
 * (politika; ayrıntı `ayrinti.yeniSifre`), 429 DENEME_SINIRI. 401 DEĞİL:
 * yanlış mevcut şifre oturumu sonlandırmış gibi görünmesin.
 */
export function sifreDegistir(govde: {
  mevcutSifre: string;
  yeniSifre: string;
  digerOturumlariSonlandir: boolean;
}): Promise<{ kapatilanOturum: number }> {
  return api("/auth/sifre-degistir", { yontem: "POST", govde });
}

export function digerOturumlariSonlandir(): Promise<{ kapatilan: number }> {
  return api("/auth/oturumlar/digerlerini-sonlandir", { yontem: "POST" });
}

export type IkiAsamaPolitikasi = "REQUIRED" | "OPTIONAL" | "DISABLED";

export interface IkiAsamaDurumu {
  etkinPolitika: IkiAsamaPolitikasi;
  kuruldu: boolean;
  kilitliRolVar: boolean;
  /** G-AUT-01b — geçiş süresinden kalan gün; null = geçiş hakkı yok */
  gecisKalanGun: number | null;
  kurulumZorunlu: boolean;
  kalanYedekKod: number;
}

/**
 * KENDİ PROFİLİM — `Ben`DEN AYRI, VE BİLEREK
 *
 * `/auth/ben` her sayfa açılışında çağrılıyor ve kabuğun ihtiyacı olan
 * şeyi taşıyor: kimlik, izinler, roller. Ad-soyad-telefon oraya
 * eklenseydi her istekte, hiç kullanılmadığı ekranlara da giderdi.
 *
 * Bu uç yalnızca `Hesabım` ekranında çağrılıyor.
 */
export interface KendiProfil {
  firstName: string;
  lastName: string;
  /** DEĞİŞTİRİLEMEZ — giriş kimliği ve davet hedefi. Ekranda okunur. */
  email: string;
  phone: string | null;
  phoneIsPrimary: boolean;
  /** Rol ADLARI (kod değil) — ekranda okunacak metin. */
  roller: string[];
}

export function kendiProfilim(): Promise<KendiProfil> {
  return api("/personel/profilim");
}

export function kendiProfilimiGuncelle(veri: {
  firstName?: string;
  lastName?: string;
  phone?: string | null;
}): Promise<{ guncellendi: string[] }> {
  return api("/personel/profilim", { yontem: "PATCH", govde: veri });
}

export function ikiAsamaDurum(): Promise<IkiAsamaDurumu> {
  return api("/auth/2fa/durum");
}

export function ikiAsamaKurulumBaslat(): Promise<{
  gizliAnahtar: string;
  adres: string;
  qrVeriUrl: string;
}> {
  return api("/auth/2fa/kurulum", { yontem: "POST" });
}

/** Yedek kodlar BU YANITTA bir kez döner; veritabanında yalnızca özetleri var. */
export function ikiAsamaKurulumTamamla(kod: string): Promise<{ yedekKodlar: string[] }> {
  return api("/auth/2fa/kurulum/tamamla", { yontem: "POST", govde: { kod } });
}

/** Kapatmak da KOD istiyor — açık bir oturumu ele geçiren kodsuz kapatamasın. */
export function ikiAsamaKapat(kod: string): Promise<{ kapatildi: true }> {
  return api("/auth/2fa/kapat", { yontem: "POST", govde: { kod } });
}

export function yedekKodYenile(kod: string): Promise<{ yedekKodlar: string[] }> {
  return api("/auth/2fa/yedek-kod/yenile", { yontem: "POST", govde: { kod } });
}

// =====================================================================
// ENTEGRASYONLAR — F-TEC-01, F-TEC-02
// =====================================================================

export type EntegrasyonDurumu =
  "YAPILANDIRILMADI" | "DENENMEDI" | "CALISIYOR" | "HATALI" | "KAPALI";

export interface EntegrasyonSatiri {
  tur: string;
  ad: string;
  durum: EntegrasyonDurumu;
  ayarlar: Record<string, unknown>;
  /** Sır kayıtlı mı — DEĞERİ ASLA DÖNMEZ */
  sirVar: boolean;
  /** "Değişti mi" sorusunu cevaplar, "hangi anahtar"ı değil */
  sirIzi: string | null;
  maske: string;
  sonTestAt: string | null;
  sonHata: string | null;
  testEdilebilir: boolean;
  testYokSebebi: string | null;
}

export function entegrasyonlar(): Promise<EntegrasyonSatiri[]> {
  return api("/teknik/entegrasyonlar");
}

export function entegrasyonKaydet(veri: {
  tur: string;
  ad: string;
  ayarlar?: Record<string, unknown>;
  /** GÖNDERİLMEZSE eski sır korunur; boş metin gönderilirse silinir. */
  sir?: string;
}): Promise<{ kaydedildi: true }> {
  return api("/teknik/entegrasyon", { yontem: "POST", govde: veri });
}

export function entegrasyonAcKapa(veri: {
  tur: string;
  acik: boolean;
}): Promise<{ durum: EntegrasyonDurumu }> {
  return api("/teknik/entegrasyon/ac-kapa", { yontem: "POST", govde: veri });
}

export function entegrasyonTest(tur: string): Promise<{ basarili: boolean; mesaj: string }> {
  return api("/teknik/entegrasyon/test", { yontem: "POST", govde: { tur } });
}

// =====================================================================
// BİLDİRİM ŞABLONLARI — F-TEC-10
// =====================================================================

export interface SablonDegiskeni {
  ad: string;
  aciklama: string;
  ornek: string;
}

export interface SablonSatiri {
  id: string;
  olay: string;
  kanal: string;
  baslik: string | null;
  govde: string;
  aktif: boolean;
  kritik: boolean;
  onayDurumu: "DRAFT" | "SUBMITTED" | "APPROVED" | "REJECTED";
  saglayiciAdi: string | null;
  redNedeni: string | null;
  arsivliMi: boolean;
  onizleme: string;
}

export interface SablonOlayi {
  olay: string;
  degiskenler: SablonDegiskeni[];
  sablonlar: SablonSatiri[];
}

export function sablonListesi(): Promise<SablonOlayi[]> {
  return api("/bildirim-sablonlari");
}

export function sablonKaydet(veri: {
  olay: string;
  kanal: string;
  baslik?: string | null;
  govde: string;
  aktif?: boolean;
  kritik?: boolean;
  saglayiciAdi?: string | null;
}): Promise<SablonSatiri> {
  return api("/bildirim-sablonlari", { yontem: "POST", govde: veri });
}

export function sablonArsivle(id: string): Promise<{ arsivlendi: true }> {
  return api(`/bildirim-sablonlari/${id}/arsivle`, { yontem: "POST" });
}

export function sablonOnayaGonder(id: string): Promise<{ durum: string }> {
  return api(`/bildirim-sablonlari/${id}/onaya-gonder`, { yontem: "POST" });
}

/** Hedef parametresi YOK — sunucu her zaman kendi hesabınıza gönderir. */
export function sablonTestGonder(id: string): Promise<{ gonderildi: true }> {
  return api(`/bildirim-sablonlari/${id}/test`, { yontem: "POST" });
}

// =====================================================================
// KULLANICI GÖRÜNÜMÜ — F-AUTH-07
// =====================================================================

export interface GorunumDurumu {
  aktif: boolean;
  gorunumId?: string;
  hedefId?: string;
  hedefAd?: string;
  /** `null` = SÜRESİZ görünüm (F-AUTH-07). `undefined` = görünüm kapalı. */
  kalanSaniye?: number | null;
  /**
   * Hedefin rolü — F-AUTH-07.
   *
   * Şerit daha önce yalnızca **adı** yazıyordu ve ad tek başına
   * "hangi gözle bakıyorum" sorusunu cevaplamıyor: bir yöneticinin
   * ekranıyla bir uzmanın ekranı çok farklı şey gösteriyor, ikisi de
   * aynı biçimde bir insan adıyla anılıyordu.
   *
   * Kişi birden fazla rol taşıyabilir; sunucu hepsini alfabetik
   * sırayla tek metinde birleştiriyor (şemada "birincil rol" diye
   * bir alan YOK — birini seçmek uydurma olurdu).
   *
   * `null` = hedefin aktif rolü yok → şerit rol satırını **hiç
   * çizmez**. Yer tutucu bir ad üretilmez.
   */
  hedefRol?: string | null;
  gerekce?: string;
}

export function gorunumeGec(veri: { hedefId: string; gerekce: string }): Promise<{
  gorunumId: string;
  hedefAd: string;
  /** `null` = hedefin aktif rolü yok. Bkz. `GorunumDurumu.hedefRol`. */
  hedefRol: string | null;
  kalanSaniye: number;
}> {
  return api("/auth/gorunum", { yontem: "POST", govde: veri });
}

export function gorunumBitir(): Promise<void> {
  return api("/auth/gorunum/bitir", { yontem: "POST" });
}

export function gorunumDurumu(): Promise<GorunumDurumu> {
  return api("/auth/gorunum/durum");
}

// =====================================================================
// GERİ YÜKLEME SİHİRBAZI — F-YED-23…29
// =====================================================================

export type GeriYuklemeKapsami =
  "FULL_SYSTEM" | "DATABASE_ONLY" | "SINGLE_TABLE" | "SINGLE_RECORD" | "POINT_IN_TIME";

export type HedefOrtam = "SANDBOX" | "PRODUCTION";

export interface GeriYuklemeTaslagi {
  id: string;
  kapsam: GeriYuklemeKapsami;
  hedef: HedefOrtam;
  durum: string;
  etki: {
    tabloSayisi: number | null;
    satirSayisi: number | null;
    /** Kaybolacak SATIR sayısı DEĞİL — işlem yoğunluğunun göstergesi */
    sinirdanSonrakiOlaySayisi: number | null;
    veriKaybiBaslangici: string | null;
    kirilacakBaglantilar: string[];
  };
  etkiCumlesi: string;
  uretimeYaziyor: boolean;
  oncekiYedekGerekli: boolean;
  /** Onay adımında YAZILMASI gereken metin — ekran kendi uydurmuyor */
  onayMetni: string;
  /** Varsayılan hedef SUNUCUDAN gelir (F-YED-24) */
  varsayilanHedef: HedefOrtam;
}

export function geriYuklemeTaslagi(veri: {
  kapsam: GeriYuklemeKapsami;
  hedef: HedefOrtam;
  runId?: string | null;
  pointInTime?: string | null;
  tableName?: string | null;
  recordId?: string | null;
}): Promise<GeriYuklemeTaslagi> {
  return api("/yedek/geri-yukleme/taslak", { yontem: "POST", govde: veri });
}

export function geriYuklemeCalistir(
  id: string,
  onay: string,
): Promise<{ id: string; basarili: boolean; sureSn: number; hata: string | null }> {
  return api(`/yedek/geri-yukleme/${id}/calistir`, { yontem: "POST", govde: { onay } });
}

export interface GeriYuklemeGecmisi {
  id: string;
  scope: GeriYuklemeKapsami;
  targetEnv: HedefOrtam;
  status: string;
  startedAt: string | null;
  finishedAt: string | null;
  durationSec: number | null;
  error: string | null;
  createdAt: string;
  requester: { id: string; firstName: string; lastName: string };
}

export function geriYuklemeGecmisi(): Promise<GeriYuklemeGecmisi[]> {
  return api("/yedek/geri-yukleme/gecmis");
}

// =====================================================================
// KURTARMA YORDAMI — F-YED-33/34
// =====================================================================

export type YordamDurumu = "HAZIR" | "EKSIK" | "KULLANILAMAZ";

export interface KurtarmaYordami {
  durum: YordamDurumu;
  eksikler: { kod: string; aciklama: string }[];
  /** Sistemin BİLEMEYECEĞİ şeyler */
  elle: {
    alanAdiNotu: string;
    sunucuNotu: string;
    zarfNotu: string;
    iletisimNotu: string;
    tatbikatNotu: string;
    gozdenGecirildiAt: string | null;
    gozdenGecirmeAraligiGun: number;
  };
  /** Sistemin BİLDİĞİ her şey — her açılışta taze, düzenlenemez */
  uretilen: {
    hedefler: {
      ad: string;
      tur: string;
      aktif: boolean;
      siteDisi: boolean;
      degismez: boolean;
      sonYazma: string | null;
      konum: string | null;
    }[];
    anahtarlar: { etiket: string; parmakIzi: string; zarftaMi: boolean; aktif: boolean }[];
    sonTatbikat: { tarih: string; basarili: boolean } | null;
    olculenRtoSn: number | null;
    sonBasariliYedek: string | null;
    gecelikIsCron: string | null;
  };
}

export function kurtarmaYordami(): Promise<KurtarmaYordami> {
  return api("/yedek/kurtarma-yordami");
}

export function kurtarmaYordamiKaydet(veri: {
  alanAdiNotu: string;
  sunucuNotu: string;
  zarfNotu: string;
  iletisimNotu: string;
  tatbikatNotu: string;
}): Promise<KurtarmaYordami> {
  return api("/yedek/kurtarma-yordami", { yontem: "POST", govde: veri });
}

/** Ana ekran uyarı bandı — F-YED-22 */
export interface YedekUyariBandi {
  goster: boolean;
  sebep: "HIC_TATBIKAT_YOK" | "TATBIKAT_COK_ESKI" | "SON_TATBIKAT_BASARISIZ" | null;
  mesaj: string | null;
  yasGun: number | null;
}

export function yedekUyariBandi(): Promise<YedekUyariBandi> {
  return api("/yedek/uyari-bandi");
}

// =====================================================================
// ÖĞRENCİLER — F-PPL
// =====================================================================

export type OgrenciDurumu = "ONBOARDING" | "ACTIVE" | "PAUSED" | "LEFT" | "ARCHIVED" | "GRADUATED";

export interface OgrenciSatiri extends OgrenciDavetDurumu {
  id: string;
  studentNo: number;
  status: string;
  ad: string;
  soyad: string;
  sinif: string;
  /**
   * T.C. kimlik numarasının MASKELİ hâli (`•••••••••46`) ya da `null`.
   * Ham değer sunucudan HİÇ gelmiyor — özel nitelikli kişisel veri.
   */
  nationalIdMaskeli: string | null;
  /**
   * Telefonlar (sürüm 9a, Emre kararı 07.10.2026): yalnız `kisi.veli.goruntule` kapsamı olan
   * personelin yanıtında VAR; yetkisizde anahtarlar hiç gelmez (`undefined`, `null` değil).
   * `null` = yetkili ama numara kayıtlı değil. Dışa aktarma yok.
   */
  telefon?: string | null;
  /** Birincil velinin telefonu — aynı görünürlük kuralı. */
  veliTelefon?: string | null;
  /**
   * Atanmış uzmanı ŞU AN izinde — F-PPL-11b.
   *
   * İşaret sunucuda izin penceresinden türetiliyor; izin bitince
   * kendiliğinden kayboluyor.
   */
  uzmanIzinde: boolean;
  /** 18 yaş altı ve veli muvafakati HENÜZ alınmadı (F-PPL-05b) */
  muvafakatBekliyor: boolean;
  /** SUNUCUDAN — ekran geçiş tablosu tutmuyor */
  izinliGecisler: string[];
  gerekceIsteyenler: string[];
}

/**
 * Liste künyesi — G-UYG-11.
 *
 * Burada YENİDEN YAZILMIYOR, `@koclukpanel/contracts`ten geliyor.
 * Sunucu da aynı yerden okuyor.
 *
 * Elle yazılmış bir ayna olarak durduğu gün ayrıştı: sunucuya
 * `toplam: number | null` eklendi, buradaki `number` kaldı ve
 * `k.toplam.toLocaleString()` çalışma anında patlayacak hâle geldi —
 * derleyici hiçbir şey söylemedi, çünkü iki tip birbirini tanımıyordu.
 */
export {
  listeKesildiMi as listeKesildi,
  listeKesintiMetni,
  type ListeKunyesi,
} from "@koclukpanel/contracts";

// Bu dosyanin KENDI imza satirlari da kunyeyi kullaniyor; yeniden disa
// aktarim adi yerel kapsama getirmiyor, o yuzden ayrica ice aliniyor.
import type { ListeKunyesi, RevizyonGerekcesiSozlesme } from "@koclukpanel/contracts";

export function ogrenciListesi(): Promise<ListeKunyesi & { ogrenciler: OgrenciSatiri[] }> {
  return api("/ogrenciler");
}

/**
 * 360° ekranının hedef bloğu — F-PPL-05.
 *
 * ================================================================
 * BU UÇ NEDEN BU KADAR AZ ŞEY DÖNDÜRÜYOR
 * ================================================================
 * Genel Bakış dokuz şey gösteriyor ama bunların yedisinin ZATEN ucu
 * var (atamalar · görüşmeler · deneme trendi · haftalık analiz ·
 * onboarding). Ekran onları kendi uçlarından çekiyor: her biri kendi
 * iznini ve kendi kayıt bazlı kontrolünü taşıyor, yani izinsiz bir
 * bölüm İSTEK BİLE ATMIYOR.
 *
 * Hepsini tek uçta toplamak tek istek olurdu ama o kontrollerin
 * ikinci bir evini açardı. Gerekçe `kisiler.service.ts` içinde yazılı.
 */
export interface OgrenciKunyesi {
  /** F-PPL-05c — `null` = alan seçilmemiş. */
  alan: { id: string; ad: string } | null;
  hedef: {
    sinav: string | null;
    puan: number | null;
    siralama: number | null;
    okul: string | null;
    sinavTarihi: string | null;
    /** `null` = sınav tarihi girilmemiş. SIFIR DEĞİL — sıfır "bugün sınav" demek. */
    kalanGun: number | null;
  };
  /**
   * Öğrencinin `User.id`si — İşlem Geçmişi sekmesi bunu istiyor.
   *
   * ANAHTAR YOKSA yetki de yok: sunucu bu alanı yalnızca
   * `denetim.etkinlik.kisi` iznini `ALL` kapsamıyla taşıyan kişiye
   * koyuyor (`kunye.ts` -> `dokumAcilabilirMi`). `null` gelmiyor,
   * anahtar hiç oluşmuyor — "veri yok" ile "yetkin yok" karışmasın.
   */
  kullaniciId?: string;
}

export function ogrenciKunyesi(ogrenciId: string): Promise<OgrenciKunyesi> {
  return api<OgrenciKunyesi>(`/ogrenciler/${encodeURIComponent(ogrenciId)}/kunye`);
}

// =====================================================================
// ÖĞRENCİ NOTLARI — F-PPL-04
// =====================================================================

/**
 * Öğrenci notu.
 *
 * ================================================================
 * `govde` İSTEĞE BAĞLI — VE YOKLUĞU BİR BİLGİ
 * ================================================================
 * Sunucu korumalı notun gövdesini `null` DÖNDÜRMÜYOR, anahtarı hiç
 * oluşturmuyor. Böylece ekran "not boş" ile "notu göremezsin"i
 * ayırabiliyor: `korumali` alanı sebebini söylüyor.
 *
 * Korumalı gövde yalnızca `korumaliNotOku` ucundan çıkıyor ve o uç
 * şifre (sudo) + yazılı gerekçe istiyor.
 */
export interface OgrenciNotu {
  id: string;
  baslik: string | null;
  govde?: string;
  gorunurluk: string;
  korumali: boolean;
  sabit: boolean;
  yazan: string;
  yazanBenMiyim: boolean;
  olusturma: string;
}

export function ogrenciNotlari(ogrenciId: string): Promise<OgrenciNotu[]> {
  return api<OgrenciNotu[]>(`/ogrenciler/${encodeURIComponent(ogrenciId)}/notlar`);
}

export function ogrenciNotuEkle(
  ogrenciId: string,
  govde: { baslik?: string | null; govde: string; gorunurluk: string; sabit?: boolean },
): Promise<{ id: string }> {
  return api(`/ogrenciler/${encodeURIComponent(ogrenciId)}/notlar`, {
    yontem: "POST",
    govde,
  });
}

/**
 * Korumalı notun gövdesi — gerekçeli erişim (F-SES-05).
 *
 * Gerekçe GÖVDEDE gidiyor, sorgu dizesinde değil: denetim kaydına
 * yazılan o metin adres çubuğuna konsaydı ayrıca erişim loglarına,
 * vekil loglarına, tarayıcı geçmişine ve `Referer` başlığına düşerdi.
 */
export function ogrenciNotuKorumaliOku(
  ogrenciId: string,
  notId: string,
  gerekce: string,
): Promise<{ govde: string; yazan: string }> {
  return api(
    `/ogrenciler/${encodeURIComponent(ogrenciId)}/notlar/${encodeURIComponent(notId)}/oku`,
    { yontem: "POST", govde: { gerekce } },
  );
}

export function ogrenciDurumDegistir(
  id: string,
  veri: { durum: string; gerekce?: string },
): Promise<{ eski: string; yeni: string }> {
  return api(`/ogrenciler/${id}/durum`, { yontem: "POST", govde: veri });
}

/**
 * Veli muvafakatı — F-PPL-05b. Muvafakat BELİRLİ BİR VELİNİN verdiği bir beyandır: `bagId` öğrencinin
 * `StudentParent` bağ kaydının kimliği (`veliListesi` yanıtındaki `VeliOzeti.id`). Sunucu hata kodları:
 * `BAGLI_VELI_YOK` (409), `VELI_BAGI_GECERSIZ` (404/422), `ZATEN_KAYITLI` (409).
 */
export function ogrenciMuvafakat(
  id: string,
  govde: { bagId: string; gerekce: string },
): Promise<{ aktiflesti: boolean }> {
  return api(`/ogrenciler/${id}/muvafakat`, { yontem: "POST", govde });
}

export interface OgrenciKayitSonucu {
  id: string;
  userId: string;
  studentNo: number;
  status: string;
  /** F-PPL-05b — 18 yaş altı ve muvafakat yoksa `true` */
  muvafakatBekliyor: boolean;
  /** F-PPL-01b — null: gönderim istenmedi; false: gönderim başarısız. */
  davetGonderildi: boolean | null;
}

/**
 * Öğrenci kaydı açar — F-PPL-01.
 *
 * `muvafakatBekliyor` SUNUCUDAN geliyor, formun kendi yaş hesabından
 * değil. Ekran kendi hesabına güvenseydi, saat dilimi ya da sınır
 * kuralı iki tarafta ayrıştığı gün kullanıcıya yanlış şeyi söylerdi.
 * Formdaki hesap yalnızca ÖNCEDEN uyarmak için.
 */
export function ogrenciOlustur(govde: {
  adayId?: string | null;
  email: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  birthDate: string;
  gradeLevel: string;
  /** F-PPL-05c — öğrencinin alanı; isteğe bağlı. */
  fieldId: string | null;
  davetGonder: boolean;
  /** G-AUT-00 — T.C. ile giriş. İsteğe bağlı. */
  nationalId: string | null;
  /** C39 — ortak numarada bu kişi mi telefonuyla giriyor. */
  phoneIsPrimary: boolean;
}): Promise<OgrenciKayitSonucu> {
  return api("/ogrenciler", { yontem: "POST", govde });
}

// =====================================================================
// YARDIM — F-HLP-09…13
// =====================================================================

export interface YardimYazisi {
  id: string;
  slug: string;
  baslik: string;
  kategori: string;
  govde: string;
  ekranAnahtari: string | null;
  sira: number;
}

export interface YardimBolumu {
  kategoriler: { ad: string; yazilar: YardimYazisi[] }[];
  toplam: number;
  /** Bağlama duyarlı yardım (F-HLP-12) */
  ekranYazilari: YardimYazisi[];
}

export function yardimBolumu(ekran?: string): Promise<YardimBolumu> {
  return api("/yardim/icerik", { sorgu: { ekran } });
}

export function yardimAra(q: string): Promise<YardimYazisi[]> {
  return api("/yardim/icerik/ara", { sorgu: { q } });
}

// =====================================================================
// KARŞILAMA TURU — F-HLP-01…08
// =====================================================================

export interface TurAdimi {
  anahtar: string;
  baslik: string;
  metin: string;
}

export interface TurDurumu {
  adimlar: TurAdimi[];
  /**
   * KARSILAMA = ilk giriş · DEVAM = yarıda bırakılmış (F-HLP-05)
   * YENI_BOLUM = tur bitirilmiş, sonradan menü eklenmiş (F-HLP-07)
   */
  tur: "KARSILAMA" | "DEVAM" | "YENI_BOLUM";
  turId: string | null;
}

export function turDurumu(): Promise<TurDurumu> {
  return api("/yardim/tur");
}

export function turAdimGoruldu(anahtar: string): Promise<{ gorulen: string[] }> {
  return api("/yardim/tur/adim", { yontem: "POST", govde: { anahtar } });
}

export function turAtla(): Promise<{ gorulen: string[] }> {
  return api("/yardim/tur/atla", { yontem: "POST" });
}

export function turYenidenBaslat(): Promise<TurDurumu> {
  return api("/yardim/tur/yeniden", { yontem: "POST" });
}

// =====================================================================
// TEKNİK YÖNETİM KONSOLU — F-TEC-03/04/08
// =====================================================================

export type KuyrukSagligi = "IYI" | "DIKKAT" | "KRITIK" | "DURMUS";

export interface TeknikKonsolu {
  /** Sunucuda `DEMO_VERI` bayrağı. `false` iken demo veri uçları yok: panel sormaz. Alan yoksa (eski sunucu) sorulur. */
  demoVeriAcik?: boolean;
  kuyruklar: {
    ad: string;
    bekleyen: number;
    isleyen: number;
    basarisiz: number;
    gecikmeli: number;
    saglik: KuyrukSagligi;
    sebep: string;
  }[];
  isler: {
    ad: string;
    desen: string;
    gereksinim: string;
    /**
     * Son çalışmanın sonucu — DÖRT hâl.
     *
     * `BILINMIYOR` ile `HIC_CALISMADI` ayrı: BullMQ tamamlanan işleri
     * sınırlı sayıda tutuyor, aylık bir işin kaydı düşmüş olabilir.
     * "Kaydı bulamadım"ı "hiç çalışmadı" saymak, düzenli çalışan bir
     * işi çalışmamış göstermek olurdu.
     */
    sonSonuc: "CALISTI" | "BASARISIZ" | "HIC_CALISMADI" | "BILINMIYOR";
    sonCalismaAt: string | null;
    /** `BILINMIYOR` iken taranan kayıtların en eskisi. */
    kayitPenceresiBaslangici: string | null;
    sonrakiCalismaAt: string | null;
    gecikmis: boolean;
  }[];
  /** Planlanmış ama ZAMANLANMAMIŞ işler — kuyruğa düşmüyorlar */
  yazilmamisIsler: { name: string; requirement: string }[];
}

export function teknikKonsolu(): Promise<TeknikKonsolu> {
  return api("/teknik/konsol");
}

export function kuyrukYenidenDene(kuyruk: string): Promise<{ denenen: number; kalan: number }> {
  return api("/teknik/kuyruk/yeniden-dene", { yontem: "POST", govde: { kuyruk } });
}

export function isTetikle(is: string): Promise<{ tetiklendi: boolean }> {
  return api("/teknik/is/tetikle", { yontem: "POST", govde: { is } });
}

// --- Bakım modu (F-TEC-08) ---

export interface BakimDurumu {
  acik: boolean;
  baslik: string | null;
  mesaj: string | null;
  bitisTahmini: string | null;
}

export function bakimDurumu(): Promise<BakimDurumu> {
  return api("/bakim/durum");
}

export function bakimAc(veri: {
  baslik: string;
  mesaj: string;
  bitis?: string | null;
}): Promise<BakimDurumu> {
  return api("/bakim/ac", { yontem: "POST", govde: veri });
}

export function bakimKapat(): Promise<BakimDurumu> {
  return api("/bakim/kapat", { yontem: "POST" });
}

// =====================================================================
// UYARI KURALLARI VE KAYITLI GÖRÜNÜMLER — F-LOG-20/21/32
// =====================================================================

export interface UyariKurali {
  id: string;
  ad: string;
  esik: number;
  pencereDk: number;
  severity: string;
  aktif: boolean;
  kilitli: boolean;
  sogumaDk: number;
  sonTetiklenmeAt: string | null;
  tetiklenmeSayisi: number;
  /** `false` ise kural "aktif" görünse bile HİÇ ÇALIŞMIYOR */
  degerlendiriliyor: boolean;
}

export interface LogGorunumu {
  id: string;
  ad: string;
  aciklama: string | null;
  sistemMi: boolean;
  paylasilan: boolean;
  filtreler: unknown;
}

export function uyariKurallari(): Promise<UyariKurali[]> {
  return api("/denetim/uyari/kurallar");
}

/**
 * Uyari kuralini gunceller — F-LOG-33.
 *
 * KILITLI kuralda yalnizca SIKILASTIRMA serbest: esik dusurulebilir,
 * pencere genisletilebilir, soguma kisaltilabilir. Gevsetme 409
 * doner — "kapatilamaz" yalnizca `aktif` bayragini korusaydi, esigi
 * 100000'e cikarmak kurali susturmanin baska adi olurdu.
 */
export function uyariKuraliGuncelle(
  id: string,
  degisiklik: { esik?: number; pencereDk?: number; sogumaDk?: number; aktif?: boolean },
): Promise<{ id: string; esik: number; pencereDk: number; sogumaDk: number; aktif: boolean }> {
  return api(`/denetim/uyari/kurallar/${encodeURIComponent(id)}`, {
    yontem: "POST",
    govde: degisiklik,
  });
}

export function logGorunumleri(): Promise<LogGorunumu[]> {
  return api("/denetim/uyari/gorunumler");
}

/**
 * TOPLU ETKINLIK OZETI — F-LOG-11, G-IZL-03a
 *
 * Demet ya yayimlanmis ya da gizli; ikisi ayri bicimde geliyor
 * cunku gizli demetin SAYISI yok. Tek bicimde `islemSayisi: 0`
 * gonderilseydi ekran "sifir islem" ile "gosterilmedi"yi ayirt
 * edemezdi.
 */
export type OzetSatiri =
  | { anahtar: string; yayimlandi: true; islemSayisi: number; kisiSayisi: number }
  | { anahtar: string; yayimlandi: false; sebep: "AZ_KISI" };

export interface EtkinlikOzeti {
  baslangic: string;
  bitis: string;
  kendiRaporu: boolean;
  kapsam: string;
  enAzKisi: number;
  moduller: OzetSatiri[];
  kategoriler: OzetSatiri[];
  saatler: OzetSatiri[];
  toplamIslem: number;
}

export interface KisiDokumu {
  kullaniciId: string;
  baslangic: string;
  bitis: string;
  moduller: { anahtar: string; islemSayisi: number }[];
  gunler: { gun: string; islemSayisi: number }[];
  toplamIslem: number;
}

export function etkinlikOzeti(gun: number): Promise<EtkinlikOzeti> {
  return api(`/denetim/etkinlik/toplu?gun=${gun}`);
}

export function etkinlikDokumu(
  kullanici: string,
  gun: number,
  gerekce: string,
): Promise<KisiDokumu> {
  /*
    GEREKÇE GÖVDEDE, SORGU DİZESİNDE DEĞİL.

    Önce `?gerekce=…` olarak gidiyordu. Gerekçe serbest metin ve bu
    ekranda hassas bir cümle oluyor; sorgu dizesi ise erişim
    loglarına, vekil loglarına ve **tarayıcı geçmişine** kopyalanır.
    Denetim kaydı korunuyor, o kopyalar korunmuyor.

    İmza DEĞİŞMEDİ — yalnızca taşıma değişti, çağıran ekranlar
    dokunulmadan çalışıyor.
  */
  return api("/denetim/etkinlik/kisi", {
    yontem: "POST",
    govde: { kullanici, gun, gerekce },
  });
}

/**
 * SURUM VE DAGITIM — F-TEC-14
 *
 * Goc uyumsuzlugu IKI YONLU ve ikisi ayri tehlike; bu yuzden tek bir
 * "uyumsuz" sayisi degil, durum basina ayri satir geliyor.
 */
export type GocDurumu = "UYGULANDI" | "BEKLIYOR" | "DOSYASI_YOK" | "YARIM" | "GERI_ALINDI";

export interface SurumBilgisi {
  uygulamaSurumu: string;
  /** `null` = “dağıtım yapılmadı” DEĞİL, “ölçülmedi” */
  sonDagitimAt: string | null;
  dagitimKaydiVarMi: boolean;
  sema: {
    satirlar: { ad: string; durum: GocDurumu }[];
    semaSurumu: string | null;
    bekleyen: number;
    dosyasiYok: number;
    yarim: number;
  };
}

export function surumBilgisi(): Promise<SurumBilgisi> {
  return api("/teknik/surum");
}

/**
 * TEK KAYIT KURTARMA — F-YED-27
 *
 * `parmakIzi` onizlemeden geliyor ve yazma istegiyle GERI gonderiliyor:
 * onay, kullanicinin GORDUGU farka ait olmali. Arada kayit degistiyse
 * sunucu 409 doner ve hicbir sey yazmaz.
 */
export type KurtarmaKarari =
  | { tur: "GERI_KONACAK"; alanSayisi: number }
  | {
      tur: "DEGISECEK";
      farklar: { alan: string; yedekteki: unknown; uretimdeki: unknown }[];
      uretimDahaYeni: boolean;
    }
  | { tur: "AYNI" }
  | { tur: "YEDEKTE_YOK" };

export interface TekKayitOnizlemesi {
  tablo: string;
  kayitId: string;
  runId: string;
  karar: KurtarmaKarari;
  cumle: string;
  yazilabilir: boolean;
  parmakIzi: string;
}

export interface TekKayitYazmaSonucu {
  tablo: string;
  kayitId: string;
  yazilanAlan: number;
  islem: "EKLENDI" | "GUNCELLENDI";
}

export function tekKayitOnizle(veri: {
  runId: string;
  tablo: string;
  kayitId: string;
}): Promise<TekKayitOnizlemesi> {
  return api("/yedek/geri-yukleme/tek-kayit/onizle", { yontem: "POST", govde: veri });
}

export function tekKayitUygula(veri: {
  runId: string;
  tablo: string;
  kayitId: string;
  parmakIzi: string;
}): Promise<TekKayitYazmaSonucu> {
  return api("/yedek/geri-yukleme/tek-kayit/uygula", { yontem: "POST", govde: veri });
}

// =====================================================================
// KONU İLERLEMESİ — F-TPC
// =====================================================================

export type KonuDurumu =
  | "NOT_STARTED"
  | "PLANNED"
  | "IN_PROGRESS"
  | "FIRST_PASS_DONE"
  | "PRACTICING"
  | "NEEDS_REINFORCEMENT"
  | "NEEDS_REVIEW"
  | "VERIFIED_BY_EXAM"
  | "MASTERED";

export interface KonuIlerleme {
  topicId: string;
  ad: string;
  ders: string;
  /**
   * Müfredattan çıkarılmış konu — sunucu ZATEN döndürüyor (`konu.service.ts`);
   * tipte yoktu. Dağılım grafiği bu konuları saymıyor (dalga 3).
   */
  arsivli?: boolean;
  /** Hata defteri kaydı `lessonId` istiyor */
  lessonId: string;
  stage: KonuDurumu;
  firstStudiedAt: string | null;
  lastStudiedAt: string | null;
  totalQuestions: number;
  totalCorrect: number;
  dogrulukOrani: number | null;
  reviewCount: number;
  lastExamAccuracy: number | null;
  /**
   * Son denemede bu konudan CEVAPLANAN soru sayısı — F-TPC-02.
   *
   *   `null`  hiç deneme sorusu gelmemiş
   *   `0`–`2` geldi ama hüküm için yetmedi (eşik `KONU_HUKMU_ICIN_EN_AZ_SORU`)
   *   `3`+    hüküm verildi, `lastExamAccuracy` da dolu
   *
   * Ekran bu alan olmadan üçüne de "—" koyuyordu ve uzman
   * "ölçülmedi" ile "yetmedi"yi ayıramıyordu. Eşiği elle yazma —
   * `@koclukpanel/contracts` içinde.
   */
  lastExamQuestions: number | null;
  coachRating: number | null;
  selfRating: number | null;
  markedAt: string | null;
  /** İşaretleyen kişi; yalnız işaret yetkisi olana sunucu doldurur, aksi hâlde `null` */
  markedByAd?: string | null;
  /**
   * Bu durumdan gidilebilecek durumlar — SUNUCUDAN gelir.
   * "Denemede doğrulandı" bu listede ASLA olmaz: hesaplanan bir
   * durum, elle işaretlenemiyor (F-TPC-02).
   */
  izinliGecisler: KonuDurumu[];
}

/**
 * ÖĞRENCİNİN KENDİ KONU LİSTESİ — F-ACA-05
 *
 * Merkezî müfredata **bağlı değil, kopyası**. Sürüm sonradan değişse
 * bile öğrencinin listesi aynen kalır; `surum` yalnızca NEREDEN
 * geldiğini söylüyor, canlı bir bağ değil.
 */
export interface OgrenciMufredatSatiri {
  id: string;
  topicId: string;
  ad: string;
  ders: string;
  sira: number;
  /** Merkezî müfredat dışı, elle eklenmiş. Yeni sürüm kopyalanınca kaybolmaz. */
  ozelMi: boolean;
  surum: string | null;
}

export function ogrenciMufredati(ogrenciId: string): Promise<OgrenciMufredatSatiri[]> {
  return api(`/ogrenciler/${ogrenciId}/mufredat`);
}

/**
 * Yayınlanmış sürümü öğrenciye KOPYALAR.
 *
 * Öğrencide zaten olan konular atlanır, özel eklenenler silinmez —
 * o yüzden yeni sürüm gelince "güncelle" güvenle çalıştırılabilir
 * (F-ACA-05: önerilir, zorlanmaz).
 */
export function mufredatKopyala(
  ogrenciId: string,
  surumId: string,
): Promise<{ kopyalanan: number; atlanan: number }> {
  return api(`/ogrenciler/${ogrenciId}/mufredat/kopyala`, {
    yontem: "POST",
    govde: { surumId },
  });
}

export function ozelKonuEkle(ogrenciId: string, konuId: string): Promise<{ id: string }> {
  return api(`/ogrenciler/${ogrenciId}/mufredat/ozel-konu`, {
    yontem: "POST",
    govde: { konuId },
  });
}

export function konuIlerlemesi(ogrenciId: string): Promise<KonuIlerleme[]> {
  return api(`/ogrenciler/${encodeURIComponent(ogrenciId)}/konular`);
}

export interface DersIlerlemesi {
  ders: string;
  toplam: number;
  biten: number;
  yuzde: number;
}

export function dersIlerlemesi(ogrenciId: string): Promise<DersIlerlemesi[]> {
  return api(`/ogrenciler/${encodeURIComponent(ogrenciId)}/konular/ilerleme`);
}

/**
 * NİHAİ DURUMU SUNUCU BELİRLER, istenen değil (F-TPC-02): deneme
 * başarısı eşiğin altındaysa "tamamlandı" işareti otomatik "tekrar
 * gerekiyor"a döner. Bu yüzden yanıttaki `durum` alanı gönderilenle
 * aynı OLMAYABİLİR ve ekran onu göstermek zorunda.
 */
export function konuDurumDegistir(
  ogrenciId: string,
  konuId: string,
  durum: KonuDurumu,
): Promise<{ durum: KonuDurumu; otomatikDusuruldu: boolean }> {
  return api(
    `/ogrenciler/${encodeURIComponent(ogrenciId)}/konular/${encodeURIComponent(konuId)}/durum`,
    { yontem: "POST", govde: { durum } },
  );
}

export function konuTopluDurum(
  ogrenciId: string,
  konuIdler: string[],
  durum: KonuDurumu,
): Promise<{ uygulanan: number; atlanan: number; otomatikDusen: number }> {
  return api(`/ogrenciler/${encodeURIComponent(ogrenciId)}/konular/toplu-durum`, {
    yontem: "POST",
    govde: { konuIdler, durum },
  });
}

// =====================================================================
// BİLDİRİM TERCİHLERİ — F-NTF-03
// =====================================================================

/**
 * Olay kataloğu DÜZ DİZGİ DİZİSİ döner (`["OnayKarariVerildi", …]`).
 * Ekran adları kendi sözlüğünden çeviriyor: sunucu tarafı katalog bir
 * KOD listesi, kullanıcıya gösterilecek metin değil.
 */
export function bildirimOlaylari(): Promise<string[]> {
  return api("/bildirimler/olaylar");
}

export type BildirimKanali = "IN_APP" | "EMAIL" | "SMS" | "WHATSAPP" | "PUSH";

export interface BildirimTercihi {
  eventCode: string;
  channel: BildirimKanali;
  enabled: boolean;
}

/**
 * YALNIZCA KAYITLI tercihler döner. Listede olmayan olay "tercih
 * belirtilmemiş" demek ve varsayılan AÇIK sayılır — ekran ikisini
 * ayırt etmek zorunda değil ama karıştırmamalı.
 */
export function bildirimTercihlerim(): Promise<BildirimTercihi[]> {
  return api("/bildirimler/tercihler");
}

/**
 * Tercih kaydı YOKSA varsayılan AÇIK. Kapalı olsaydı sistem sessiz
 * açılır ve kimse bildirim aldığını fark etmezdi (F-NTF-03).
 */
export function bildirimTercihi(olay: string, kanal: string, acik: boolean): Promise<unknown> {
  return api("/bildirimler/tercih", { yontem: "POST", govde: { olay, kanal, acik } });
}

export function bildirimHepsiOkundu(): Promise<{ adet: number }> {
  return api("/bildirimler/hepsi-okundu", { yontem: "POST" });
}

// =====================================================================
// AKADEMİK EŞİKLER — F-TPC-02 / F-EXM-11
// =====================================================================

export interface Esikler {
  denemeBasariEsigi: number;
  konuHukmuIcinEnAzSoru: number;
}

export interface EsikDurumu {
  esikler: Esikler;
  /** İzinli aralık — ekran bunu SUNUCUDAN alıyor, kendi sınırını yazmıyor */
  sinirlar: {
    denemeBasariEsigi: { enAz: number; enFazla: number };
    konuHukmuIcinEnAzSoru: { enAz: number; enFazla: number };
  };
  varsayilan: Esikler;
}

export function esikDurumu(): Promise<EsikDurumu> {
  return api("/akademik-esikler");
}

/**
 * Sınır dışı değer REDDEDİLİR, kırpılmaz: kullanıcı %20 yazıp %40
 * uygulandığını bilmezse sistemin söylediği yanlış olur.
 */
export function esikGuncelle(veri: {
  denemeBasariEsigi?: number;
  konuHukmuIcinEnAzSoru?: number;
}): Promise<{
  esikler: Esikler;
  etki: {
    /** Yeni eşikle FARKLI değerlendirilecek konu sayısı */
    etkilenen: number;
    ornekler: Array<{ topicId: string; mevcut: string; yeniOlurdu: string }>;
    /** Soru sayısı eşiğinin geçmişe etkisi ÖLÇÜLEMİYOR — sunucu bunu söylüyor */
    soruEsigiOlculemedi?: boolean;
  };
}> {
  return api("/akademik-esikler", { yontem: "POST", govde: veri });
}

// =====================================================================
// PERSONELE GÖREV ATAMA — matris §5.8
// =====================================================================

export function gorevAta(veri: {
  atananId: string;
  baslik: string;
  aciklama?: string | null;
  sonTarih?: string | null;
  oncelik?: number;
}): Promise<{ id: string }> {
  return api("/operasyon/gorevler", { yontem: "POST", govde: veri });
}

// =====================================================================
// TALEP KATEGORİSİ TANIMLAMA
// =====================================================================

/*
  `TalepKategorisi` tipi ve `talepKategorileri()` YUKARIDA zaten var
  (öğrenci talebi bölümü). İkinci kez yazmıştım; TypeScript yakaladı.
  Aynı kaydın iki farklı tipi olsaydı, biri güncellenip öteki
  güncellenmediğinde ekranlar sessizce ayrışırdı.
*/

/**
 * `kararIzni` bu kategorideki talebi karara bağlamak için gereken İZİN
 * KODU. Kural role değil KAYDA bağlı; kod katalog karşısında
 * doğrulanıyor ve tanınmayan kod kategoriyi açmıyor.
 */
export function kategoriTanimla(veri: {
  kod: string;
  ad: string;
  aciklama?: string | null;
  kararIzni: string;
  acabilen?: "STUDENT" | "PARENT" | "BOTH";
  sira?: number;
}): Promise<TalepKategorisi> {
  return api("/operasyon/talep-kategorileri", { yontem: "POST", govde: veri });
}

// =====================================================================
// REKLAM HARCAMASI — F-CRM-07
// =====================================================================

/** Tutar DİZGİ olarak gidiyor — kuruş kaybı olmasın (para `Decimal`). */
export function harcamaGir(veri: {
  kaynak: string;
  kampanya?: string | null;
  baslangic: string;
  bitis: string;
  tutar: string;
  not?: string | null;
}): Promise<{ id: string }> {
  return api("/crm/harcamalar", { yontem: "POST", govde: veri });
}

// =====================================================================
// MEŞGULİYET KAYDI — matris §5.11
// =====================================================================

/** Program üretimi bu saatleri DOLU sayıyor. */
export function mesguliyetEkle(veri: {
  ogrenciId: string;
  baslik: string;
  baslangic: string;
  bitis: string;
  tekrarKurali?: string | null;
  /** F-PPL-07b — okul / dershane / özel ders / sosyal / diğer. */
  kategori?: Kategori | null;
  aciklama?: string | null;
}): Promise<{ id: string }> {
  return api("/takvim/mesguliyet", { yontem: "POST", govde: veri });
}

/**
 * ÖĞRENCİ HAFTALIK PLANI — F-PPL-07b. Tekrarlı kayıtlar AÇILMADAN döner
 * ("her pazartesi okul" tek satır); plan ekranı bunları düzenliyor.
 */
export function planTanimlari(ogrenciId: string): Promise<PlanTanimi[]> {
  return api(`/takvim/ogrenci/${encodeURIComponent(ogrenciId)}/plan`);
}

export function planGuncelle(
  etkinlikId: string,
  veri: {
    baslik?: string;
    baslangic?: string;
    bitis?: string;
    tekrarKurali?: string | null;
    kategori?: Kategori | null;
    aciklama?: string | null;
  },
): Promise<{ id: string }> {
  return api(`/takvim/mesguliyet/${encodeURIComponent(etkinlikId)}`, {
    yontem: "PATCH",
    govde: veri,
  });
}

/** Kaldırma = iptal; sunucu silmiyor. */
export function planIptal(etkinlikId: string): Promise<{ iptal: true }> {
  return api(`/takvim/mesguliyet/${encodeURIComponent(etkinlikId)}/iptal`, { yontem: "POST" });
}

// =====================================================================
// VELİ İLİŞKİSİ — F-PPL-03
// =====================================================================

export interface VeliOzeti {
  /** `StudentParent` BAĞ kaydının kimliği (veli hesabının değil): muvafakat ve işaret uçları bunu ister. */
  id: string;
  parentUserId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  relation: string;
  isPrimary: boolean;
  receivesReport: boolean;
  receivesBilling: boolean;
  /** Kapatılırsa veli o öğrencinin verisine ANINDA erişemez olur */
  hasPortalAccess: boolean;
  isLegalGuardian: boolean;
}

export function veliListesi(ogrenciId: string): Promise<VeliOzeti[]> {
  return api(`/ogrenciler/${encodeURIComponent(ogrenciId)}/veliler`);
}

/**
 * Veli hesabı yoksa AÇILIR ve davet gönderilir. Aynı e-posta başka bir
 * öğrenciye de bağlanırsa yeni hesap açılmaz, mevcut hesap kullanılır.
 */
export function veliBagla(
  ogrenciId: string,
  veri: {
    email: string;
    firstName: string;
    lastName: string;
    phone?: string | null;
    nationalId?: string | null;
    phoneIsPrimary?: boolean;
    relation: string;
    davetGonder?: boolean;
    isPrimary?: boolean;
    receivesReport?: boolean;
    receivesBilling?: boolean;
    hasPortalAccess?: boolean;
    isLegalGuardian?: boolean;
  },
): Promise<{ id: string; yeniHesap: boolean; davetGonderildi: boolean | null }> {
  return api(`/ogrenciler/${encodeURIComponent(ogrenciId)}/veliler`, {
    yontem: "POST",
    govde: veri,
  });
}

export function veliIsaretGuncelle(
  ogrenciId: string,
  bagId: string,
  isaretler: Partial<
    Pick<
      VeliOzeti,
      "isPrimary" | "receivesReport" | "receivesBilling" | "hasPortalAccess" | "isLegalGuardian"
    >
  >,
): Promise<VeliOzeti> {
  return api(`/ogrenciler/${encodeURIComponent(ogrenciId)}/veliler/${encodeURIComponent(bagId)}`, {
    yontem: "PATCH",
    govde: isaretler,
  });
}

/** Veli HESABI silinmez — başka bir öğrencinin velisi olabilir. */
export function veliBagiKaldir(ogrenciId: string, bagId: string, gerekce: string): Promise<void> {
  return api(`/ogrenciler/${encodeURIComponent(ogrenciId)}/veliler/${encodeURIComponent(bagId)}`, {
    yontem: "DELETE",
    govde: { gerekce },
  });
}

// =====================================================================
// UZMAN ATAMASI VE DEVRİ — F-PPL-10
// =====================================================================

export interface AtamaOzeti {
  id: string;
  coachId: string;
  coachUserId: string;
  firstName: string;
  lastName: string;
  isPrimary: boolean;
  assignedAt: string;
  /** Erişimin kesileceği an — null = süresiz */
  endedAt: string | null;
  gecerli: boolean;
}

export function atamaListesi(ogrenciId: string): Promise<AtamaOzeti[]> {
  return api(`/ogrenciler/${encodeURIComponent(ogrenciId)}/atamalar`);
}

/**
 * Atanabilir uzmanlar — TAM YÜK TABLOSUYLA (F-PPL-12).
 *
 * Uç uzun süre yalnızca aktif öğrenci sayısı döndürüyordu ve o,
 * maddenin ilk cümlesine aykırıydı: *"öğrenci sayısı tek başına iş
 * yükü değildir."* Sıralama en uygun adayı üste taşıyor ama izinli ve
 * kotası dolmuş uzmanlar listeden DÜŞMÜYOR — bazen bilerek onlardan
 * birine atanır.
 */
export function atanabilirUzmanlar(
  ogrenciId: string,
): Promise<Array<{ coachId: string; ad: string; yuk: UzmanYuku }>> {
  return api(`/ogrenciler/${encodeURIComponent(ogrenciId)}/atanabilir-uzmanlar`);
}

/** Atanmış uzman VARSA reddeder — değişiklik `devir` ile yapılır. */
export function uzmanAta(ogrenciId: string, coachId: string): Promise<{ id: string }> {
  return api(`/ogrenciler/${encodeURIComponent(ogrenciId)}/atamalar`, {
    yontem: "POST",
    govde: { coachId },
  });
}

/**
 * Eski uzmanın erişimi DEVİR ANINDA kesilir — seçilebilir bir bitiş
 * tarihi yoktur (F-PPL-10, 08.08.2026). Korumalı notlar VARSAYILAN
 * OLARAK devredilmez.
 *
 * `erisimBitis` yanıtta duruyor ama artık her zaman `null`.
 */
export function uzmanDevret(
  ogrenciId: string,
  veri: {
    toCoachId: string;
    reason: string;
    handoverNote?: string | null;
    transferProtectedNotes?: boolean;
  },
): Promise<{ id: string; erisimBitis: string | null }> {
  return api(`/ogrenciler/${encodeURIComponent(ogrenciId)}/devir`, {
    yontem: "POST",
    govde: veri,
  });
}

export function devirGecmisi(ogrenciId: string): Promise<
  Array<{
    id: string;
    fromCoachAd: string | null;
    toCoachAd: string;
    reason: string;
    handoverNote: string | null;
    transferredAt: string;
  }>
> {
  return api(`/ogrenciler/${encodeURIComponent(ogrenciId)}/devir-gecmisi`);
}

// =====================================================================
// ÖĞRENCİ DOSYALARI — F-SYS-03
// =====================================================================

export type DosyaTuru = "PDF" | "IMAGE" | "REPORT" | "RECEIPT" | "AVATAR" | "OTHER";

export interface DosyaOzeti {
  id: string;
  originalName: string;
  kind: DosyaTuru;
  mimeType: string;
  sizeBytes: number;
  createdAt: string;
}

export function dosyaListesi(ogrenciId: string): Promise<DosyaOzeti[]> {
  return api(`/ogrenciler/${encodeURIComponent(ogrenciId)}/dosyalar`);
}

export function dosyaKotasi(ogrenciId: string): Promise<{
  kullanilan: number;
  sinir: number;
  /** 0..1 arası — yüzde DEĞİL */
  oran: number;
  uyariVer: boolean;
  doldu: boolean;
  /** "12,3 MB / 500 MB" — biçimlendirmeyi sunucu yapıyor */
  okunabilir: string;
}> {
  return api(`/ogrenciler/${encodeURIComponent(ogrenciId)}/dosyalar/kota`);
}

/**
 * Dosya multipart/form-data ile gidiyor. Eskiden api() yalniz JSON
 * gonderdigi icin ayri fetch kullaniliyordu; hata isleme 401/5xx
 * davranisini korumak amaciyla burada tekrarlanmisti.
 *
 * 14.09.2026: api() FormData destekliyor. Ayri yol oturum iptalini,
 * gec yanit denetimini ve istek kimligini kacirdigi icin tek kapiya
 * tasindi; boundary basligini yine tarayici uretiyor.
 */
export async function dosyaYukle(
  ogrenciId: string,
  dosya: File,
  kind: DosyaTuru,
): Promise<DosyaOzeti> {
  const govde = new FormData();
  govde.append("dosya", dosya);
  govde.append("kind", kind);

  // F-AUTH-07 — dosya yükleme de oturum iptali ve geç yanıt denetiminden geçer.
  return api(`/ogrenciler/${encodeURIComponent(ogrenciId)}/dosyalar`, { yontem: "POST", govde });
}

/** İmzalı, 15 dakika geçerli bağlantı. Doğrudan erişim yok. */
export function dosyaBaglantisi(
  dosyaId: string,
): Promise<{ url: string; gecerlilikSaniye: number }> {
  return api(`/dosyalar/${encodeURIComponent(dosyaId)}/baglanti`);
}

/** Yumuşak silme — depodan kaldırma 30 gün sonra temizlik işinde. */
export function dosyaSil(dosyaId: string, gerekce: string): Promise<void> {
  return api(`/dosyalar/${encodeURIComponent(dosyaId)}/sil`, {
    yontem: "POST",
    govde: { gerekce },
  });
}

// =====================================================================
// reCAPTCHA v3 YÖNETİMİ — F-AUTH
// =====================================================================

/*
  `RecaptchaDurumu` ve `recaptchaDurumu()` YUKARIDA zaten var (giriş
  ekranı bölümü). İkinci kez yazmıştım; TypeScript yakaladı.
*/

/**
 * Gizli anahtar şifrelenerek saklanır ve BİR DAHA OKUNAMAZ. Boş
 * bırakılırsa mevcut anahtar KORUNUR — "sil" anlamına gelmez.
 */
export function recaptchaAyarla(veri: {
  siteAnahtari: string;
  gizliAnahtar?: string | null;
  aktif: boolean;
  esik?: number;
  /** Etkinleştirme kanıtı — `aktif: true` için ZORUNLU */
  dogrulamaJetonu?: string | null;
}): Promise<{ kaydedildi: true; aktif: boolean; neden?: string }> {
  /*
    Yanıttaki `aktif` GERÇEKTE ne olduğunu söyler. Gizli anahtar
    olmadan `aktif: true` istenirse sunucu `false` döner — istenenle
    olanın farkını ekran göstermek zorunda.
  */
  return api("/auth/recaptcha/ayarla", { yontem: "POST", govde: veri });
}

// =====================================================================
// TEK TABLO KURTARMA — F-YED-28
// =====================================================================

export type TabloRiski = "GUVENLI" | "VERI_KAYBI" | "BAGLANTI_KIRILIR";

export interface TabloOnizlemesi {
  tablo: string;
  runId: string;
  ozet: {
    geriGelecek: number;
    guncellenecek: number;
    /** Üretimde var, yedekte YOK — SİLİNECEK. Tek yıkıcı küme bu. */
    silinecek: number;
  };
  kirilanBaglantilar: Array<{ tablo: string; sutun: string; satir: number }>;
  risk: TabloRiski;
  cumle: string;
  calistirilabilir: boolean;
  /** Yazma isteği bunu geri gönderiyor — onay GÖRÜLEN özete ait olmalı */
  parmakIzi: string;
}

export function tekTabloOnizle(runId: string, tablo: string): Promise<TabloOnizlemesi> {
  return api("/yedek/geri-yukleme/tek-tablo/onizle", {
    yontem: "POST",
    govde: { runId, tablo },
  });
}

export function tekTabloUygula(veri: {
  runId: string;
  tablo: string;
  parmakIzi: string;
}): Promise<{ tablo: string; eklenen: number; guncellenen: number; silinen: number }> {
  return api("/yedek/geri-yukleme/tek-tablo/uygula", { yontem: "POST", govde: veri });
}

// =====================================================================
// HATALI SORU DEFTERİ — F-MST
// =====================================================================

export type HataTipi = "KNOWLEDGE_GAP" | "CARELESS" | "TIME" | "MISREAD" | "CALCULATION";

export interface HataKaydi {
  id: string;
  studentId: string;
  lessonId: string;
  topicId: string | null;
  imageUrl: string | null;
  errorType: HataTipi | null;
  studentNote: string | null;
  coachNote: string | null;
  /** Çözüldü işareti — İKİ kez üst üste doğru gerekiyor (F-MST-04) */
  resolvedAt: string | null;
  retryCount: number;
  lastRetryOk: boolean | null;
  createdAt: string;
  lesson?: { name: string } | null;
  topic?: { name: string } | null;
}

/** Katalog SUNUCUDAN — ekran kendi listesini tutmuyor. */
export function hataTipleri(): Promise<Record<string, string>> {
  return api("/hata-defteri/hata-tipleri");
}

export function hataDefteri(ogrenciId: string, yalnizcaCozulmemis = false): Promise<HataKaydi[]> {
  return api(`/hata-defteri/ogrenci/${encodeURIComponent(ogrenciId)}`, {
    sorgu: { cozulmemis: yalnizcaCozulmemis ? "1" : undefined },
  });
}

export interface HataHaritasi {
  konular: Array<{
    topicId: string;
    konuAdi: string;
    dersAdi: string | null;
    toplam: number;
    cozulmemis: number;
    baskinTip: HataTipi | null;
    /**
     * Beş hata tipinin her birinin sayısı — F-MST-07.
     *
     * `baskinTip` tek başına yanıltıcı: 12 dikkatsizlik + 11 bilgi
     * eksiği ile 12 dikkatsizlik + 1 bilgi eksiği aynı görünür, oysa
     * ilkine konu anlatımı, ikincisine soru çözümü yazılır.
     * Beraberlikte `baskinTip` `null` dönüyor ve o durumda ekranda
     * hiçbir bilgi kalmıyordu.
     *
     * Sıfır olan tipler de anahtarda VAR — eksik anahtar "ölçülmedi"
     * ile "sıfır"ı ısı haritasında aynı boş hücreye çevirirdi.
     *
     * Toplamları `toplam` ile tutmayabilir: tipi işaretlenmemiş kayıt
     * hiçbir tipe sayılmıyor. Bu bir hata değil.
     */
    tipDagilimi: Record<HataTipi, number>;
  }>;
  konusuzKayit: number;
}

/**
 * Isı haritasının sütun sırası ve tip adları — F-MST-07.
 *
 * Burada YENİDEN YAZILMIYOR, `@koclukpanel/contracts`ten geliyor.
 * Sunucu da aynı listeyi oradan okuyor; iki taraf ayrı ayrı yazsaydı
 * biri değiştiği gün ayrışır ve iki öğrencinin haritası farklı sıralı
 * çizilirdi — hiçbir şey patlamaz, yalnızca uzmanın çıkardığı sonuç
 * yanlış olurdu.
 */
export { HATA_TIPI_SIRASI, HATA_TIPI_ADI } from "@koclukpanel/contracts";

export function hataHaritasi(ogrenciId: string): Promise<HataHaritasi> {
  return api(`/hata-defteri/ogrenci/${encodeURIComponent(ogrenciId)}/harita`);
}

/** Çözülmüşler listede YOK; en eski hata önce (unutulmuş olma ihtimali yüksek). */
export function hataGozdenGecir(ogrenciId: string, adet = 20): Promise<HataKaydi[]> {
  return api(`/hata-defteri/ogrenci/${encodeURIComponent(ogrenciId)}/gozden-gecir`, {
    sorgu: { adet },
  });
}

/*
 * `hataEkle` ve `hataKaldir` BU DOSYADA BILEREK YOK.
 *
 * Ikisi de ogrenci uygulamasinda yasiyor (`apps/ogrenci/src/api.ts`)
 * ve panelden cagrilmalari SARTNAMEYE aykiri:
 *
 * - EKLEME: kaydi ogrenci acar (F-MST-01). Uzmanin ogrenci adina
 *   soru uydurabilmesi, defterin ne ise yaradigini bozar.
 * - KALDIRMA: uc "YALNIZCA defterin sahibi" diyor; matris uzmana
 *   `RCU` veriyor, `D` vermiyor.
 *
 * Panelde durduklari surece "yazilmis, cagrilmayi bekliyor" gibi
 * gorunuyorlardi ve bir gun biri onlari ekrana baglardi. Bosluk
 * taramasi da her turda olu istemci olarak sayiyordu. Silmek,
 * yorumla "kullanmayin" yazmaktan daha guclu bir koruma.
 */

export function hataGuncelle(
  id: string,
  veri: {
    errorType?: HataTipi | null;
    topicId?: string | null;
    studentNote?: string | null;
    coachNote?: string | null;
  },
): Promise<HataKaydi> {
  return api(`/hata-defteri/${encodeURIComponent(id)}`, { yontem: "POST", govde: veri });
}

/**
 * TEK doğru "çözüldü" saymaz — iki kez üst üste doğru gerekir.
 * Yanlış tekrar "çözüldü" işaretini SIFIRLAR: artık doğru olmayan bir
 * bilgi öğrenciyi yanıltır.
 */
export function hataTekrar(id: string, dogruMu: boolean): Promise<HataKaydi> {
  return api(`/hata-defteri/${encodeURIComponent(id)}/tekrar`, {
    yontem: "POST",
    govde: { dogruMu },
  });
}

// =====================================================================
// VELİ MEMNUNİYET ANKETİ — F-SRV
// =====================================================================

export interface AnketSorusu {
  anahtar: string;
  metin: string;
  tip: "SCALE_5" | "NPS" | "TEXT";
  zorunlu: boolean;
}

export interface AnketAcilis {
  id: string;
  ogrenciAdi: string;
  sorular: AnketSorusu[];
}

/**
 * GİRİŞ GEREKTİRMEZ (F-SRV-02). Güvenlik jetonun kendisinde: tek
 * ankete bağlı, cevaplanınca ve süresi dolunca geçersiz. Açtığı tek
 * şey o velinin kendi anketi.
 */
export function anketiAc(jeton: string): Promise<AnketAcilis> {
  return api(`/anketler/jeton/${encodeURIComponent(jeton)}`);
}

export function anketiYanitla(
  jeton: string,
  yanitlar: Record<string, number | string>,
): Promise<unknown> {
  return api(`/anketler/jeton/${encodeURIComponent(jeton)}/yanit`, {
    yontem: "POST",
    govde: { yanitlar },
  });
}

// =====================================================================
// UZMAN TARAFI SONUÇ GİRİŞİ — F-EXM-04
// =====================================================================

/**
 * Cevap cevap giriş. `cevaplar` soru numarası -> harf; boş bırakılan
 * soru gönderilmez (boş = cevaplanmadı).
 */
export function uzmanSonucGirisi(
  denemeId: string,
  veri: { ogrenciId: string; cevaplar: Record<string, string>; sureDakika?: number | null },
): Promise<unknown> {
  return api(`/denemeler/${encodeURIComponent(denemeId)}/sonuc`, {
    yontem: "POST",
    govde: veri,
  });
}

/**
 * Ders bazlı toplu net. `rawAnswers` BOŞ kalır ve soru/konu bazlı
 * analiz ÜRETİLMEZ — olmayan veriden çıkarım yapmış olurduk.
 */
export function topluNetGirisi(
  denemeId: string,
  veri: {
    ogrenciId: string;
    netler: Array<{ sectionId: string; dogru: number; yanlis: number; bos: number }>;
    sureDakika?: number | null;
  },
): Promise<unknown> {
  return api(`/denemeler/${encodeURIComponent(denemeId)}/toplu-net`, {
    yontem: "POST",
    govde: veri,
  });
}

export interface TopluOnizleme {
  /** Bulunan ayraç — Türkçe Excel noktalı virgül kullanıyor */
  ayirac: string;
  yuklenecek: Array<{
    satirNo: number;
    ogrenciId: string;
    ogrenciNo: number;
    ogrenciAdi: string;
    isaretliSayisi: number;
    sureDakika: number | null;
    /** Bu öğrencinin ZATEN sonucu var — üzerine yazılacak */
    uzerineYazilacak: boolean;
  }>;
  hatali: Array<{ satirNo: number; ham: string; sebep: string }>;
}

/** HİÇBİR ŞEY YAZMAZ — yalnızca ne olacağını gösterir. */
export function topluOnizle(denemeId: string, metin: string): Promise<TopluOnizleme> {
  return api(`/denemeler/${encodeURIComponent(denemeId)}/toplu-onizleme`, {
    yontem: "POST",
    govde: { metin },
  });
}

/**
 * Metin YENİDEN çözülür; ön izlemenin sonucuna güvenilmiyor. Bir
 * satır patlarsa diğerleri devam eder.
 */
export function topluUygula(
  denemeId: string,
  metin: string,
): Promise<{
  yazilan: number;
  ayrinti: unknown[];
  hatali: Array<{ satirNo: number; ham: string; sebep: string }>;
  basarisiz: Array<{ satirNo: number; ogrenciNo: number; sebep: string }>;
}> {
  return api(`/denemeler/${encodeURIComponent(denemeId)}/toplu-uygula`, {
    yontem: "POST",
    govde: { metin },
  });
}

// =====================================================================
// KONU BİTİRME TAKVİMİ — F-CUR (§4.7b)
// =====================================================================

/**
 * BU BLOK 04.09.2026'DA YAZILDI.
 *
 * Plan modülünün DÖRT ucu da sunucuda hazırdı ve `api.ts`'te hiçbir
 * karşılığı yoktu: öğrenciye konu planı kurmanın panelde yolu yoktu.
 * Görev listesinde madde işaretliydi çünkü sunucu tarafı gerçekten
 * bitmişti.
 */

export type PlanDurumu = "PLANSIZ" | "PLANLANDI" | "DEVAM" | "GECIKTI" | "TAMAMLANDI";

export interface KonuPlani {
  id: string;
  konuId: string;
  konu: string;
  dersId: string;
  ders: string;
  asama: string;
  baslangic: string | null;
  sureGun: number | null;
  bitis: string | null;
  /** `null` = plan yok YA DA konu bitti. SIFIR DEĞİL — ikisi ayrı şey. */
  kalanGun: number | null;
  durum: PlanDurumu;
  gecKapandi: boolean;
  videoKanali: string | null;
  not: string | null;
  planlayan: string | null;
  planlanmaZamani: string | null;
  kaynaklar: Array<{ id: string; ad: string }>;
}

export type TopluPlanSonucu =
  | {
      onIzleme: true;
      uygulanacak: Array<{ kayitId: string; konu: string; revizeMi: boolean }>;
      atlanan: Array<{ kayitId: string; neden: string }>;
    }
  | {
      onIzleme: false;
      planlanan: number;
      atlanan: Array<{ kayitId: string; neden: string }>;
    };

/**
 * PLANI OLMAYAN konular da döner (`durum: "PLANSIZ"`).
 *
 * Plan koyabilmek için önce görünmeleri gerekiyor; süzgeç isteğe
 * bağlı. `yalnizPlanli` varsayılan olarak KAPALI — açık olsaydı ekran
 * "plansız konu yok" izlenimi verirdi.
 */
export function planTakvimi(
  ogrenciId: string,
  filtre: { ders?: string; yalnizPlanli?: boolean } = {},
): Promise<KonuPlani[]> {
  return api<KonuPlani[]>(`/ogrenciler/${encodeURIComponent(ogrenciId)}/plan`, {
    sorgu: {
      ders: filtre.ders,
      yalnizPlanli: filtre.yalnizPlanli ? "1" : undefined,
    },
  });
}

export function planKoy(
  ogrenciId: string,
  kayitId: string,
  veri: {
    baslangic: string;
    sureGun: number;
    videoKanali?: string | null;
    not?: string | null;
    kaynakIdler?: string[];
  },
): Promise<KonuPlani> {
  return api(`/ogrenciler/${encodeURIComponent(ogrenciId)}/plan/${encodeURIComponent(kayitId)}`, {
    yontem: "POST",
    govde: veri,
  });
}

/**
 * TOPLU PLANLAMA — F-CUR-07.
 *
 * `onIzleme: true` gönderildiğinde HİÇBİR ŞEY YAZILMIYOR; hangi
 * konunun planlanacağı ve hangisinin neden atlanacağı dönüyor.
 * Ekran önce onu gösteriyor: iki yüz konuyu görmeden yazmak,
 * geri alınması zor bir işlem.
 */
export function planToplu(
  ogrenciId: string,
  kalemler: Array<{ kayitId: string; baslangic: string; sureGun: number }>,
  onIzleme = false,
): Promise<TopluPlanSonucu> {
  return api(`/ogrenciler/${encodeURIComponent(ogrenciId)}/plan/toplu`, {
    yontem: "POST",
    govde: { kalemler, onIzleme },
  });
}

/** PLANI kaldırır — KONUYU kaldırmaz. Gerekçe zorunlu, kayda geçer. */
export function planKaldir(ogrenciId: string, kayitId: string, gerekce: string): Promise<unknown> {
  return api(`/ogrenciler/${encodeURIComponent(ogrenciId)}/plan/${encodeURIComponent(kayitId)}`, {
    yontem: "DELETE",
    govde: { gerekce },
  });
}

// =====================================================================
// PROFİL DÜZENLEME — F-PPL
// =====================================================================

/**
 * BU BLOK 04.09.2026'DA YAZILDI.
 *
 * İki `PATCH` ucu da sunucuda hazırdı, hiçbir ekran çağırmıyordu:
 * yanlış yazılmış bir ad ya da değişen bir telefon numarası panelden
 * DÜZELTİLEMİYORDU.
 */

/**
 * Personel bilgisi günceller.
 *
 * `email` ve `roleIds` BİLEREK YOK — ayrı uçlar, ayrı korumalar.
 * E-posta değişimi hesabı ele geçirmenin en kısa yolu; rol değişimi
 * sudo ve yükseltme koruması istiyor.
 */
export function personelGuncelle(
  id: string,
  veri: { firstName?: string; lastName?: string; phone?: string | null },
): Promise<{ guncellendi: string[] }> {
  return api(`/personel/${encodeURIComponent(id)}`, { yontem: "PATCH", govde: veri });
}

/**
 * Öğrenci profili günceller.
 *
 * `birthDate` BİLEREK YOK: değiştirilebilseydi 18 yaş altı bir
 * öğrencinin tarihi ileri çekilerek veli muvafakati kapısı
 * atlatılabilirdi.
 */
/**
 * Hedef alanlari — F-PPL-05.
 *
 * `null` "TEMIZLE", alanin yoklugu "DOKUNMA" demek. Ikisi ayri
 * anlamlar ve sunucu da boyle ayiriyor (`hedef.ts`). Tipte
 * `| null` olmasaydi bir hedefi silmenin yolu olmazdi.
 */
export interface HedefGirdisi {
  targetExamKind?: string | null;
  targetScore?: number | null;
  targetRank?: number | null;
  targetSchool?: string | null;
  /** `YYYY-AA-GG` */
  examDate?: string | null;
}

export function ogrenciProfilGuncelle(
  id: string,
  veri: {
    firstName?: string;
    lastName?: string;
    phone?: string | null;
    gradeLevel?: string;
    fieldId?: string | null;
  } & HedefGirdisi,
): Promise<unknown> {
  return api(`/ogrenciler/${encodeURIComponent(id)}`, { yontem: "PATCH", govde: veri });
}

/**
 * Program görevini günceller — F-SCH.
 *
 * `gorevTasi` ile karışmasın: o yalnızca GÜN ve SIRA yazıyor, bu
 * görevin içeriğini (hedef, kaynak, sayfa, not) değiştiriyor.
 * Bu uç olmadan bir görevi düzeltmenin tek yolu silip yeniden
 * eklemekti — ve öğrenci işaretlemişse silinemiyordu.
 */
export function gorevGuncelle(
  gorevId: string,
  veri: {
    kind?: string;
    lessonId?: string;
    topicId?: string | null;
    pageFrom?: number | null;
    pageTo?: number | null;
    targetQuestionCount?: number | null;
    startTime?: string | null;
    coachNote?: string | null;
    isExcused?: boolean;
  } & GorevIcerigi,
): Promise<ProgramGorevi> {
  return api<ProgramGorevi>(`/program/gorevler/${encodeURIComponent(gorevId)}`, {
    yontem: "PATCH",
    govde: veri,
  });
}

/**
 * KONU BAZLI DENEME SONUCU — F-TPC-02.
 *
 * NİHAİ DURUMU SUNUCU BELİRLER: başarı eşiğin altındaysa konu
 * otomatik "tekrar gerekiyor"a döner; üstündeyse "tamamlandı"
 * işareti "denemede doğrulandı"ya yükselir.
 *
 * `konuDurumDegistir` ile karışmasın: o uzmanın İDDİASINI yazıyor,
 * bu ÖLÇÜMÜ. Faz 4'teki deneme motoru aynı servisi çağıracak —
 * kural tek yerde kalsın diye.
 */
export function konuDenemeSonucu(
  ogrenciId: string,
  konuId: string,
  veri: { dogru: number; toplam: number },
): Promise<{ durum: KonuDurumu; otomatikDusuruldu: boolean }> {
  return api(
    `/ogrenciler/${encodeURIComponent(ogrenciId)}/konular/${encodeURIComponent(konuId)}/deneme`,
    { yontem: "POST", govde: veri },
  );
}

/** Görüşme kaydını arşivler — kalıcı silme yok (kural 7). */
export function gorusmeArsivle(id: string): Promise<unknown> {
  return api(`/gorusmeler/${encodeURIComponent(id)}/arsivle`, { yontem: "POST" });
}

// =====================================================================
// HAFTALIK ÖĞRENCİ ANALİZİ — F-ANL-02/03/04/05/08
// =====================================================================

/**
 * BU BLOK 04.09.2026'DA YAZILDI.
 *
 * `GET /analiz/ogrenci/:id/hafta` sunucuda hazırdı ve hiçbir ekran
 * çağırmıyordu: dört başlıklı analiz (program uyumu, akademik
 * performans, çalışma davranışı, müdahale önerileri) üretiliyor ve
 * kimse görmüyordu.
 */

export interface Degisim {
  onceki: number | null;
  simdiki: number | null;
  fark: number | null;
  /** Yüzde puan DEĞİL, oransal değişim (0.2 = %20 artış) */
  oransalDegisim: number | null;
  yorum: "IYILESME" | "KOTULESME" | "AYNI" | "KARSILASTIRILAMAZ";
}

export interface VeriKapsami {
  toplamGorev: number;
  veriGirilenGorev: number;
  /** 0..1; görev yoksa `null` */
  oran: number | null;
  dusukMu: boolean;
  metin: string;
}

export interface AnalizOnerisi {
  kod: string;
  /** Kararlı ve benzersiz (`kod:ders` / `kod:konu`); eski sunucuda yok olabilir (rol testi H11). */
  anahtar?: string;
  metin: string;
  oncelik: "YUKSEK" | "ORTA" | "BILGI";
}

export interface HaftalikAnaliz {
  haftaBasi: string;
  haftaSonu: string;
  kapsam: VeriKapsami;
  programUyumu: {
    atanan: number;
    tamamlanan: number;
    mazeretli: number;
    /** Ölçülemeyen metrik `null` döner, 0 DEĞİL. */
    tamamlamaOrani: number | null;
    ertelemeOrani: number | null;
    devamlilik: number | null;
  };
  akademik: {
    dogru: number;
    yanlis: number;
    bos: number;
    cozulenSoru: number;
    basariOrani: number | null;
    dersler: Array<{ ders: string; dogru: number; yanlis: number; basariOrani: number | null }>;
  };
  calismaDavranisi: {
    toplamSureSaniye: number;
    /**
     * Veri girilmiş ama süresi girilmemiş görev sayısı.
     *
     * Sunucu bunu 04.09.2026'dan beri GÖNDERİYORDU; bu tip alanı
     * bildirmediği için TypeScript hiçbir ekranın ona erişmesine izin
     * vermiyordu. Yani alan tel üzerinden geliyor, ekran göremiyordu —
     * "yazıldı ama görünmez"in bir kat aşağıdaki hâli, ve derleyici
     * bunu hata değil ERİŞİLEMEZLİK olarak yaşattığı için sessizdi.
     *
     * Sıfırdan büyükse toplam süre EKSİK demektir (yanlış değil).
     */
    sureGirilmeyenGorev: number;
    ortalamaGorevSuresi: number | null;
    enVerimliGun: string | null;
  };
  oncekiHaftayaGore: {
    basariOrani: Degisim;
    tamamlamaOrani: Degisim;
    cozulenSoru: Degisim;
    calismaSuresi: Degisim;
  };
  oneriler: AnalizOnerisi[];
}

/**
 * Öneriler ŞABLON TABANLI (LLM yok): aynı girdi her zaman aynı
 * çıktıyı veriyor ve cümlenin nereden geldiği açıklanabilir.
 */
/**
 * DETAYLI ANALİZ — F-ANL-08b (04.10.2026, müşteri revizesi).
 * Gün gün + ders/konu + iki haftanın karşılaştırması. Cevap şekli
 * `@koclukpanel/ui` içindeki `DetayliAnalizVerisi` — panel ve öğrenci
 * uygulaması aynı bileşenle çiziyor.
 */
/** Sürüm 9a — ders/konu süzgeci (kimlik) ve açılır liste seçenekleri (süzülmemiş iki hafta). */
export interface AnalizSuzgeci {
  ders?: string | undefined;
  konu?: string | undefined;
}

export interface AnalizSuzgecSecenekleri {
  dersler: Array<{
    dersId: string;
    ders: string;
    konular: Array<{ konuId: string; konu: string }>;
  }>;
}

export type DetayliAnalizYaniti = DetayliAnalizVerisi & {
  suzgecSecenekleri?: AnalizSuzgecSecenekleri;
};

export function detayliAnaliz(
  ogrenciId: string,
  tarih: string,
  karsilastir?: string,
  suzgec: AnalizSuzgeci = {},
): Promise<DetayliAnalizYaniti> {
  return api<DetayliAnalizYaniti>(`/analiz/ogrenci/${encodeURIComponent(ogrenciId)}/detay`, {
    sorgu: { tarih, karsilastir, ders: suzgec.ders, konu: suzgec.konu },
  });
}

/**
 * Sürüm 9a — detaylı analizin PDF'i: sunucu istek anında üretir (depoya yazılmaz), dosya adı +
 * base64 döner. `api()` tek kapısından geçer (JSON); `pdfIndir` tarayıcıda dosyaya çevirir.
 */
export function detayliAnalizPdf(
  ogrenciId: string,
  tarih: string,
  karsilastir?: string,
  suzgec: AnalizSuzgeci = {},
): Promise<{ dosyaAdi: string; pdfBase64: string }> {
  return api(`/analiz/ogrenci/${encodeURIComponent(ogrenciId)}/detay/pdf`, {
    sorgu: { tarih, karsilastir, ders: suzgec.ders, konu: suzgec.konu },
  });
}

/** Sürüm 9a — koç anasayfa kontenjan kartı; koç değilse `null`. */
export function kontenjanim(): Promise<{ aktifOgrenci: number; kota: number } | null> {
  return api("/personel/kontenjanim");
}

export function haftalikAnaliz(ogrenciId: string, tarih?: string): Promise<HaftalikAnaliz> {
  return api<HaftalikAnaliz>(`/analiz/ogrenci/${encodeURIComponent(ogrenciId)}/hafta`, {
    sorgu: { tarih },
  });
}

// =====================================================================
// DENEME ANALİZİ VE CEVAP ANAHTARI — F-EXM-07/08/12
// =====================================================================

export interface SoruIstatistigi {
  questionNo: number;
  cozenSayisi: number;
  dogruSayisi: number;
  /** Çözen yoksa `null` — "%0 doğru" DEĞİL */
  dogruOrani: number | null;
  bosSayisi: number;
}

export interface DenemeAnalizi {
  denemeId: string;
  katilimci: number;
  /** Soru bazlı analiz ancak CEVAP KODLAMASI varsa üretilir. */
  cevapliSonuc: boolean;
  sorular: SoruIstatistigi[];
  bosBirakmaOrani: number | null;
}

/**
 * SORU BAZLI ANALİZ — F-EXM-07/08.
 *
 * Doğru oranı boşları paydaya ALMAZ: ölçülen şey sorunun zorluğu,
 * öğrencinin zaman yönetimi değil.
 */
export function denemeAnalizi(denemeId: string): Promise<DenemeAnalizi> {
  return api<DenemeAnalizi>(`/denemeler/${encodeURIComponent(denemeId)}/analiz`);
}

export interface AnahtarBolumu {
  id: string;
  name: string;
  questions: Array<{
    questionNo: number;
    correctAnswer: string;
    isCancelled: boolean;
    topicId: string | null;
  }>;
}

/**
 * CEVAP ANAHTARI — F-EXM-12.
 *
 * Öğrenciye cevap giriş penceresi kapanmadan AÇILMAZ (`ANAHTAR_KAPALI`).
 * `deneme.cevapanahtari.duzenle` izni olan personel her zaman görür.
 * Okuma DATA_READ olarak kayda geçiyor.
 */
export function cevapAnahtari(denemeId: string): Promise<AnahtarBolumu[]> {
  return api<AnahtarBolumu[]>(`/denemeler/${encodeURIComponent(denemeId)}/anahtar`);
}

/**
 * PAKET GÖRÜŞME HAKKI — F-SES-07.
 *
 * `toplam` ve `kalan` şu an `null` DÖNÜYOR: paket tanımı Faz 5'te
 * geldi ama hak sayısı henüz pakete bağlanmadı. `kullanilan` gerçek.
 * Ekran `null`ı "0 hak kaldı" diye göstermemeli.
 */
export function gorusmeHakDurumu(
  ogrenciId: string,
): Promise<{ toplam: number | null; kullanilan: number; kalan: number | null }> {
  return api(`/gorusmeler/ogrenci/${encodeURIComponent(ogrenciId)}/hak`);
}

/** PROGRAM REVİZYON GEÇMİŞİ — F-SCH-14. */
export interface ProgramRevizyonu {
  id: string;
  revision: number;
  changedById: string;
  diff: unknown;
  reason: string | null;
  createdAt: string;
}

export function programRevizyonlari(programId: string): Promise<ProgramRevizyonu[]> {
  return api<ProgramRevizyonu[]>(`/program/${encodeURIComponent(programId)}/revizyonlar`);
}

// =====================================================================
// GÖRÜLEMEYEN VERİ BLOĞU — 04.09.2026
// =====================================================================

/**
 * Hepsi sunucuda hazırdı ve hiçbir ekran çağırmıyordu. Boşluk
 * taraması bunları "okuma ucu var, bu veriyi kimse görmüyor"
 * başlığı altında sayıyordu.
 */

/**
 * UZMANIN KENDİ ANKET ÖZETİ — F-SRV-07.
 *
 * ANONİM: hangi velinin ne yazdığı YOK, yalnızca yorum metinleri.
 * Şartnamenin kuralı bu ve tip de öyle: `yorumlar` bir dizi metin,
 * kimlik taşımıyor.
 */
export interface AnketOzetim {
  gonderilen: number;
  yanitlanan: number;
  /** `null` = hiç anket gönderilmedi — "%0 yanıt" DEĞİL */
  yanitOrani: number | null;
  ortalama: number | null;
  npsSkoru: number | null;
  yorumlar: string[];
}

export function anketOzetim(): Promise<AnketOzetim> {
  return api<AnketOzetim>("/anketler/ozetim");
}

/**
 * GÖRÜNÜMÜNE GEÇİLEBİLECEK KİŞİLER — G-SES-06.
 *
 * `sistem.gorunum.gec` izni ister ve o izin sudo listesinde.
 * Arama metniyle süzülüyor: bütün kullanıcıları listelemek, kimin
 * kim olduğunu tarama imkânı verirdi.
 */
export function gorunumAdaylari(
  q: string,
): Promise<Array<{ id: string; ad: string; roller: string[] }>> {
  return api("/auth/gorunum/adaylar", { sorgu: { q } });
}

/** GEÇMİŞ BAKIM PENCERELERİ — "sistem ne zaman kapalıydı". */
export interface BakimPenceresi {
  id: string;
  title: string;
  message: string | null;
  startsAt: string;
  endsAt: string | null;
  isActive: boolean;
  createdAt: string;
}

export function bakimGecmisi(): Promise<BakimPenceresi[]> {
  return api<BakimPenceresi[]>("/bakim/gecmis");
}

/** DUYURUYU KİMLER OKUDU — matris §5.8. */
export function duyuruOkumalari(
  duyuruId: string,
): Promise<{ toplam: number; okumalar: Array<{ userId: string; readAt: string }> }> {
  return api(`/operasyon/duyurular/${encodeURIComponent(duyuruId)}/okumalar`);
}

/**
 * BİR KOŞUNUN ÜÇ KADEMELİ DOĞRULAMA GEÇMİŞİ — F-YED-01.
 *
 * `basarili: null` = denenmedi YA DA hâlâ sürüyor. Ekran bunu
 * "başarısız" diye göstermemeli: yedeğin bozuk olduğunu sanmak,
 * bozuk olduğunu bilmemekten farklı bir hata üretir.
 */
export interface KademeDurumu {
  denendi: boolean;
  basarili: boolean | null;
  tarih: string | null;
}

export interface DogrulamaGorunumu {
  runId: string;
  durum: string;
  basladi: string;
  saglamaToplamiVar: boolean;
  kademeler: Record<"CHECKSUM" | "OPENABLE" | "FULL_DRILL", KademeDurumu>;
  gecmis: Array<{
    id: string;
    level: string;
    startedAt: string;
    finishedAt: string | null;
    isOk: boolean | null;
    restoreSec: number | null;
  }>;
}

export function yedekDogrulamalari(kosuId: string): Promise<DogrulamaGorunumu> {
  return api<DogrulamaGorunumu>(`/yedek/kosu/${encodeURIComponent(kosuId)}/dogrulamalar`);
}

/*
 * `yardimYazisi` YAZILDI VE SİLİNDİ — 04.09.2026.
 *
 * `GET /yardim/icerik/:slug` tek bir yazıyı döndürüyor. İstemcisini
 * yazdım, sonra sildim: `yardimBolumu` ve `yardimAra` zaten GÖVDEYİ
 * döndürüyor, yani ekranın ihtiyacı olan her şey elinde. İkinci bir
 * yol açmak, aynı veriyi iki kaynaktan okumak ve ikisi ayrıştığında
 * hangisinin doğru olduğunu tartışmak demekti.
 *
 * "Bir gün derin bağlantı lazım olur" diye bırakmak, tam da boşluk
 * taramasının saydığı ölü istemciyi üretir.
 */

/**
 * BİR KAYDIN ONAY TALEPLERİ — F-APR.
 *
 * `entityType` + `entityId` ile: "bu program için açık onay talebi
 * var mı" sorusunun cevabı.
 */
export function kayitOnayTalepleri(entityType: string, entityId: string): Promise<OnayTalebi[]> {
  return api<OnayTalebi[]>("/onaylar/kayit", { sorgu: { entityType, entityId } });
}

/**
 * ONAY TALEBİ AÇAR — F-APR-01.
 *
 * Aynı kayıt için AÇIK talep varsa ikincisi açılmıyor: açılsaydı aynı
 * program için üç talep birikir, biri onaylanıp diğerleri bekler ve
 * hangisinin geçerli olduğu belirsiz kalırdı.
 *
 * `entityType` + `entityId` serbest: onay motoru bir kayda değil
 * kayıt TÜRÜNE bağlı çalışıyor. Ekranın işi doğru türü göndermek —
 * kullanıcıya tür yazdırmıyoruz.
 */
export function onayTalebiAc(veri: {
  entityType: string;
  entityId: string;
  category?: string | null;
  studentId?: string | null;
  summary?: string | null;
  priority?: number;
  dueAt?: string | null;
}): Promise<{ id: string }> {
  return api("/onaylar", { yontem: "POST", govde: veri });
}

/**
 * ÖĞRENCİNİN OKUL/DERSHANE MEŞGULİYETİ — F-SCH-11.
 *
 * Şartname: *"öğrencinin okul, dershane ve özel ders saatleri program
 * hazırlama ekranında **bilgi amaçlı** gösterilir."* Bilgi amaçlı:
 * program üretimini engellemiyor, yalnızca görünüyor.
 *
 * Tekrarlı kayıtlar SUNUCUDA açılıyor. Veritabanında "her hafta
 * pazartesi okul" tek satır; RRULE çözümlemesini burada tekrar
 * yazmak, iki yerin ayrıştığı gün yanlış saat göstermek olurdu.
 */
export function ogrencininMesguliyeti(
  ogrenciId: string,
  a: Aralik,
): Promise<Array<{ baslik: string; baslangic: string; bitis: string }>> {
  return api(`/takvim/ogrenci/${encodeURIComponent(ogrenciId)}/mesguliyet`, { sorgu: a });
}

// =====================================================================
// PROFİL GÖRSELİ — F-PPL-13
// =====================================================================

export interface AvatarGorunumu {
  /**
   * Yuklenmis fotograf VAR MI.
   *
   * Baglantinin kendisi burada DONMUYOR: imzali baglanti 15 dakika
   * yasiyor ve her istekte yeniden uretiliyor. Otuz kisilik bir liste
   * icin otuz imza uretmek gereksiz olurdu — baglanti ayri uctan,
   * gosterilecek anda.
   */
  fotoVarMi: boolean;
  avatarPreset: number | null;
  /** Görsel yoksa gösterilecek harfler. Boş olabilir. */
  basHarfler: string;
}

export function avatarim(): Promise<AvatarGorunumu> {
  return api<AvatarGorunumu>("/avatar");
}

/**
 * BASKASININ profil gorseli — F-PPL-13.
 *
 * Ayri bir izin YOK: soru "bu kisiyi zaten gorebiliyor muyum".
 * Yetkisi olmayan 403 aliyor.
 */
export function kisininAvatari(userId: string): Promise<AvatarGorunumu> {
  return api<AvatarGorunumu>(`/avatar/kisi/${encodeURIComponent(userId)}`);
}

/**
 * Profil fotografinin SURELI baglantisi — F-PPL-13.
 *
 * 15 dakika yasiyor. Kalici bir adres verilseydi paylasilan bir
 * baglanti yetki kontrolunu tamamen atlardi.
 */
/** KENDI fotografimin baglantisi — kisi kendi kimligini bilmek zorunda degil. */
export function avatarimFotoBaglantisi(): Promise<{ url: string; gecerlilikSaniye: number }> {
  return api("/avatar/foto");
}

export function avatarFotoBaglantisi(
  userId: string,
): Promise<{ url: string; gecerlilikSaniye: number }> {
  return api(`/avatar/kisi/${encodeURIComponent(userId)}/foto`);
}

/**
 * Kendi profil fotografimi yukler — F-PPL-13.
 *
 * jpeg / png / webp, en fazla 2 MB. Yeni fotograf eskisini arsivliyor
 * ve hazir avatar secimini dusuruyor: gorsel TEK.
 */
export function avatarFotoYukle(dosya: File): Promise<AvatarGorunumu> {
  const govde = new FormData();
  govde.append("dosya", dosya);
  return api<AvatarGorunumu>("/avatar/foto", { yontem: "POST", govde });
}

export function hazirAvatarSayisi(): Promise<{ adet: number }> {
  return api("/avatar/hazir");
}

/** `secim: null` seçimi kaldırır ve baş harflere döner. */
export function hazirAvatarSec(secim: number | null): Promise<AvatarGorunumu> {
  return api<AvatarGorunumu>("/avatar/hazir", { yontem: "POST", govde: { secim } });
}

/**
 * Başkasının görselini KALDIRIR — değiştirmez.
 *
 * Şartname: yönetici başkasınınkini değiştiremez, yalnızca
 * kaldırabilir (uygunsuz içerik). Gerekçe zorunlu, kayda geçer.
 */
export function avatarKaldir(userId: string, gerekce: string): Promise<{ kaldirildi: true }> {
  return api(`/avatar/${encodeURIComponent(userId)}/kaldir`, {
    yontem: "POST",
    govde: { gerekce },
  });
}

// =====================================================================
// ONBOARDING KONTROL LİSTESİ — F-PPL-09
// =====================================================================

export interface OnboardingMaddesi {
  kod: string;
  baslik: string;
  tamamlandiMi: boolean;
  sonTarih: string;
  gecikti: boolean;
}

export interface OnboardingGorunumu {
  kayitTarihi: string;
  penceKapandi: boolean;
  toplam: number;
  tamamlanan: number;
  /** `null` = madde yok. "%0" DEĞİL. */
  oran: number | null;
  geciken: number;
  maddeler: OnboardingMaddesi[];
}

/**
 * Altı maddenin altısı da SİSTEM DURUMUNDAN okunuyor; hiçbiri elle
 * işaretlenmiyor. Ayrı bir kayıt tutmak ikinci bir gerçek üretirdi.
 */
export function onboardingDurumu(ogrenciId: string): Promise<OnboardingGorunumu> {
  return api<OnboardingGorunumu>(`/ogrenciler/${encodeURIComponent(ogrenciId)}/onboarding`);
}

// =====================================================================
// GÜVENLİK KONSOLU — F-SEC-02/03/04/07/09
// =====================================================================

export interface GuvenlikAyari {
  kod: string;
  /** `giris` | `oturum` | `kimlik` — ekran gruplamayı BUNDAN alıyor. */
  bolum: string;
  ad: string;
  /**
   * NE İŞE YARADIĞI DEĞİL, NEDEN VAR OLDUĞU. Ekranda gösteriliyor:
   * bir güvenlik ayarını değiştiren kişi neyi gevşettiğini bilmeli.
   */
  aciklama: string;
  birim: string;
  varsayilan: number;
  deger: number;
  /**
   * Sunucu hesaplıyor, ekran değil. Gevşetilmiş bir ayarın öteki on
   * beşin arasında kaybolmaması F-SEC-05'in en ucuz parçası.
   */
  varsayilanMi: boolean;
}

/**
 * OKUMA SUDO İSTEMEZ — `sistem.guvenlik.goruntule`.
 *
 * Ayrım bilinçli (F-SEC-09): okumayı sudo arkasına koymak kullanıcıyı
 * şifresini refleksle yazmaya alıştırır ve sudo'yu gerçekten gereken
 * yerlerde (korumalı not, kalıcı silme, görünüm) değersizleştirir.
 * Bir koruma her yerde kullanıldığında hiçbir yerde korumaz.
 */
export function guvenlikAyarlari(): Promise<{ ayarlar: GuvenlikAyari[] }> {
  return api("/guvenlik/ayarlar");
}

/**
 * DEĞİŞTİRME SUDO İSTER — `sistem.guvenlik.ayar` sudo listesinde.
 * Ekran bunu kendi bilmiyor; sunucu `SUDO_GEREKLI` dönünce
 * `useSudoKapisi` soruyor.
 *
 * TAVAN YOK (Emre'nin kararı): büyük değerler kabul edilir. Reddedilen
 * yalnızca ÇALIŞAMAZ değer. Ekran da bu yüzden kendi sınırını yazmıyor
 * — yazsaydı sunucunun kabul ettiği bir değeri "geçersiz" gösterebilirdi.
 */
export function guvenlikAyariYaz(
  kod: string,
  deger: number,
): Promise<{ kod: string; eski: number; yeni: number }> {
  return api(`/guvenlik/ayarlar/${encodeURIComponent(kod)}`, {
    yontem: "PATCH",
    govde: { deger },
  });
}

export interface KilitliHesap {
  userId: string;
  adSoyad: string;
  email: string | null;
  lockedUntil: string;
  /** YUKARI yuvarlanmış — "0 dakika kaldı" yazıp denemeye göndermemek için. */
  kalanDakika: number;
  /**
   * KAÇINCI KİLİT. `failedLoginCount` DEĞİL: sunucu kilidi atarken o
   * sayacı sıfırlıyor, yani kilitli bir hesapta hep `0` olurdu.
   * Kilit süresi üstel artıyor ve bu sayı o üssün kendisi — yöneticinin
   * "açayım mı" kararı için gereken tek bilgi.
   */
  kilitSayisi: number;
  sonKilitAt: string | null;
}

/** Liste de AÇMA iznine bağlı: "şu an kim kilitli" saldırganın en çok isteyeceği bilgi. */
export function kilitliHesaplar(): Promise<{ hesaplar: KilitliHesap[] }> {
  return api("/guvenlik/kilitli-hesaplar");
}

/**
 * Gerekçe ZORUNLU (en az 5 karakter, kuralı sunucu koyuyor).
 * "Kim açtı" tek başına yetmiyor: aynı hesabı üç kez açmak normal de
 * olabilir şüpheli de; ayıran şey NEDEN açıldığı.
 *
 * `acildi: false` = hesap zaten kilitli değildi. Sunucu bunu gizlemiyor.
 */
export function kilitAc(
  userId: string,
  gerekce: string,
): Promise<{ userId: string; acildi: boolean }> {
  return api(`/guvenlik/kilitli-hesaplar/${encodeURIComponent(userId)}/ac`, {
    yontem: "POST",
    govde: { gerekce },
  });
}

export interface IpYogunlugu {
  ipAddress: string;
  deneme: number;
  /** 1 ise bir hesabın şifresi deneniyor; çoksa kimlik listesi taranıyor. */
  farkliKimlik: number;
  sonDeneme: string;
}

export interface GirisDenemesi {
  /** Maskeli kimlik — e-posta, telefon ya da maskeli T.C. Ham T.C. asla. */
  kimlik: string;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
}

export function girisDenemeleri(saat = 24): Promise<{
  denemeler: GirisDenemesi[];
  ipler: IpYogunlugu[];
  pencereSaat: number;
}> {
  return api(`/guvenlik/giris-denemeleri?saat=${saat}`);
}

export type GuvenlikDurumu = "iyi" | "dikkat" | "acil";

export interface PanoMaddesi {
  anahtar: string;
  /** Sayıyı İÇERMEZ — ekran sayıyı ayrı çiziyor. */
  etiket: string;
  sayi: number;
  durum: GuvenlikDurumu;
  /**
   * Tıklanınca gidilecek ekran anahtarı. HİÇ boş olmuyor:
   * "tıklanamayan bir sayı bir gösterge değil süstür" (F-SEC-01).
   */
  ekran: string;
  /** Neden bu durumda — eşiğin kendisi kullanıcıya açık. */
  aciklama: string;
}

/**
 * Genel durum maddelerin ORTALAMASI değil EN KÖTÜSÜ — sunucu
 * hesaplıyor. Ortalama, "dört iyi bir felaket" hâlini "çoğunlukla
 * iyi" gösterirdi.
 */
export function guvenlikPanosu(): Promise<{
  genel: GuvenlikDurumu;
  maddeler: PanoMaddesi[];
  olcumAni: string;
}> {
  return api("/guvenlik/pano");
}

export interface AcikOturum {
  id: string;
  userId: string;
  adSoyad: string;
  email: string | null;
  cihaz: string | null;
  ipAddress: string | null;
  ulke: string | null;
  guvenilir: boolean;
  basladi: string;
  sonGorulme: string;
  biter: string;
}

/**
 * Sistemdeki BÜTÜN açık oturumlar — `sistem.guvenlik.goruntule`.
 * Jeton ya da özeti hiçbir alanda yok (F-LOG-12); sunucu onları hiç
 * seçmiyor, "yanıttan silmeyi unutma" ihtimali ortadan kalksın diye.
 */
export function acikOturumlar(): Promise<{ oturumlar: AcikOturum[]; toplam: number }> {
  return api("/guvenlik/oturumlar");
}

/**
 * Başkasının oturumunu uzaktan kapatır — `sistem.guvenlik.ayar` (sudo).
 *
 * `kapatildi: false` = oturum zaten kapalıymış. Sunucu bunu gizlemiyor;
 * olmayan bir işlemi "başarılı" göstermek, denetim kaydını gerçek
 * müdahalelerin arasında gürültüye boğardı.
 */
export function oturumKapat(
  sessionId: string,
  gerekce: string,
): Promise<{ sessionId: string; kapatildi: boolean }> {
  return api(`/guvenlik/oturumlar/${encodeURIComponent(sessionId)}/kapat`, {
    yontem: "POST",
    govde: { gerekce },
  });
}

/**
 * ACİL DURUM İŞLEMLERİ — F-SEC-06
 *
 * Üçü de toplu etkili ve geri alınamaz; hepsi gerekçe istiyor ve
 * sunucuda SENKRON `SECURITY` kaydı yazıyor (kayıt yazılamıyorsa işlem
 * de yapılmıyor).
 *
 * ⚠️ İki adımlı onay ARAYÜZDE. Sunucu her çağrıyı geçerli sayıyor —
 * "onayladın mı" sorusunun cevabı istemciden gelirse koruma olmaz;
 * gerçek koruma sudo, izin ve denetim kaydı.
 */
export function tumOturumlariSonlandir(gerekce: string): Promise<{ kapatilan: number }> {
  return api("/guvenlik/acil/tum-oturumlari-sonlandir", { yontem: "POST", govde: { gerekce } });
}

export function guvenilirCihazlariIptalEt(gerekce: string): Promise<{ iptalEdilen: number }> {
  return api("/guvenlik/acil/guvenilir-cihazlari-iptal-et", { yontem: "POST", govde: { gerekce } });
}

export function kisiyiDondur(
  userId: string,
  gerekce: string,
): Promise<{ userId: string; oturum: number; cihaz: number }> {
  return api(`/guvenlik/acil/dondur/${encodeURIComponent(userId)}`, {
    yontem: "POST",
    govde: { gerekce },
  });
}

export interface GunKovasi {
  /** `YYYY-MM-DD` — UTC gününe göre. */
  gun: string;
  sayi: number;
}

export interface GuvenlikSerisi {
  anahtar: string;
  baslik: string;
  /** NEDEN bakıldığı. Grafiğin altında duruyor — sayı tek başına anlam taşımaz. */
  aciklama: string;
  kovalar: GunKovasi[];
  toplam: number;
  /** Ölçek. Hepsi sıfırsa sunucu `1` döndürüyor, `0` değil (NaN olmasın). */
  enYuksek: number;
  yuksekIyiMi: boolean;
}

export function guvenlikGrafikleri(gun = 30): Promise<{
  gun: number;
  seriler: GuvenlikSerisi[];
  anlik: {
    /** `null` = sayılacak personel yok. `0` DEĞİL — ikisi ayrı gerçek. */
    ikiAsamaOrani: number | null;
    personelToplam: number;
    ikiAsamaliPersonel: number;
    acikOturum: number;
  };
  saatDilimiNotu: string;
}> {
  return api(`/guvenlik/grafikler?gun=${gun}`);
}

/* ================================================================
   MESAJLAŞMA — F-MSG
   ================================================================
   Kapsam sunucuda: `kutum` ve `yazisma` yalnızca KENDİ yazışmalarını
   döndürüyor, süzgeç `participants.some.userId` ile kilitli. Panel
   burada hiçbir süzgeç uygulamıyor — uygulasaydı, kapsam iki eve
   dağılır ve arayüz süzgeci "güvenlik" sanılırdı.
   ================================================================ */

export type MesajKanali = "KOC" | "KURUM" | "PERSONEL";

export interface YazismaOzeti {
  id: string;
  kanal: MesajKanali;
  baslik: string | null;
  /** KURUM kanalında "Kurum" — kişi adı DEĞİL. Yanıtlayan değişebilir. */
  karsiTaraf: string;
  sonMesajAt: string;
  okunmamis: number;
  arsivlendiMi: boolean;
}

export interface YazismaMesaji {
  id: string;
  gonderenId: string;
  gonderenAd: string;
  /** Geri alınmışsa `null` — gövde veritabanında DURUYOR, ekrana gelmiyor. */
  govde: string | null;
  geriAlindiMi: boolean;
  createdAt: string;
  benimMi: boolean;
  /** Ek — F-MSG-03. Geri alınmışsa `null`. */
  ek: MesajEki | null;
}

export function mesajKutum(): Promise<{
  yazismalar: YazismaOzeti[];
  toplamOkunmamis: number;
}> {
  return api("/mesaj/kutum");
}

export function mesajYazisma(id: string): Promise<{
  id: string;
  kanal: MesajKanali;
  baslik: string | null;
  /** Arşivlenmişse yeni mesaj gönderilemez — F-MSG-07. */
  arsivlendiMi: boolean;
  /** Karşı tarafın mesai notu — F-MSG-05. Tanımlı değilse `null`. */
  mesai: { icindeMi: boolean; metin: string } | null;
  mesajlar: YazismaMesaji[];
}> {
  return api(`/mesaj/yazisma/${encodeURIComponent(id)}`);
}

export function mesajOkundu(id: string): Promise<{ okunduAt: string }> {
  return api(`/mesaj/yazisma/${encodeURIComponent(id)}/okundu`, { yontem: "POST" });
}

export interface YazilabilirKisiler {
  koc: Array<{ userId: string; ad: string; iliski: string }>;
  /**
   * `roller` ROL ADLARI (kod değil) ve MÜŞTERİ ROLLERİ AYIKLANMIŞ
   * gelir: çocuğu aynı kurumda okuyan bir personelin yanında "Veli"
   * yazmamalı — kişisel bir olgu, mesaj kutusunda işi yok.
   * Boş olabilir; o zaman etiket yalnızca adı gösteriyor.
   */
  personel: Array<{ userId: string; ad: string; roller: string[] }>;
  kurumAcabilirMi: boolean;
}

export function mesajKisileri(): Promise<YazilabilirKisiler> {
  return api("/mesaj/kisiler");
}

export function mesajGonder(veri: {
  kanal: MesajKanali;
  aliciId?: string | null;
  conversationId?: string | null;
  govde: string;
}): Promise<{ conversationId: string; messageId: string }> {
  return api("/mesaj/gonder", { yontem: "POST", govde: veri });
}

export function mesajGeriAl(messageId: string): Promise<{ messageId: string }> {
  return api(`/mesaj/mesaj/${encodeURIComponent(messageId)}/geri-al`, { yontem: "POST" });
}

/**
 * BAŞKASININ YAZIŞMASINI GEREKÇEYLE OKU — F-MSG-06.
 *
 * Sudo istiyor (sunucu `SUDO_GEREKLI` döner) ve gerekçe zorunlu.
 * Geri alınan mesajın gövdesi BURADA görünüyor: geri alma ekrandan
 * gizler, denetimden gizlemez.
 */
export function mesajDenetimOku(
  conversationId: string,
  gerekce: string,
): Promise<{
  id: string;
  kanal: MesajKanali;
  katilimcilar: string[];
  mesajlar: Array<{
    id: string;
    gonderenAd: string;
    govde: string;
    geriAlindiMi: boolean;
    createdAt: string;
    /** Ekin ADI ve TÜRÜ — geri alınmış olsa bile görünüyor (F-MSG-03). */
    ek: MesajEki | null;
  }>;
}> {
  return api(`/mesaj/denetim/${encodeURIComponent(conversationId)}`, {
    yontem: "POST",
    govde: { gerekce },
  });
}

export interface DenetimYazismasi {
  id: string;
  kanal: MesajKanali;
  katilimcilar: string[];
  mesajSayisi: number;
  /** Geri alınmış mesaj sayısı — bu gövdeler denetim okumasında GÖRÜNÜYOR. */
  geriAlinan: number;
  sonMesajAt: string;
  arsivlendiMi: boolean;
}

/**
 * Denetim için yazışma listesi — ÜST VERİ, içerik yok.
 *
 * Bu liste olmadan `mesajDenetimOku` çağrılamaz: kimliği hiçbir yerden
 * alamayan bir yönetici o ucu hiç kullanamazdı. Uç sudo istiyor.
 */
export function mesajDenetimYazismalari(ara?: string): Promise<{
  yazismalar: DenetimYazismasi[];
}> {
  const s = ara?.trim() ? `?ara=${encodeURIComponent(ara.trim())}` : "";
  return api(`/mesaj/denetim/yazismalar${s}`);
}

/** Mesaj ekinin ekranda gösterilen bilgisi — içerik YOK, yalnızca üst veri. */
export interface MesajEki {
  ad: string;
  mime: string;
  boyut: number;
}

/**
 * Ekli mesaj gönderir — F-MSG-03.
 *
 * Ek AYRI bir adımda değil mesajla BİRLİKTE gidiyor: ayrı olsaydı
 * yarıda bırakılan yüklemeler sahipsiz dosya bırakır ve bir yazışmaya
 * yüklenen ekin başka bir yazışmaya iliştirilmesi mümkün olurdu.
 */
export function mesajEkliGonder(veri: {
  kanal: MesajKanali;
  aliciId?: string | null;
  conversationId?: string | null;
  govde: string;
  dosya: File;
}): Promise<{ conversationId: string; messageId: string }> {
  const govde = new FormData();
  govde.append("dosya", veri.dosya);
  govde.append("kanal", veri.kanal);
  if (veri.aliciId) govde.append("aliciId", veri.aliciId);
  if (veri.conversationId) govde.append("conversationId", veri.conversationId);
  if (veri.govde.trim()) govde.append("govde", veri.govde);
  return api("/mesaj/gonder-ek", { yontem: "POST", govde });
}

/**
 * Ekin SÜRELİ indirme bağlantısı — F-MSG-03.
 *
 * Bağlantı 15 dakika yaşıyor ve her istekte yeniden üretiliyor; kalıcı
 * bir adres verilseydi paylaşılan bir bağlantı yetki kontrolünü
 * tamamen atlardı.
 */
export function mesajEkBaglantisi(messageId: string): Promise<{ url: string; ad: string }> {
  return api(`/mesaj/mesaj/${encodeURIComponent(messageId)}/ek`);
}

/** Kendi mesai saatlerim — F-MSG-05. `kocMu` false ise ekran bölümü çizilmiyor. */
export function mesaimiGetir(): Promise<{ araliklar: MesaiAraligi[]; kocMu: boolean }> {
  return api("/mesaj/mesai");
}

export function mesaimiYaz(araliklar: MesaiAraligi[]): Promise<{ araliklar: MesaiAraligi[] }> {
  return api("/mesaj/mesai", { yontem: "POST", govde: { araliklar } });
}

/** Hazır mesaj şablonu — F-MSG-04. */
export interface MesajSablonu {
  id: string;
  baslik: string;
  govde: string;
}

/**
 * Kendi hazır yanıtlarım.
 *
 * `personelMi` AYRI dönüyor: müşteriye şablon verilmiyor ve ekran o
 * bölümü hiç çizmemeli. Boş liste ile "sen personel değilsin" aynı şey
 * değil — birincisi "henüz yazmadın" der ve ekleme kutusu açar.
 */
export function mesajSablonlarim(): Promise<{
  sablonlar: MesajSablonu[];
  personelMi: boolean;
}> {
  return api("/mesaj/sablonlar");
}

export function mesajSablonuEkle(veri: { baslik: string; govde: string }): Promise<{ id: string }> {
  return api("/mesaj/sablonlar", { yontem: "POST", govde: veri });
}

/** Kaldırma SİLMİYOR, arşivliyor (CLAUDE.md kural 7). */
export function mesajSablonuArsivle(id: string): Promise<{ arsivlendi: true }> {
  return api(`/mesaj/sablonlar/${encodeURIComponent(id)}/arsivle`, { yontem: "POST" });
}

/* ================================================================
   PERSONEL 360° — F-PPL-12 ve personel dökümü
   ================================================================ */

export type YukSeviyesi = "izinli" | "asiri" | "dolu" | "uygun";

export interface UzmanYuku {
  aktifOgrenci: number;
  kota: number;
  buHaftaGorusme: number;
  hazirlanacakProgram: number;
  bekleyenVeliRapor: number;
  gecikenGorev: number;
  /** `null` = hiç gönderilmiş rapor yok. Sıfır DEĞİL — ikisi ayrı gerçek. */
  ortalamaRaporSaati: number | null;
  izinli: boolean;
  /** Kota sıfırsa `null` — "%NaN" diye bir şey yok. */
  doluluk: number | null;
  seviye: YukSeviyesi;
  /** Seviyenin NEDEN o olduğu. Gerekçesiz seviye ya körü körüne güvenilir ya hiç. */
  gerekce: string;
}

/** Koç kaydı olmayan personelde `null` — olmayan bir ölçüyü varmış gibi göstermiyoruz. */
export function personelYuku(personelId: string): Promise<UzmanYuku | null> {
  return api(`/personel/${encodeURIComponent(personelId)}/yuk`);
}

export interface PersonelinOgrencisi {
  id: string;
  studentNo: number;
  ad: string;
  soyad: string;
  status: string;
}

export function personelinOgrencileri(personelId: string): Promise<PersonelinOgrencisi[]> {
  return api(`/personel/${encodeURIComponent(personelId)}/ogrenciler`);
}

// =====================================================================
// GRAFİKLER — dalga 3 (F-ANL-06/08, F-PPL-05)
// =====================================================================

/** Bir haftanın program uyumu — sunucudaki `analiz/haftalik-seri.ts` ile aynı şekil. */
export interface HaftaNoktasi {
  haftaBasi: string;
  haftaSonu: string;
  atanan: number;
  tamamlanan: number;
  mazeretli: number;
  /** 0..1; payda (atanan − mazeretli) yoksa `null` — "%0" DEĞİL. */
  tamamlamaOrani: number | null;
  /** Veri girilmiş görev yoksa `null`. */
  cozulenSoru: number | null;
  toplamSureSaniye: number | null;
  kapsam: {
    toplamGorev: number;
    veriGirilenGorev: number;
    oran: number | null;
    dusukMu: boolean;
    metin: string;
  };
}

/** Son N hafta, eskiden yeniye — 360° Genel Bakış küçük çizgileri. */
export function analizTrendi(ogrenciId: string, hafta = 4): Promise<{ haftalar: HaftaNoktasi[] }> {
  return api(`/analiz/ogrenci/${encodeURIComponent(ogrenciId)}/trend`, { sorgu: { hafta } });
}

/**
 * Öğrenciler listesi mini çubuğu: bu hafta + önceki hafta. Sunucu istenen
 * kimlikleri KENDİ kapsamıyla kesiştiriyor; kapsam dışı satır cevapta hiç yok.
 */
export function haftaOzetleri(ogrenciIdleri: readonly string[]): Promise<{
  ogrenciler: Array<{ ogrenciId: string; buHafta: HaftaNoktasi; oncekiHafta: HaftaNoktasi }>;
}> {
  return api(`/analiz/ogrenciler/hafta-ozeti`, { sorgu: { ids: ogrenciIdleri.join(",") } });
}

// =====================================================================
// KVKK — saklama süresi dolan kişisel verinin anonimleştirilmesi (F-LGL-06)
// =====================================================================

export type AnonimlestirmeModu = "KAPALI" | "KURU" | "ACIK";

/** Yalnız SAYILAR — kişi verisi dönmez. */
export interface AnonimlestirmeOzeti {
  mod: "KURU" | "ACIK";
  ogrenci: number;
  /** K7: kullanıcı hesabı korunan öğrenci (aynı zamanda aktif veli / ödeyen veli). */
  ogrenciKullanicisiKorunan: number;
  veli: number;
  odeyenVeli: number;
  aday: number;
  dosya: number;
  /** K2-b: silinecek rıza kanıtı dosyası. */
  rizaKaniti: number;
  pdf: number;
  ertelenen: { kvkkBasvurusu: number; odemeAcik: number; iadeSuruyor: number };
  tarihsiz: { ogrenci: number; aday: number };
  /** Kilit altında yeniden okununca uygun olmayan (işlenmedi). */
  uygunlukDegisti: number;
  /** Commit'ten sonra silinemeyen nesne (sonraki koşu yeniden dener). */
  bekleyenNesne: number;
  oncekiBekleyenDosya: number;
  oncekiBekleyenOnek: number;
  oncekiBekleyenNiyet: number;
  pdfBilinmeyen: number;
  butceKesilen: number;
  supurulen: number;
  hata: number;
}

/** KURU önizleme — hiçbir şey yazmaz. Sudo ister (`sistem.kayit.kalici_sil`). */
export function anonimlestirmeOnizleme(): Promise<{
  mod: AnonimlestirmeModu;
  ozet: AnonimlestirmeOzeti;
}> {
  return api("/kvkk/anonimlestirme");
}

export function anonimlestirmeModu(
  mod: AnonimlestirmeModu,
  onay: string | null,
): Promise<{ mod: AnonimlestirmeModu }> {
  return api("/kvkk/anonimlestirme/mod", { yontem: "POST", govde: { mod, onay } });
}

// ---------------------------------------------------------------------------
// OYUNLASTIRMA — F-GAM dalga 1. Koc: atanmis ogrencisinin ozeti + elle puan (limitli). Yonetici: dokum, duzeltme,
// kural surumleri, supheli kayitlar, kazanim hizi. Kapsam ve limit SUNUCUDA (oyun.service.ts).

import type { OyunOgrenciOzeti, PuanHareketiOzeti, OyunKurali } from "@koclukpanel/contracts";
export type { OyunOgrenciOzeti, PuanHareketiOzeti, OyunKurali };
export type OyunYonetimHareketi = PuanHareketiOzeti & { ogrenciId: string; ogrenciAdi: string };

export function oyunOgrenciOzeti(ogrenciId: string): Promise<OyunOgrenciOzeti> {
  return api(`/oyun/ogrenci/${encodeURIComponent(ogrenciId)}/ozet`);
}

export function oyunKocLimiti(
  ogrenciId: string,
): Promise<{ kalan: number; hareketTavani: number; haftalikTavan: number }> {
  return api(`/oyun/ogrenci/${encodeURIComponent(ogrenciId)}/koc-limiti`);
}

export function oyunPuanVer(
  ogrenciId: string,
  govde: { miktar: number; gerekce: string; istekKimligi: string },
): Promise<PuanHareketiOzeti> {
  return api(`/oyun/ogrenci/${encodeURIComponent(ogrenciId)}/puan`, { yontem: "POST", govde });
}

export function oyunYonetimHareketleri(f: {
  ogrenciId?: string;
  elle?: boolean;
  kesilen?: boolean;
  imlec?: string;
}): Promise<{ satirlar: OyunYonetimHareketi[]; sonraki: string | null }> {
  const q = new URLSearchParams();
  if (f.ogrenciId) q.set("ogrenciId", f.ogrenciId);
  if (f.elle) q.set("elle", "1");
  if (f.kesilen) q.set("kesilen", "1");
  if (f.imlec) q.set("imlec", f.imlec);
  const s = q.toString();
  return api(`/oyun/yonetim/hareketler${s ? `?${s}` : ""}`);
}

export function oyunSupheliKayitlar(): Promise<
  { gorevId: string; ogrenciId: string; ogrenciAdi: string; gun: string; nedenler: string[] }[]
> {
  return api("/oyun/yonetim/supheli");
}

export function oyunKazanimHizi(): Promise<{
  hafta: number;
  ogrenciHafta: number;
  ortalama: number;
  medyan: number;
  ustCeyrek: number;
}> {
  return api("/oyun/yonetim/kazanim-hizi");
}

export function oyunKurallari(): Promise<
  {
    surum: number;
    gecerliBaslangic: string;
    kurallar: OyunKurali;
    gerekce: string;
    tarih: string;
  }[]
> {
  return api("/oyun/yonetim/kurallar");
}

export function oyunYeniKural(govde: {
  kurallar: OyunKurali;
  gecerliBaslangic: string;
  gerekce: string;
}): Promise<{ surum: number; gecerliBaslangic: string }> {
  return api("/oyun/yonetim/kurallar", { yontem: "POST", govde });
}

export function oyunDuzelt(
  ogrenciId: string,
  govde: { miktar: number; gerekce: string; istekKimligi: string },
): Promise<PuanHareketiOzeti> {
  return api(`/oyun/yonetim/ogrenci/${encodeURIComponent(ogrenciId)}/duzeltme`, {
    yontem: "POST",
    govde,
  });
}

export function oyunModulDurumu(): Promise<{ acik: boolean }> {
  return api("/oyun/yonetim/modul");
}

export function oyunModulAyarla(acik: boolean): Promise<{ acik: boolean }> {
  return api("/oyun/yonetim/modul", { yontem: "POST", govde: { acik } });
}

// =====================================================================
// CANLI CALISMA IZLEME — F-LIV (sartname 4.6b)
// =====================================================================
import type { CanliListeYaniti, CanliModulDurumu } from "@koclukpanel/contracts";

/** F-LIV-01/02 — acik sayaclar; kapsami sunucu daraltir (izin `canli.izleme.goruntule`). */
export function canliListe(): Promise<CanliListeYaniti> {
  return api("/canli-izleme");
}

/** F-LIV-05 — giris yapmis her kullanici sorabilir (menu ve not gorunurlugu icin). */
export function canliModulDurumu(): Promise<CanliModulDurumu> {
  return api("/canli-izleme/modul");
}

/** F-LIV-05 — `canli.izleme.yonet` + sudo; sudo yoksa sunucu `SUDO_GEREKLI` doner. */
export function canliModulAyarla(acik: boolean): Promise<CanliModulDurumu> {
  return api("/canli-izleme/modul", { yontem: "POST", govde: { acik } });
}

// F-PPL-01b — hesap türünü istemci belirlemez; yalnız bağlı kaydın kimliğini taşır.
export function davetiYenidenGonder(
  hedef: DavetYenidenGonderIstegi,
): Promise<{ davetGonderildi: boolean }> {
  return api("/auth/davet-yeniden-gonder", { yontem: "POST", govde: hedef });
}
